// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { swrListKey, swrGet, swrPut, swrRevalidate, swrMiss, flushSwrCache, swrInvalidate, rememberRxListPaths, rxPrefetchTargets, isCacheablePayload, isCacheableResponse } from './swrCache'
import type { EnrollmentDraft, PregnancyScreeningResult } from './carerxEnrollment'
import type { CombineTarget } from './carerxCombine'

// Resolves API base URL.
// In dev (vite): VITE_API_BASE or http://localhost:7071/api
// In production: relative `/api` — Static Web Apps proxies /api/* to the
//   linked Function App backend (see .github/workflows/link-swa-backend.yml).
//   The SWA was upgraded from Free to Standard to enable backend linking.
//   Same-origin /api means the session cookie is set on the SWA domain
//   (first-party) and works reliably across all browsers including iOS
//   Safari, where third-party cookies are aggressively restricted.
const DEFAULT_API_BASE = import.meta.env.DEV
  ? 'http://localhost:7071/api'
  : '/api'

export const API_BASE = import.meta.env.VITE_API_BASE ?? DEFAULT_API_BASE

export class ApiError extends Error {
  constructor(public status: number, public body: any, msg: string) {
    super(msg)
  }
}

/**
 * Parse a non-OK Response into an ApiError. Use when calling the API via
 * raw fetch (e.g., for endpoints not yet wrapped by the typed client).
 */
export async function parseApiError(res: Response): Promise<ApiError> {
  let body: any = null
  try { body = await res.json() } catch { /* non-JSON */ }
  return new ApiError(res.status, body, body?.error ?? `HTTP ${res.status}`)
}

// --- Per-tab session token (T2). sessionStorage is per-tab, so each tab holds its
// own identity. We attach it as a Bearer credential; the cookie stays as a fallback
// (dual-mode) so a missed call still authenticates. This is what keeps a logged-in
// tab on its own identity even after another tab overwrites the shared cookie. ---
const SESSION_TOKEN_KEY = 'owlrx_session_token'
export function setSessionToken(token: string | null) {
  // D-PERF-3: any identity change (login, logout, workspace switch) flushes the in-memory list
  // cache so a shared tab can never serve one pharmacy's cached list under another identity.
  flushSwrCache()
  try {
    if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token)
    else sessionStorage.removeItem(SESSION_TOKEN_KEY)
  } catch { /* storage may be unavailable; degrade to cookie-only */ }
}
export function getSessionToken(): string | null {
  try { return sessionStorage.getItem(SESSION_TOKEN_KEY) } catch { return null }
}

// F-02: a global sink so server/network failures surface to the user (a toast) even when a call site
// swallows the error. Registered by ToastProvider; null in non-UI contexts. Fired only for 5xx and
// network errors — 4xx (validation/auth) are handled inline by callers, so we don't toast those.
let apiErrorSink: ((msg: string) => void) | null = null
export function setApiErrorSink(fn: ((msg: string) => void) | null) { apiErrorSink = fn }

// Ticket #1028: a tenant DB at its worker cap answers 503 db_busy (ExceptionHandlingMiddleware) or
// tenant_db_busy (Pioneer webhook). Waiting a few seconds is what succeeds, so GET and PUT — safe to
// repeat — retry twice before anything is shown. A WorkBench save used to revert as "not saved" here.
// POST/DELETE never auto-retry: a repeat could create twice.
const DB_BUSY_RETRIES = 2
const isDbBusy = (status: number, payload: any) =>
  status === 503 && (payload?.error === 'db_busy' || payload?.error === 'tenant_db_busy')
const dbBusyDelayMs = (payload: any, attempt: number) =>
  Math.min(Number(payload?.retry_after_s) || 3, 3) * 1000 * (attempt + 1) + Math.floor(Math.random() * 500)
const busyRetryable = (method: string) => method === 'GET' || method === 'PUT'
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

// Authenticated raw-Response fetch for call sites that need the Response object
// (custom error handling, blobs) instead of request<T>(). Identity is token-first:
// attaches the per-tab Bearer token; the cookie stays as a fallback. Prepends API_BASE
// so callers pass a path (e.g. "/roles"), not a full URL.
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // D-PERF-3 Phase 1: SWR for the raw-fetch path StationQueue uses for /rx?state=… lists. A fresh
  // cached body is returned instantly as a synthesized 200 Response while a background refresh
  // updates the cache (never suppressed). Non-GETs and non-list paths pass straight through.
  const method = (init.method ?? 'GET').toUpperCase()
  const key = method === 'GET' ? swrListKey(path) : null
  if (key) {
    const doNet = () => apiFetchNetwork(path, init).then(async r => {
      // Cache only JSON bodies — a binary (download/PDF) on an allowed path must never be
      // text-cached and re-synthesized.
      // Header check is O(1) and happens BEFORE clone().text(), so an oversized payload is never
      // read into a string on the main thread.
      if (r.ok && (r.headers.get('Content-Type') ?? '').includes('json') && isCacheableResponse(r)) {
        try { swrPut(key, await r.clone().text()) } catch { /* body not cacheable — skip */ }
      }
      return r
    })
    const cached = swrGet(key)
    if (cached !== null) {
      swrRevalidate(key, doNet)
      return Promise.resolve(new Response(cached, { status: 200, headers: { 'Content-Type': 'application/json' } }))
    }
    swrMiss()
    // Single-flight on misses: if a prefetch (or another consumer) already has this list in flight,
    // share it — each consumer gets its own body via clone(), and the query never double-fires.
    const existing = rxListInflight.get(key)
    if (existing) return existing.then(r => r.clone())
    const p = doNet().finally(() => rxListInflight.delete(key))
    rxListInflight.set(key, p)
    return p.then(r => r.clone())
  }
  return apiFetchNetwork(path, init)
}

const rxListInflight = new Map<string, Promise<Response>>()

async function apiFetchNetwork(path: string, init: RequestInit = {}, busyAttempt = 0): Promise<Response> {
  const token = getSessionToken()
  const headers = new Headers(init.headers ?? {})
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...init, headers })
  if (res.status === 503 && busyRetryable((init.method ?? 'GET').toUpperCase()) && busyAttempt < DB_BUSY_RETRIES) {
    let payload: any = null
    try { payload = await res.clone().json() } catch { /* non-JSON 503 — not ours */ }
    if (isDbBusy(res.status, payload)) {
      await sleep(dbBusyDelayMs(payload, busyAttempt))
      return apiFetchNetwork(path, init, busyAttempt + 1)
    }
  }
  return res
}

// D-PERF-3 Phase 1: intent-based Prescriptions prefetch. StationQueue registers the exact list URLs
// it loads (rememberRxQueuePaths); the Sidebar calls prefetchRxQueueOnIntent when the pointer enters
// the Prescriptions nav item, warming any stale/missing entries so the click lands on a warm cache.
// Throttled + registered-paths-only, so it only ever fires work the user is about to request anyway.
export function rememberRxQueuePaths(paths: string[]): void { rememberRxListPaths(paths) }
export function prefetchRxQueueOnIntent(): void {
  for (const p of rxPrefetchTargets()) {
    apiFetch(p, { credentials: 'include' }).catch(() => { /* prefetch is best-effort; never surfaces */ })
  }
}

// Self-heal for a dead per-tab token (T2). A tab that idles out (>15 min) holds a session
// token the server can no longer resolve; because a present Bearer is terminal (RCA-AUTH-1)
// it will NOT fall back to the still-valid SSO cookie, so every data call in that tab reads
// as anonymous until the tab is closed. On such a 401 we re-mint a FRESH per-tab token from
// the cookie (single-flight, shared by concurrent 401s) and let the caller retry once. If the
// cookie is also dead, we clear the token so the tab reads cleanly logged out instead of looping.
let reauthInFlight: Promise<string | null> | null = null
function reauthFromCookie(): Promise<string | null> {
  if (!reauthInFlight) {
    reauthInFlight = (async () => {
      try {
        const t = await mintSessionToken()
        if (t.session_token) { setSessionToken(t.session_token); return t.session_token }
      } catch { /* no valid cookie session — fall through to clear */ }
      setSessionToken(null)
      return null
    })()
    // release the latch once settled so a later expiry can heal again
    void reauthInFlight.finally(() => { reauthInFlight = null })
  }
  return reauthInFlight
}

// Download a patient / Secure-Share document. The endpoint returns EITHER a JSON { url } (legacy
// plaintext blob, fetched via a short-lived SAS) OR the decrypted file bytes directly (encrypted
// docs stream through the Function — a SAS would expose ciphertext). Either way we resolve to a
// { url } the caller can window.open(): legacy → the SAS url; encrypted → a blob object URL.
async function fetchDocDownload(path: string): Promise<{ url: string; file_name: string; content_type: string }> {
  let res = await apiFetch(path, { credentials: 'include' })
  if (res.status === 401) {
    const t = await reauthFromCookie()
    if (t) res = await apiFetch(path, { credentials: 'include' })
  }
  if (!res.ok) throw await parseApiError(res)
  const ctype = res.headers.get('content-type') || ''
  if (ctype.includes('application/json')) {
    return res.json() as Promise<{ url: string; file_name: string; content_type: string }>
  }
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const m = /filename="?([^";]+)"?/i.exec(cd)
  return { url: URL.createObjectURL(blob), file_name: m?.[1] || 'document', content_type: blob.type }
}

// D-PERF-3 Phase 1: SWR wrapper. GETs on the list surfaces (/patients, /prescribers,
// /shipping/shipments, /rx) are served instantly from the in-memory cache when fresh, and EVERY
// cached serve fires a background revalidate that updates the cache (screens pick it up on their own
// reload/poll/next mount — a cached serve never suppresses refresh). Everything else, and all
// non-GETs, pass straight through. Rollback: SWR_LIST_CACHE_ENABLED=false in swrCache.ts.
type RequestOpts = { credentials?: RequestCredentials; _noReauth?: boolean; signal?: AbortSignal; _busyRetry?: number }
async function request<T>(method: string, path: string, body?: any, opts?: RequestOpts): Promise<T> {
  const key = method === 'GET' ? swrListKey(path) : null
  if (!key) return requestNetwork<T>(method, path, body, opts)
  const doNet = () => requestNetwork<T>(method, path, body, opts).then(d => {
    // Size cap first: isCacheablePayload is O(1) (item count), so an oversized list never pays the
    // JSON.stringify cost that blocked the main thread and lagged typing.
    if (isCacheablePayload(d)) { try { swrPut(key, JSON.stringify(d)) } catch { /* non-serializable — skip */ } }
    return d
  })
  const cached = swrGet(key)
  if (cached !== null) {
    swrRevalidate(key, doNet)
    try { return JSON.parse(cached) as T } catch { /* corrupt entry — fall through to network */ }
  }
  swrMiss()
  return doNet()
}

async function requestNetwork<T>(method: string, path: string, body?: any, opts?: RequestOpts): Promise<T> {
  const token = getSessionToken()
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
    method,
    // Cookie stays included so cookie-only flows (e.g. download links that open as
    // navigations) keep working. Identity, however, is token-first: when a per-tab
    // Bearer is present the server uses it and ignores the cookie. The /auth/me call
    // overrides credentials to 'omit' so a tokenless tab reads as logged out instead
    // of inheriting another tab's cookie identity.
    credentials: opts?.credentials ?? 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal: opts?.signal,
    })
  } catch (e) {
    // A caller that cancelled its own request (a superseded search) is not a network failure.
    if ((e as { name?: string })?.name === 'AbortError') throw e
    apiErrorSink?.('Network error — please check your connection and try again.')
    throw e
  }

  let payload: any = null
  try {
    payload = await res.json()
  } catch {
    /* non-JSON response, leave payload null */
  }

  if (!res.ok) {
    const busyAttempt = opts?._busyRetry ?? 0
    if (isDbBusy(res.status, payload) && busyRetryable(method) && busyAttempt < DB_BUSY_RETRIES) {
      await sleep(dbBusyDelayMs(payload, busyAttempt))
      return requestNetwork<T>(method, path, body, { ...opts, _busyRetry: busyAttempt + 1 })
    }
    // 501 is the product's "not configured" answer (AI, DocuSign): the caller shows its own
    // message, and a global error toast on top of it covers the caller's controls.
    if (res.status >= 500 && res.status !== 501) apiErrorSink?.('Something went wrong on our end. Please try again.')
    // Dead per-tab token self-heal: a tab whose session idled out sends a token the server
    // can't resolve (401 anonymous). Re-mint a fresh token from the cookie and retry ONCE
    // before surfacing the error. Skipped for /auth/* (avoids recursion with the mint/me
    // flow) and on the already-retried call (_noReauth) to prevent loops.
    if (res.status === 401 && token && !opts?._noReauth && !path.startsWith('/auth/')) {
      const fresh = await reauthFromCookie()
      if (fresh) return requestNetwork<T>(method, path, body, { ...opts, _noReauth: true })
    }
    // DIAG (temporary, I-SS-401): on an auth failure, surface whether THIS request
    // actually carried a per-tab Bearer. A 401 "anonymous" then tells us, with no
    // guessing, whether the token was missing client-side (sentToken=N -> frontend)
    // or sent but not resolved server-side (sentToken=Y -> backend). The token VALUE
    // is never included — only presence and length.
    if (res.status === 401) {
      const diag = `sentToken=${token ? `Y(len=${token.length})` : 'N'}`
      payload = payload && typeof payload === 'object'
        ? { ...payload, message: `${payload.message ?? ''} [${diag}]`.trim() }
        : { error: (payload && payload.error) || 'anonymous', message: `[${diag}]` }
    }
    throw new ApiError(res.status, payload, payload?.error ?? `HTTP ${res.status}`)
  }
  return payload as T
}

// ---------- Feedback ----------
export interface FeedbackInput {
  screen: string
  category: 'bug' | 'feature_request' | 'ux' | 'data_correction' | 'patient_care' | 'other'
  severity?: 'low' | 'medium' | 'high'
  message: string
  submitter_email?: string
  submitter_role?: string
  viewport?: string
  phi_acknowledged?: boolean
}

export interface FeedbackResponse {
  id: string
  status: string
  received_at: string
  note: string
}

export const submitFeedback = (input: FeedbackInput) =>
  request<FeedbackResponse>('POST', '/feedback', input)

// ---------- Feedback inbox (admin) ----------
export interface FeedbackItem {
  id: string
  submitted_at: string
  submitter_email: string | null
  submitter_role: string | null
  screen: string
  category: string
  severity: string | null
  message: string
  viewport: string | null
  user_agent: string | null
  status: 'new' | 'triaged' | 'linked' | 'closed'
  triaged_at: string | null
  triaged_by: string | null
  triage_notes: string | null
  linked_roadmap_id: string | null
  linked_roadmap_title: string | null
}

export interface FeedbackInboxCounts {
  total: number
  status_new: number
  triaged: number
  linked: number
  closed: number
}

export interface FeedbackInboxResponse {
  items: FeedbackItem[]
  counts: FeedbackInboxCounts
  limit: number
}

export interface TriageInput {
  status?: 'new' | 'triaged' | 'linked' | 'closed'
  linked_roadmap_id?: string | null
  triage_notes?: string
}

export const listFeedback = (params?: { status?: string; category?: string; limit?: number }) => {
  const q = new URLSearchParams()
  if (params?.status)   q.set('status', params.status)
  if (params?.category) q.set('category', params.category)
  if (params?.limit)    q.set('limit', String(params.limit))
  const qs = q.toString() ? `?${q}` : ''
  return request<FeedbackInboxResponse>('GET', `/feedback${qs}`)
}

export const triageFeedback = (id: string, input: TriageInput) =>
  request<{ id: string; status: string }>('POST', `/feedback/${id}/triage`, input)


// ---------- Roadmap ----------
export type RoadmapStatus = 'new' | 'triaged' | 'planned' | 'in_progress' | 'done' | 'wont_fix'
                          | 'now' | 'next' | 'later' | 'shipped'  // legacy

export interface RoadmapItem {
  id: string
  title: string
  description: string | null
  category: string
  status: RoadmapStatus
  phase: string | null
  voteCount: number
  priority: number
  owningPersona: string | null
  effortEstimate: string | null
  shippedAt: string | null
  shippedInPr: string | null
}

export interface RoadmapModuleSummary {
  module: string
  total: number
  done: number
  in_progress: number
  pct: number
}

export interface RoadmapStatusCounts {
  new: number
  triaged: number
  planned: number
  in_progress: number
  done: number
  wont_fix: number
}

export interface RoadmapOverall {
  total: number
  done: number
  pct: number
}

export interface RoadmapResponse {
  // Modern shape
  items: RoadmapItem[]
  by_module: RoadmapModuleSummary[]
  status_counts: RoadmapStatusCounts
  overall: RoadmapOverall
  // Legacy lanes (kept for back-compat; the new UI doesn't use them)
  now: RoadmapItem[]
  next: RoadmapItem[]
  later: RoadmapItem[]
  shipped: RoadmapItem[]
  generated_at: string
  total: number
}

export const getRoadmap = () => request<RoadmapResponse>('GET', '/roadmap')

// ---------- Roadmap detail (Sprint 9) ----------
export interface RoadmapItemDetail {
  id: string
  title: string
  summary: string | null
  description: string | null
  category: string
  status: RoadmapStatus
  phase: string | null
  sprint: string | null
  epic: string | null
  vote_count: number
  priority: number
  owning_persona: string | null
  effort_estimate: string | null
  shipped_at: string | null
  shipped_in_pr: string | null
  created_at: string
}

export interface RoadmapComment {
  id: string
  author_id: string | null
  author_name: string
  author_role: string
  body: string
  created_at: string
}

export interface RoadmapAttachment {
  id: string
  comment_id: string | null
  uploader_name: string
  file_name: string
  content_type: string
  size_bytes: number
  created_at: string
  download_url: string
}

export interface RoadmapDetailResponse {
  item: RoadmapItemDetail
  comments: RoadmapComment[]
  attachments: RoadmapAttachment[]
}

export const getRoadmapDetail = (id: string) =>
  request<RoadmapDetailResponse>('GET', `/roadmap/${id}`)

export const postRoadmapComment = (id: string, body: string) =>
  request<RoadmapComment>('POST', `/roadmap/${id}/comments`, { body })

export async function postRoadmapAttachment(
  itemId: string,
  file: File,
  commentId?: string,
): Promise<RoadmapAttachment> {
  const fd = new FormData()
  fd.append('file', file)
  const qs = commentId ? `?comment_id=${commentId}` : ''
  const res = await fetch(`${API_BASE}/roadmap/${itemId}/attachments${qs}`, {
    method: 'POST',
    credentials: 'omit',
    headers: { ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}) },
    body: fd,
  })
  if (!res.ok) throw await parseApiError(res)
  return res.json()
}

// ---------- Vote ----------
export interface VoteResponse {
  roadmap_id: string
  vote_count: number
  registered: boolean
}

export const submitVote = (roadmapId: string) =>
  request<VoteResponse>('POST', '/vote', { roadmap_id: roadmapId })

// ============================================================================
// Workflow API — Sprint 3b
// ============================================================================

// ---------- Real auth (WI-004 + WI-007) ----------
export interface AuthMeResponse {
  authenticated: boolean
  account_id?: string
  email?: string
  display_name?: string
  pharmacy_id?: string | null
  is_synthetic?: boolean
  permissions: string[]
  primary_role_id?: string | null
  role_name?: string | null
  role_ids?: string[]
  session_expires_at?: string
  unread_notifications?: number
  /** D-PMS-1: which dispensing system this tenant runs, so vendor-specific copy
   *  names the real system instead of hardcoding PioneerRx. Null = unknown. */
  pms_vendor?: string | null
  pms_display_name?: string | null
}
// Token-only identity: omit the cookie so a tab without its own per-tab token reads as
// logged out, instead of adopting whatever account the shared cookie last held.
export const getAuthMe = () => request<AuthMeResponse>('GET', '/auth/me', undefined, { credentials: 'omit' })

export interface LoginInput { email: string; password: string; pharmacy_slug?: string }
export interface LoginResponse {
  email: string
  display_name: string
  is_synthetic: boolean
  session_expires_at?: string
  // Returned INSTEAD of a session when the account has MFA enabled — the client must
  // complete /auth/mfa/verify with mfa_token + a code to get the session.
  mfa_required?: boolean
  mfa_enrollment_required?: boolean
  mfa_token?: string
  session_token?: string // T2: per-tab token (also sets the cookie in dual-mode)
}
export const authLogin  = (input: LoginInput) => request<LoginResponse>('POST', '/auth/login', input)
export const authLogout = ()                    => request<{ ok: true }>('POST', '/auth/logout')
// Heartbeat fired on real user interaction (throttled by the caller). Advances the server-side idle
// clock; without it, the session idles out after SESSION_IDLE_MINUTES. Best-effort: never throws.
export const authActivity = ()                   => request<void>('POST', '/auth/activity').catch(() => {})
// T1b: mint a per-tab session token from the current authenticated SSO cookie. This is
// the ONLY call that reads the cookie (credentials:include); it runs once on an SSO
// landing, after which the tab is token-only.
export async function mintSessionToken(): Promise<{ session_token?: string; session_expires_at?: string }> {
  const res = await fetch(`${API_BASE}/auth/session-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new ApiError(res.status, null, `HTTP ${res.status}`)
  return res.json()
}

// ---------- MFA (TOTP) ----------
export interface MfaEnrollResponse { secret: string; otpauth_uri: string; issuer: string; account: string }
export const mfaEnroll  = ()                                => request<MfaEnrollResponse>('POST', '/auth/mfa/enroll', {})
export const mfaConfirm = (code: string)                    => request<{ ok: true; enabled: boolean }>('POST', '/auth/mfa/confirm', { code })
export const mfaStatus  = ()                                => request<{ enabled: boolean }>('GET', '/auth/mfa/status')
export const mfaDisable = (code: string)                    => request<{ ok: true; enabled: boolean }>('POST', '/auth/mfa/disable', { code })
export const mfaVerify  = (mfa_token: string, code: string) => request<LoginResponse>('POST', '/auth/mfa/verify', { mfa_token, code })
// Forced-enrollment during login: provision a TOTP secret for the challenged account (no session yet).
export const mfaEnrollBegin = (mfa_token: string)           => request<MfaEnrollResponse>('POST', '/auth/mfa/enroll-begin', { mfa_token })
// Admin (Owner/Admin) sets/clears a user's MFA requirement.
export const setUserMfaRequired = (accountId: string, required: boolean) =>
  request<{ id: string; mfa_required: boolean }>('POST', `/users/${accountId}/mfa-required`, { required })

// ---------- API keys (WI-980 + WI-981 UI + WI-982 scopes) ----------
export interface ApiKeySummary {
  id: string
  account_id: string
  account_email: string
  name: string
  key_prefix: string
  created_at: string
  expires_at: string | null
  last_used_at: string | null
  last_used_ip: string | null
  revoked_at: string | null
  revoke_reason: string | null
  scopes: string[] | null
  is_active: boolean
}
export interface ApiKeyListResponse {
  items: ApiKeySummary[]
  count: number
}
export interface ApiKeyIssueInput {
  name: string
  expiresAt?: string         // ISO 8601 datetime
  forAccountId?: string      // platform admin only; omit for self
  scopes: string[]           // WI-982: required, must be subset of account perms
}
export interface ApiKeyIssueResponse {
  id: string
  account_id: string
  name: string
  plaintext: string          // returned ONCE
  key_prefix: string
  created_at: string
  expires_at: string | null
  scopes: string[]
  warning: string
}
export interface ApiKeyAvailablePermissionsResponse {
  account_id: string
  permissions: string[]
  count: number
}
export const listApiKeys = (accountId?: string) => {
  const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''
  return request<ApiKeyListResponse>('GET', `/ops/api-keys${qs}`)
}
export const issueApiKey = (input: ApiKeyIssueInput) =>
  request<ApiKeyIssueResponse>('POST', '/ops/api-keys', input)
export const revokeApiKey = (id: string, reason?: string) =>
  request<{ id: string; revoked: boolean; was_already_revoked: boolean }>(
    'DELETE', `/ops/api-keys/${id}`, reason ? { reason } : undefined,
  )
export const listAvailableApiKeyPermissions = (accountId?: string) => {
  const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''
  return request<ApiKeyAvailablePermissionsResponse>('GET', `/ops/api-keys/available-permissions${qs}`)
}

// ---------- Invitations (WI-006 anonymous endpoints) ----------
export interface InvitationDescribeResponse {
  valid: boolean
  invitation_id?: string
  email?: string
  display_name?: string | null
  expires_at?: string
  error?: string
}
export interface InvitationAcceptInput {
  token: string
  password: string
  display_name?: string
}
export interface InvitationAcceptResponse {
  account_id: string
  email: string
  display_name: string
  pharmacy_url_code?: string | null
  pharmacy_slug?: string | null
}

export const describeInvitation = (token: string) =>
  request<InvitationDescribeResponse>('GET', `/invitations/accept?token=${encodeURIComponent(token)}`)

export const acceptInvitation = (input: InvitationAcceptInput) =>
  request<InvitationAcceptResponse>('POST', '/invitations/accept', input)

// ---------- Password reset ----------
export const requestPasswordReset = (email: string) =>
  request<{ ok: true; message: string }>('POST', '/auth/forgot-password', { email })

export const completePasswordReset = (token: string, password: string) =>
  request<{ ok: true; pharmacy_url_code?: string | null; pharmacy_slug?: string | null }>('POST', '/auth/reset-password', { token, password })


export interface RxStatesResponse {
  clinical_states: string[]
  payer_states: string[]
  terminal_clinical_states: string[]
  payer_states_allowing_ready_to_fill: string[]
  clinical_transitions: { from: string; to: string; allowed_roles: string[] }[]
  payer_transitions:    { from: string; to: string; allowed_roles: string[] }[]
}
export const getRxStates = () => request<RxStatesResponse>('GET', '/workflow/rx-states')

export interface PatientLite {
  id: string
  mrn: string
  display_name: string
  date_of_birth: string
  sex: string | null
  insurance_carrier: string | null
  allergies: string | null
}
export const listPatientsLite = () =>
  request<{ items: PatientLite[] }>('GET', '/workflow/patients')

export interface PrescriberLite {
  id: string
  npi: string
  display_name: string
  specialty: string | null
  clinic_name: string | null
}
export const listPrescribersLite = () =>
  request<{ items: PrescriberLite[] }>('GET', '/workflow/prescribers')

// Prescribers THIS patient has Rx from — the scoped list for the "share to provider" picker.
// has_account = the provider has a linked active portal account, so a notification will actually reach them.
export interface PatientPrescriber { id: string; display_name: string; npi: string | null; clinic_name: string | null; has_account: boolean }
export const getPatientRxPrescribers = (patientId: string) =>
  request<{ items: PatientPrescriber[] }>('GET', `/patients/${patientId}/prescribers`)

// ---------- Rx ----------

/** G-UI-1 S1: one server-paged, server-sorted query for a whole tab (comma-separated states).
 *  Replaces the per-state client-side union that made honest paging impossible. */
// ---------- D-RXFILTER-1: server-side column filters ----------
//
// The Rx list used to abandon pagination the moment any funnel was ticked, because the filtering
// happened in the browser and you cannot page a set you are about to filter locally. These types
// carry the filter to the SERVER instead, so "filtered" and "paged" stop being mutually exclusive.
//
// The frontend deliberately keeps NO list of which columns are filterable — that second list is
// how the old design drifted (a 9-column backend sort whitelist against ~40 header funnels).
// Capability comes from getRxColumnCaps() below, i.e. from the server's registry.

export type RxFilterOp = 'in' | 'gte' | 'lte' | 'eq' | 'gt' | 'lt' | 'contains'
export interface RxFilter { key: string; op: RxFilterOp; values: string[] }

/** Wire form: `filter=<key>:<op>:<v1>,<v2>` — repeated, greppable in logs, no opaque blob. */
export const encodeRxFilters = (filters: RxFilter[]): string[] =>
  filters
    .filter(f => f.values.length > 0)
    .map(f => `${f.key}:${f.op}:${f.values.join(',')}`)

export interface RxColumnCap {
  key: string
  klass: 'configured' | 'enum' | 'entity' | 'date' | 'numeric' | 'refused'
  sortable: boolean
  filterable: boolean
  facetable: boolean
  ops: RxFilterOp[]
  /** Why a capability is off. Shown in the UI — a disabled funnel must say why, never just fail. */
  reason: string | null
}
/** The server registry's capability report. Cached hard: it changes only on deploy. */
export const getRxColumnCaps = () =>
  request<{ items: RxColumnCap[]; count: number }>('GET', '/rx/columns')

/**
 * D-VIEWS-SRV-1 WS2 — what a CUSTOM VIEW's columns can do, from the server.
 *
 * Same shape as RxColumnCap so the frontend reads ONE contract on every list. The key is the
 * DISPLAY LABEL, because that is what the grid keys rows and sort state by; `column_name` is the
 * physical name, which a filter pushdown needs and must never be confused with the key (WS1
 * shipped that confusion once and every sort silently no-opped).
 */
export interface ViewColumnCap extends RxColumnCap { column_name: string }

/** Cached hard client-side: a view's shape changes when someone edits the view, not per request. */
export const getViewColumnCaps = (viewId: string) =>
  request<{ items: ViewColumnCap[]; count: number }>('GET', `/views/${viewId}/columns`)

export interface RxFacetOption { value: string; label: string }
export interface RxFacetResponse {
  column: string
  klass: RxColumnCap['klass']
  /** configured = the pharmacy's authoritative choice list (includes values no row uses yet);
   *  distinct = TOP-capped observed values; entity = typeahead returning ids; range = use a
   *  from/to or numeric input; not_enumerable = never listed, search only. */
  source: 'configured' | 'distinct' | 'entity' | 'range' | 'not_enumerable'
  options: RxFacetOption[]
  /** True when the TOP cap was hit. Surfaced to the user — never a silent trim. */
  truncated: boolean
  notice: string | null
}
/** Options for ONE column, scoped to the tab and to every OTHER active filter. */
export const getRxFacet = (opts: {
  column: string; q?: string; states?: string; filters?: RxFilter[]
  payerBlocked?: boolean; claimRejected?: boolean
}) => {
  const p = new URLSearchParams({ column: opts.column })
  if (opts.q) p.set('q', opts.q)
  if (opts.states) p.set('state', opts.states)
  if (opts.payerBlocked) p.set('payer_blocked', '1')
  if (opts.claimRejected) p.set('claim_rejected', '1')
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<RxFacetResponse>('GET', `/rx/facets?${p.toString()}`)
}

export const listRxPaged = (opts: {
  /** Comma-separated clinical states. Omit to span every state (e.g. the payer-block queue). */
  states?: string; page: number; pageSize: number
  orderBy?: string; orderDir?: 'asc' | 'desc'; q?: string; pharmacyId?: string
  /** P1.1: server-side payer-block filter (payer_state present and not approved/not_required). */
  payerBlocked?: boolean
  /** Open claim rejections: newest rx_claims row carries NCPDP response status R. */
  claimRejected?: boolean
  /** D-RXFILTER-1: registry-validated column filters, applied in the QUERY. An unknown key or a
   *  disallowed operator is a 400 from the server — never silently dropped. */
  filters?: RxFilter[]
}) => {
  const p = new URLSearchParams({
    page: String(opts.page), page_size: String(opts.pageSize),
  })
  if (opts.states) p.set('state', opts.states)
  if (opts.payerBlocked) p.set('payer_blocked', '1')
  if (opts.claimRejected) p.set('claim_rejected', '1')
  if (opts.orderBy) p.set('order_by', opts.orderBy)
  if (opts.orderDir) p.set('order_dir', opts.orderDir)
  if (opts.q) p.set('q', opts.q)
  if (opts.pharmacyId) p.set('pharmacy_id', opts.pharmacyId)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<{ items: RxItem[]; count: number; total: number; page: number; page_size: number; total_pages: number }>('GET', `/rx?${p.toString()}`)
}

export interface RxItem {
  id: string
  patient_id: string
  patient_name: string
  patient_mrn: string
  // D-SOC-4 F-2: a site-of-care block stood here with no column, no projection and no consumer.
  // Removed 2026-09-20; see docs/audits/REVIEW-2026-09-20-site-of-care.md §2.
  prescriber_id: string
  prescriber_name: string
  prescriber_npi?: string
  drug_name: string
  drug_strength: string
  drug_form: string
  sig: string
  quantity_authorized: number
  quantity_unit: string
  refills_authorized: number
  state: string
  state_reason: string | null
  hold_returns_to?: string | null
  payer_state: string
  payer_reason: string | null
  insurance_carrier: string | null
  created_by_account: string
  created_at: string
  last_state_change_at: string
  quantity_filled: number
  quantity_remaining?: number
  // D-HOME-2 click-through: present in the list payload so metric-card filters
  // replay exactly over loaded rows.
  refills_remaining?: number | null
  days_supply?: number | null
  pioneerrx_rx_status?: string | null
  /** ASP-56: DOB + phone on the row so list search matches them (six DOB formats, digit phone). */
  patient_dob?: string | null
  patient_phone?: string | null
  /** Ingested at source by PioneerRxFullProjection — read directly, never re-extracted. */
  priority_type_text?: string | null
  current_tx_status_text?: string | null
  /** Latest delivery for this Rx: shipment status (in_transit/delivered/...) via formal link or imported Ref# match. */
  ship_status?: string | null
  ship_delivered_at?: string | null
  ship_id?: string | null  // CV-2a: TRUE rx_items.id — in fill-grain lists `id` is the fill's id; all workbench/custom-field writes must use this.
  rx_item_id?: string
  /** Canonical copay: latest POSITIVE rx_claims.patient_pay_amount_paid — same source as the label/shipping. */
  copay_amount?: number | null
}

// listRx (the unpaginated GET /rx helper) is DELETED. It had no callers left, and leaving it would
// have been a trap: /rx now paginates unconditionally, so it would have quietly returned the first
// 50 rows to anyone who reached for "give me the list".  Use listRxPaged.

export interface AvailableAction {
  kind: 'clinical' | 'payer'
  to_state: string
  label: string
  blocked: boolean
  blocked_reason?: string | null
}

export interface RxStateLogEntry {
  machine: 'clinical' | 'payer'
  from_state: string | null
  to_state: string
  actor_role: string
  actor_account: string
  reason: string | null
  transitioned_at: string
}

export interface RxFill {
  id: string
  fill_number: number
  quantity_filled: number
  days_supply: number | null
  lot_number: string | null
  filled_by_account: string
  filled_at: string
  is_reversed: boolean
  reversed_at?: string | null
  reversed_reason?: string | null
}

export interface RxDetailResponse {
  rx: RxItem
  state_log: RxStateLogEntry[]
  fills: RxFill[]
  available_actions: AvailableAction[]
}

export const getRx = (id: string) => request<RxDetailResponse>('GET', `/rx/${id}`)

// ---------- Rx (full PioneerRx-mapped detail) ----------
export type RxFullField = string | number | boolean | null
export interface RxFullResponse {
  phi_access?: boolean
  phi_masked?: boolean
  rx: Record<string, RxFullField>
  patient: Record<string, RxFullField>
  prescriber: Record<string, RxFullField>
  dispensed: Record<string, RxFullField>[]
  claims: Record<string, RxFullField>[]
  employees: Record<string, { display_name: string; employee_type: string | null }>
  fills: Array<{
    id: string
    fill_number: number
    quantity_filled: number
    days_supply: number | null
    lot_number: string | null
    filled_by_account: string
    filled_at: string
    is_reversed: boolean
    reversed_at: string | null
    reversed_reason: string | null
  }>
  state_log: Array<{
    machine: string
    from_state: string | null
    to_state: string
    actor_role: string
    actor_account: string
    reason: string | null
    transitioned_at: string
  }>
}
export const getRxFull = (id: string, mask = false) => request<RxFullResponse>('GET', `/rx/${id}/full${mask ? '?mask=1' : ''}`)

// ---------- Patients ----------
export interface PatientListItem {
  // D-SOC-2: the patient's current site of care. These were deleted on 2026-09-20 (D-SOC-4 F-2)
  // because they had no column, no projection and no consumer. They are BACKED now —
  // dbo.patient_site_of_care (mig 401) with the projection in ListPatientsRich — so the reason
  // for the deletion no longer holds.
  site_of_care_type?: string | null
  site_of_care_facility?: string | null
  id: string
  mrn: string
  display_name: string
  date_of_birth: string | null
  sex: string | null
  gender: string | null
  primary_phone: string | null
  email: string | null
  city: string | null
  state_code: string | null
  insurance_carrier: string | null
  allergies: string | null
  allergy_alert_type: string | null
  status: string | null
  language_text: string | null
  delivery_method: string | null
  is_animal: boolean
  rx_total: number
  rx_active: number
  last_rx_change: string | null
}
export interface PatientFullResponse {
  patient: Record<string, RxFullField>
  rx: Array<{
    id: string
    drug_name: string
    drug_strength: string
    drug_form: string
    sig: string
    quantity_authorized: number
    quantity_unit: string
    refills_authorized: number
    state: string
    payer_state: string
    created_at: string
    last_state_change_at: string
    rx_number: string | null
    refill_number: number
    prescriber_name: string
    is_controlled: boolean
    prescribed_dea_schedule: string | null
  }>
}
export interface ShippingPharmacyAddress { source: 'profile' | 'carrier' | null; name?: string | null; line1?: string | null; city?: string | null; state?: string | null; zip?: string | null; phone?: string | null }
export const getShippingPharmacyAddress = (carrierAccountId?: string) =>
  request<ShippingPharmacyAddress>('GET', `/shipping/pharmacy-address${carrierAccountId ? `?carrier_account_id=${carrierAccountId}` : ''}`)
// D-RXFILTER-2: /patients paginates unconditionally now, so a helper that LOOKS like "give me all
// the patients" would quietly hand back the first page instead. The three remaining callers are all
// type-ahead PICKERS (EntityPicker, CreateShipmentDrawer, CareRxPotQueue) which already slice to
// 6-12 results, so the bound is made explicit here rather than left to a server default.
export const listPatients = (q?: string, limit = 50) =>
  listPatientsPaged({ page: 1, pageSize: limit, q })
export const listPatientsPaged = (opts: {
  page: number; pageSize: number; q?: string
  orderBy?: string; orderDir?: 'asc' | 'desc'
  /** Registry-validated column filters, applied in the QUERY. An unknown key or disallowed
   *  operator is a 400 from the server — never silently dropped. */
  filters?: RxFilter[]
}) => {
  const p = new URLSearchParams({ page: String(opts.page), page_size: String(opts.pageSize) })
  if (opts.q) p.set('q', opts.q)
  if (opts.orderBy) p.set('order_by', opts.orderBy)
  if (opts.orderDir) p.set('order_dir', opts.orderDir)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<{ items: PatientListItem[]; count: number; total?: number; page: number; page_size: number; total_pages?: number; truncated?: boolean }>('GET', `/patients?${p.toString()}`)
}

/** The patients registry's capability report. Cached hard: it changes only on deploy. */
export const getPatientColumnCaps = () =>
  request<{ items: RxColumnCap[]; count: number }>('GET', '/patients/columns')

/** Options for ONE patients column, scoped to every OTHER active filter. */
export const getPatientFacet = (opts: { column: string; q?: string; filters?: RxFilter[] }) => {
  const p = new URLSearchParams({ column: opts.column })
  if (opts.q) p.set('q', opts.q)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<RxFacetResponse>('GET', `/patients/facets?${p.toString()}`)
}
export const getPatient = (id: string) =>
  request<PatientFullResponse>('GET', `/patients/${id}`)

// ---------- Prescribers ----------
export interface PrescriberListItem {
  id: string
  npi: string
  dea_number: string | null
  display_name: string
  first_name: string | null
  last_name: string | null
  suffix: string | null
  specialty: string | null
  prescriber_type: string | null
  status: string | null
  clinic_name: string | null
  primary_phone: string | null
  primary_fax: string | null
  email: string | null
  address_line: string | null
  city: string | null
  state_code: string | null
  rx_total: number
  last_rx_at: string | null
}
export interface PrescriberFullResponse {
  prescriber: Record<string, RxFullField>
  rx: Array<{
    id: string
    drug_name: string
    drug_strength: string
    drug_form: string
    sig: string
    quantity_authorized: number
    quantity_unit: string
    refills_authorized: number
    state: string
    payer_state: string
    created_at: string
    last_state_change_at: string
    rx_number: string | null
    refill_number?: number
    patient_name: string
    patient_mrn: string
    is_controlled: boolean
  }>
  // WI-908: team members at the same clinic_name (excludes alt-ids of this prescriber)
  team?: Array<{
    id: string
    display_name: string
    specialty: string | null
    npi: string | null
    primary_phone: string | null
    is_synthetic: boolean
    source: string | null
    address_line?: string | null
    city?: string | null
    state_code?: string | null
    rx_total: number
  }>
  // 'clinic_name' if matched by clinic, 'address' if matched by address triple, null if no team
  team_match_kind?: 'clinic_name' | 'address' | null
  // WI-908: all prescriber row ids whose Rxs are aggregated into this view
  // (the row clicked + any same-name/same-clinic duplicates from PioneerRx).
  merged_ids?: string[]
}
// D-RXFILTER-3: /prescribers pages unconditionally now, so this is an EXPLICITLY bounded lookup for
// the type-ahead pickers (EntityPicker and friends), not a "give me everything" helper.
export const listPrescribers = (q?: string, limit = 50) =>
  listPrescribersPaged({ page: 1, pageSize: limit, q })

// Server-paged prescribers. Paging happens AFTER the WI-908 dedup/merge — a logical prescriber is
// several physical rows — so `total` counts merged prescribers, which is what the UI shows.
export const listPrescribersPaged = (opts: {
  page: number; pageSize: number; q?: string
  orderBy?: string; orderDir?: 'asc' | 'desc'
  filters?: RxFilter[]
}) => {
  const p = new URLSearchParams({ page: String(opts.page), page_size: String(opts.pageSize) })
  if (opts.q) p.set('q', opts.q)
  if (opts.orderBy) p.set('order_by', opts.orderBy)
  if (opts.orderDir) p.set('order_dir', opts.orderDir)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<{ items: PrescriberListItem[]; count: number; total?: number; page: number; page_size: number; total_pages?: number; truncated?: boolean }>('GET', `/prescribers?${p.toString()}`)
}

/** The prescribers registry's capability report. Cached hard: changes only on deploy. */
export const getPrescriberColumnCaps = () =>
  request<{ items: RxColumnCap[]; count: number }>('GET', '/prescribers/columns')

/** Options for ONE prescribers column, scoped to every OTHER active filter. */
export const getPrescriberFacet = (opts: { column: string; q?: string; filters?: RxFilter[] }) => {
  const p = new URLSearchParams({ column: opts.column })
  if (opts.q) p.set('q', opts.q)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<RxFacetResponse>('GET', `/prescribers/facets?${p.toString()}`)
}
export const getPrescriber = (id: string) =>
  request<PrescriberFullResponse>('GET', `/prescribers/${id}`)

// ---------- Manufacturer role (D-MFR-1) ----------
// Catalog behind the drug-grant picker. Sourced from what this pharmacy has actually
// dispensed (rx_dispensed_detail), grouped by NDC. One search box matches NDC, drug
// name, brand, generic and manufacturer. `labeler` is the first five digits of the
// NDC — FDA-assigned, and the only trustworthy grouping key: the manufacturer string
// is free text and lossy (163 distinct labelers vs 149 distinct strings on ASP).
export interface ManufacturerDrug {
  ndc: string
  labeler: string
  brand_name: string | null
  generic_name: string | null
  drug_name: string | null
  strength: string | null
  manufacturer: string | null
  fills: number
}
export interface ManufacturerScopeItem {
  match_type: 'ndc' | 'labeler'
  match_value: string
}
export const listManufacturerDrugs = (q?: string) =>
  request<{ items: ManufacturerDrug[]; total: number; note?: string }>(
    'GET', `/manufacturer/drug-catalog${q ? `?q=${encodeURIComponent(q)}` : ''}`)

// The manufacturer's own read surface. Already scoped and already masked by the
// server — `phi_visible` reports which, it does not control it.
export interface ManufacturerRxItem {
  rx_id: string
  ndc: string
  drug: string | null
  strength: string | null
  quantity: string | null
  days_supply: string | null
  filled_at: string | null
  state: string | null
  patient_state: string | null
  patient: string | null
  patient_mrn: string | null
  patient_dob: string | null
}
// Grants for an EXISTING account. The invite form is not the only place a Manufacturer
// role can be assigned, so the grant has to be readable and writable here too.
export const getUserDrugAccess = (userId: string) =>
  request<{ grants: ManufacturerScopeItem[]; count: number; phi_access: 'masked' | 'full'; agreement_ref: string | null }>(
    'GET', `/users/${userId}/drug-access`)
export const setUserDrugAccess = (
  userId: string,
  body: { grants: ManufacturerScopeItem[]; phi_access: 'masked' | 'full'; agreement_ref?: string | null },
) => request<{ ok: boolean; count: number; phi_access: string }>('PUT', `/users/${userId}/drug-access`, body)

export const listManufacturerRx = (q?: string) =>
  request<{ items: ManufacturerRxItem[]; total: number; phi_visible: boolean }>(
    'GET', `/manufacturer/rx${q ? `?q=${encodeURIComponent(q)}` : ''}`)

// Full scoped set for the table + CSV export (server caps at 5000). Same scoping and
// masking as the paged list above.
export const listManufacturerRxAll = (limit = 5000) =>
  request<{ items: ManufacturerRxItem[]; total: number; phi_visible: boolean }>(
    'GET', `/manufacturer/rx?limit=${limit}`)

// Dashboard aggregates over the same scoped fill set — counts only, no patient data.
export interface ManufacturerAnalytics {
  total_fills: number
  distinct_drugs: number
  distinct_manufacturers: number
  first_fill: string | null
  last_fill: string | null
  by_drug: { ndc: string; drug: string | null; fills: number }[]
  by_month: { month: string | null; fills: number }[]
  by_state: { state: string; fills: number }[]
}
export const getManufacturerAnalytics = () =>
  request<ManufacturerAnalytics>('GET', '/manufacturer/analytics')

// ---------- Provider portal ----------
export interface ProviderRxItem {
  id: string
  // D-SOC-2 (S2), 2026-09-21: site of care, BACKED this time. A block of the same name was deleted
  // here on 2026-09-20 (F-2) precisely because it had no column, no projection and no consumer.
  // It returns now with `SiteOfCareProjectionAsync` behind it in GET /provider/rx — the therapy's
  // own `care_delivery_mode` and facility SNAPSHOT, joined by rx_number. Null where the tenant has
  // no CareRx or no live therapy matches; absent reads as absent, never as a default.
  site_of_care_type?: string | null
  site_of_care_facility?: string | null
  rx_number: string | null
  refill_number: number
  drug_name: string
  drug_strength: string
  drug_form: string
  sig: string
  quantity_authorized: number
  quantity_unit: string
  refills_authorized: number
  state: string
  payer_state: string
  created_at: string
  last_state_change_at: string
  patient_id: string
  patient_name: string
  patient_mrn: string
  prescriber_id: string
  prescriber_name: string
  is_controlled: boolean
  prescribed_dea_schedule: string | null
  comment_count: number
  pioneerrx_rx_status?: string | null
  pioneerrx_tx_status?: string | null
  directions_translated?: string | null
  patient_dob?: string | null
  unread_for_provider: number
}
/**
 * D-PROV-2: the provider lists are server-paged, server-filtered and server-searched.
 *
 * Previously these fetched the newest 1,000 rows and the browser did the paging, the state funnel
 * and the search. On ASP (44k Rx) that made the search box quietly wrong — it only ever saw the
 * loaded window, so a patient outside it came back as "no matches" rather than "not loaded".
 * `q` and `state` now go to the server, which applies them across the caller's whole scope.
 */
export interface ProviderListQuery {
  page?: number
  pageSize?: number
  q?: string
  state?: string
  orderBy?: string
  orderDir?: 'asc' | 'desc'
  /** D-PROVGRID-1: per-column funnels, applied server-side over the WHOLE caseload — not over
   *  the page on screen. Same wire form and same engine as the pharmacy list's filters. */
  filters?: RxFilter[]
  /** D-SOC-6: site-of-care code (home, infusion_center, snf…), matching the column S2 shows. */
  siteOfCare?: string
  /** Shipping-status chips. Long-standing param; typed here so it stops travelling untyped. */
  ship?: string
}

function providerListQs(o: ProviderListQuery = {}): string {
  const p = new URLSearchParams()
  if (o.page) p.set('page', String(o.page))
  if (o.pageSize) p.set('page_size', String(o.pageSize))
  if (o.q) p.set('q', o.q)
  if (o.state) p.set('state', o.state)
  if (o.orderBy) p.set('order_by', o.orderBy)
  if (o.orderDir) p.set('order_dir', o.orderDir)
  if (o.ship) p.set('ship', o.ship)
  if (o.siteOfCare) p.set('site_of_care', o.siteOfCare)   // D-SOC-6
  for (const f of encodeRxFilters(o.filters ?? [])) p.append('filter', f)
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** The provider registry's capability report. The portal keeps NO second list of what can be
 *  filtered — a second list is how the pharmacy list's sort whitelist and its header funnels
 *  drifted apart in the first place. */
export const getProviderRxColumnCaps = () =>
  request<{ items: RxColumnCap[]; count: number }>('GET', '/provider/rx/columns')

/** Options for ONE provider-list column, scoped to the caller's prescriber + drug grant and to
 *  every OTHER active filter. */
export const getProviderRxFacet = (opts: { column: string; q?: string; filters?: RxFilter[]; patient?: string }) => {
  const p = new URLSearchParams({ column: opts.column })
  if (opts.q) p.set('q', opts.q)
  // The patient card lists one patient's prescriptions, so its funnels offer that patient's values.
  if (opts.patient) p.set('patient', opts.patient)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<RxFacetResponse>('GET', `/provider/rx/facets?${p.toString()}`)
}

/** The canonical paged envelope (Paging.cs PagedResult), shared by both provider lists. */
export interface ProviderPage<T> {
  items: T[]
  count: number
  total: number
  page: number
  page_size: number
  total_pages: number
  truncated?: boolean
}

export const listProviderRx = (o: ProviderListQuery = {}) =>
  request<ProviderPage<ProviderRxItem>>('GET', `/provider/rx${providerListQs(o)}`)

/**
 * Practice-wide counts for the provider home tiles.
 *
 * These MUST come from the server: the tiles used to be derived by filtering the full in-memory
 * list, which only told the truth while the list was "everything". Against one page of 25 rows the
 * same arithmetic answers a different question and shows a confidently wrong number.
 */
export interface ProviderSummary {
  total_rx: number
  active_rx: number
  active_patients: number
  total_patients: number
  needs_attention: number
}
export const getProviderSummary = () =>
  request<ProviderSummary>('GET', '/provider/summary')

export interface ProviderPatientItem {
  id: string
  display_name: string | null
  mrn: string | null
  rx_count: number
  last_rx_at: string | null
}
export const listProviderPatients = (o: ProviderListQuery = {}) =>
  request<ProviderPage<ProviderPatientItem>>('GET', `/provider/patients${providerListQs(o)}`)

// ---- D-SOC-9 S2/S3: the facility portal's own data source ---------------------------------
// Same DTOs as the provider ones on purpose. ProviderDashboard renders all three tabs and reads
// list_columns config; only its five api.* calls are provider-specific, so the facility portal
// swaps the DATA SOURCE and reuses the component rather than forking it (L-067).
export const getFacilitySummary = () =>
  request<ProviderSummary>('GET', '/facility/summary')
export const listFacilityPatients = (o: ProviderListQuery = {}) =>
  request<ProviderPage<ProviderPatientItem>>('GET', `/facility/patients${providerListQs(o)}`)

export interface ProviderLink { id: string; name: string | null; role: 'nurse' | 'provider' }
export const listProviderLinks = () =>
  request<{ links: ProviderLink[]; count: number }>('GET', '/provider/links')

export interface ProviderPatientRx {
  id: string
  rx_number: string | null
  refill_number: number | null
  drug_name: string | null
  drug_strength: string | null
  drug_form: string | null
  sig: string | null
  quantity_authorized: number | null
  quantity_unit: string | null
  refills_authorized: number | null
  state: string | null
  payer_state: string | null
  created_at: string | null
  last_state_change_at: string | null
  prescriber_name: string | null
  pioneerrx_rx_status?: string | null
  pioneerrx_tx_status?: string | null
  directions_translated?: string | null
  ship_status?: string | null
  ship_delivered_at?: string | null
  site_of_care_type?: string | null
  site_of_care_facility?: string | null
}
export interface ProviderPatientDetail {
  patient: Record<string, any> | null
  rx: ProviderPatientRx[]
  /** Rows in THIS response — unchanged meaning, so a pre-paging caller still reads correctly. */
  rx_count: number
  /** The patient's FULL in-scope Rx count, which is what the header and pager need. */
  rx_total?: number
  page?: number
  page_size?: number
  total_pages?: number
}
export const getProviderPatient = (id: string, o: ProviderListQuery = {}) =>
  request<ProviderPatientDetail>('GET', `/provider/patients/${id}${providerListQs(o)}`)

// ---------- Rx comments ----------
export interface MentionUser { id: string; display_name: string; email: string }
export interface RxComment {
  id: string
  author_role: string
  author_display_name: string
  author_account_id?: string | null
  body: string
  notify_pharmacy: boolean
  notify_provider: boolean
  comment_type: string
  created_at: string
  edited_at?: string | null
  mentioned_account_ids?: string | null
}
export const listRxComments = (rxId: string) =>
  request<{ items: RxComment[]; count: number }>('GET', `/rx/${rxId}/comments`)
export const createRxComment = (rxId: string, body: {
  body: string
  notify_pharmacy?: boolean
  notify_provider?: boolean
  comment_type?: string
  mentions?: string[]
}) =>
  request<{ id: string; notify_pharmacy: boolean; notify_provider: boolean }>('POST', `/rx/${rxId}/comments`, body)
export const editRxComment = (rxId: string, commentId: string, body: string, mentions?: string[]) =>
  request<{ id: string; edited: boolean }>('PATCH', `/rx/${rxId}/comments/${commentId}`, { body, mentions })
export const deleteRxComment = (rxId: string, commentId: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/rx/${rxId}/comments/${commentId}`)
export const searchMentionUsers = (q: string) =>
  request<{ items: MentionUser[] }>('GET', `/comment-mention-users?q=${encodeURIComponent(q)}`)

// ---------- Notifications ----------
export interface RxNotification {
  id: string
  entity_type?: 'rx' | 'patient'
  rx_item_id: string | null
  rx_refs?: { rx_number: string; rx_id: string }[]
  patient_id?: string | null
  rx_number: string | null
  drug_name: string | null
  drug_strength: string | null
  patient_name: string
  kind: 'comment' | 'pa_submitted'
  snippet: string
  created_at: string
  acknowledged_at: string | null
  acknowledged_by: string | null
  author_role: string | null
  author_display_name: string | null
}
export interface PatientComment {
  id: string
  author_role: string
  author_display_name: string
  author_account_id?: string | null
  body: string
  notify_pharmacy: boolean
  notify_provider: boolean
  created_at: string
  edited_at?: string | null
  mentioned_account_ids?: string | null
}
export const getPatientComments = (patientId: string) =>
  request<{ items: PatientComment[]; count: number }>('GET', `/patients/${patientId}/comments`)
export const postPatientComment = (patientId: string, body: string, mentions?: string[]) =>
  request<{ id: string; created: boolean }>('POST', `/patients/${patientId}/comments`, { body, mentions })
export const editPatientComment = (patientId: string, commentId: string, body: string, mentions?: string[]) =>
  request<{ id: string; edited: boolean }>('PATCH', `/patients/${patientId}/comments/${commentId}`, { body, mentions })
export const deletePatientComment = (patientId: string, commentId: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/patients/${patientId}/comments/${commentId}`)
export const listNotifications = (unreadOnly = false) =>
  request<{
    mode: 'pharmacy' | 'saas'
    items: RxNotification[]
    count: number
    unread: number
    hint?: string
  }>('GET', `/notifications/rx${unreadOnly ? '?unread=1' : ''}`)
export const ackNotification = (id: string) =>
  request<{ acknowledged: boolean }>('POST', `/notifications/${id}/ack`, {})

// ---------- WI-896 SaaS notifications ----------
export interface SaasAlert {
  id: string
  severity: 'info' | 'warn' | 'error'
  kind: string
  title: string
  detail: string
  link: string
  created_at: string
  context: Record<string, unknown>
}
export interface SaasNotifications {
  mode: 'saas'
  count: number
  counts_by_severity: { error: number; warn: number; info: number }
  items: SaasAlert[]
  generated_at: string
}
export const listSaasNotifications = () =>
  request<SaasNotifications>('GET', '/saas/notifications')

export const saasNotificationsUnreadCount = (since?: string) =>
  request<{ mode: 'saas'; unread: number; breakdown: Record<string, number>; since: string | null }>(
    'GET', `/saas/notifications/unread-count${since ? `?since=${encodeURIComponent(since)}` : ''}`)

// ---------- Dashboards ----------
export interface PulseStateBucket { count: number; avg_wait_min: number; oldest_min: number }
export interface OwnerSummary {
  generated_at: string
  pulse: {
    in_progress: PulseStateBucket
    active_therapy: PulseStateBucket
    on_hold: PulseStateBucket
    ready_to_fill: PulseStateBucket
    pickup_delivery: PulseStateBucket
    expired: PulseStateBucket
    awaiting_verification: PulseStateBucket  // legacy alias
    refills_pending: number
  }
  action_lane: {
    unread_provider_comments: number
    recent_comments: Array<{
      id: string; author_role: string; author_name: string; body: string
      created_at: string
      rx_id: string; rx_number: string | null; drug_name: string; patient_name: string; patient_id: string
    }>
    pa_awaiting: number
    old_holds: Array<{
      rx_id: string; rx_number: string | null; drug_name: string
      patient_name: string; last_state_change_at: string; hours_in_hold: number
      patient_id: string
    }>
    rejected_24h: number
    old_holds_count?: number
    card_params?: { hold_hours: number; aging_days: number; rejected_hours: number; pa_states: string[]; volume_days?: number }
    aging_precheck: { count: number; items: { rx_id: string; rx_number: string | null; drug_name: string; patient_name: string; days_in_precheck: number; patient_id: string }[] }
    expiring_soon: number
    recently_closed: number
  }
  status_breakdown: Array<{ status: string; count: number; oldest_min: number }>
  volume_sparkline: Array<{ day: string; count: number }>
}
export const getOwnerSummary = () =>
  request<OwnerSummary>('GET', '/dashboards/owner-summary')

export interface OpsBoardEntry { value: string; count: number; color_bg?: string | null; color_text?: string | null }
export interface OpsDrillRow {
  rx_id: string; rx_number: string | null; drug_name: string | null
  patient_name: string | null; status_value: string | null; since: string | null
}
export interface OperationalDashboard {
  generated_at: string
  pharmacy_queue: OpsBoardEntry[]
  rx_status: OpsBoardEntry[]
  tx_status: OpsBoardEntry[]
  delivery: { shipments: OpsBoardEntry[]; past_promise_unshipped: number; delivery_calls_pending: number }
  refills: { pending_requests: number; low_refills: number; expiring_30d: number }
  time_in_state: Array<{ state: string; count: number; avg_min: number; max_min: number }>
  hold_reasons: Array<{ reason: string; count: number }>
  reject_reasons_30d: Array<{ reason: string; count: number }>
  recent_cancellations: Array<{
    rx_id: string; rx_number: string | null; drug_name: string
    patient_name: string; cancelled_at: string; reason: string
  }>
  throughput_24h: Array<{ hours_ago: number; verified: number; filled: number; dispensed: number }>
}
export const getOperationalDashboard = () =>
  request<OperationalDashboard>('GET', '/dashboards/operational')
export const getOperationalRxDrill = (dim: string, value?: string) =>
  request<{ dim: string; value: string; rows: OpsDrillRow[]; count: number }>(
    'GET', `/dashboards/operational/rx?dim=${encodeURIComponent(dim)}${value != null ? `&value=${encodeURIComponent(value)}` : ''}`)

export interface ClinicalDashboard {
  generated_at: string
  high_alert: Array<{
    rx_id: string; rx_number: string | null; drug_name: string; drug_strength: string
    schedule: string | null; state: string; patient_name: string; patient_mrn: string
    prescriber_name: string; last_state_change_at: string
  }>
  polypharmacy: Array<{ patient_id: string; patient_name: string; mrn: string; active_rx: number }>
  open_clinical_questions: Array<{
    comment_id: string; author_role: string; author_name: string; body: string
    created_at: string
    rx_id: string; rx_number: string | null; drug_name: string; patient_name: string
  }>
  pa_stuck_48h: Array<{
    rx_id: string; rx_number: string | null; drug_name: string; payer_state: string
    patient_name: string; last_state_change_at: string; hours_pending: number
  }>
  refills_overdue_24h: Array<{
    request_id: string; requested_at: string
    rx_id: string; rx_number: string | null; drug_name: string; patient_name: string
    hours_overdue: number
  }>
}
export const getClinicalDashboard = () =>
  request<ClinicalDashboard>('GET', '/dashboards/clinical')

// ---------- Reports ----------
export interface ReportEnvelope<T> {
  name: string
  from: string
  to: string
  rows?: T[]
  summary?: Record<string, any>
  denial_reasons?: { reason: string; count: number }[]
  detail?: any[]
  detail_truncated?: boolean
  detail_limit?: number
}
export const getReport = <T = any>(name: string, from?: string, to?: string) => {
  const qs: string[] = []
  if (from) qs.push(`from=${from}`)
  if (to)   qs.push(`to=${to}`)
  const q = qs.length ? `?${qs.join('&')}` : ''
  return request<ReportEnvelope<T>>('GET', `/reports/${name}${q}`)
}

// Prior-authorization-by-drug report (D-PA-1 downstream) — dedicated aggregate,
// not date-ranged. Grouped drug × insurance with confirmed-PA counts + real profit.
export interface PaByDrugRow {
  drug_name: string
  generic_name: string
  carrier: string
  pa_status: 'no_pa' | 'pa_required' | 'mixed'
  pa_confirmed: number
  pa_required: number
  pa_pending: number
  pa_denied: number
  total_rx: number
  avg_profit: number | null
  avg_margin_pct: number | null
}
export interface PaByDrugEnvelope {
  group: 'drug' | 'molecule'
  summary: { drugs_with_confirmed_pa: number; rx_pa_confirmed: number; pa_denied: number; pa_outstanding: number }
  rows: PaByDrugRow[]
  row_count: number
}
export const getPaByDrugReport = (opts?: { group?: 'drug' | 'molecule'; orderBy?: string; orderDir?: 'asc' | 'desc'; minRx?: number }) => {
  const qs: string[] = []
  if (opts?.group)    qs.push(`group=${opts.group}`)
  if (opts?.orderBy)  qs.push(`order_by=${encodeURIComponent(opts.orderBy)}`)
  if (opts?.orderDir) qs.push(`order_dir=${opts.orderDir}`)
  if (opts?.minRx)    qs.push(`min_rx=${opts.minRx}`)
  const q = qs.length ? `?${qs.join('&')}` : ''
  return request<PaByDrugEnvelope>('GET', `/reports/analytics/pa-by-drug${q}`)
}

// Note: Rx mutation client functions (createRx, transitionRx, fillRx,
// fulfillRefill, denyRefill, submitPriorAuth) were removed in Sprint 10
// per ADR-014 Phase B. PioneerRx is the system of record; the portal
// observes state and offers comments + refill *requests* only.

// ---------- Pharmacy onboarding (platform admin) ----------
export interface PharmacyListItem {
  id: string
  name: string
  slug: string | null
  npi: string | null
  ncpdp: string | null
  city: string | null
  state_code: string | null
  email: string | null
  primary_phone: string | null
  onboarding_state: 'pending_baa' | 'pending_provision' | 'pending_admin' | 'pending_ingest_setup' | 'active' | 'suspended'
  baa_reference: string | null
  baa_effective_date: string | null
  baa_expiry_date: string | null
  coo_approval_recorded_at: string | null
  coo_approver_name: string | null
  primary_admin_email: string | null
  sso_provider: string
  created_at: string
  active_users: number
  pending_invitations: number
  active_creds_created_at: string | null
  parent_organization_id: string | null
  parent_organization_name: string | null
  branch_label: string | null
  /** WI-892 — present when pharmacy is soft-deleted */
  archived_at?: string | null
  archive_reason?: string | null
  /** WI-902 — branding for the topbar chip when SaaS admin enters this pharmacy */
  logo_url?: string | null
  primary_color_hex?: string | null
  /** Opaque anti-enumeration login code (migration S1+). Canonical /p/{url_code} login path. */
  url_code?: string | null
  /** Storage utilization (migration 163): bytes used across blob-backed docs, and the quota. */
  storage_used_bytes?: number
  storage_quota_bytes?: number
}
export interface PharmacyDetail {
  pharmacy: PharmacyListItem & {
    address_line: string | null
    zip_code: string | null
    dea: string | null
    coo_approval_recorded_by: string | null
    logo_url: string | null
    display_name_override: string | null
    primary_color_hex: string | null
    /** D-CRX-76 — letterhead identity; the generated documents print it. */
    primary_fax?: string | null
    /** D-POT-6 — LOA §11 signatory / invoice BILL TO contact (mig 454). */
    doc_signatory_name?: string | null
    doc_signatory_title?: string | null
    storage_used_bytes?: number
    storage_quota_bytes?: number
    storage_price_per_gb_cents?: number | null
    storage_included_gb?: number
  }
  active_credentials: {
    id: string
    username: string
    created_at: string
    last_used_at: string | null
    rotation_grace_until: string | null
    ip_allowlist: string | null
  } | null
  webhook_url: string | null
  sibling_branches?: { id: string; name: string; branch_label: string | null; onboarding_state: string }[]
  // Present only while onboarding_state === 'pending_provision': catalog registry state so the
  // UI can show a failed provision (status 'failed' + reason) instead of an endless spinner.
  provisioning?: { status: string; failure_reason: string | null; updated_at: string } | null
}
export interface ParentOrganization {
  id: string
  display_name: string
  slug: string
  billing_email: string | null
  status: 'active' | 'suspended'
  created_at: string
  branch_count?: number
}
export interface CreatePharmacyInput {
  name: string
  npi?: string
  ncpdp?: string
  dea?: string
  email?: string
  primaryPhone?: string
  addressLine?: string
  city?: string
  stateCode?: string
  zipCode?: string
  baaReference: string
  baaEffectiveDate?: string
  baaExpiryDate?: string
  cooApproverName: string
  cooApproverEmail: string
  ssoProvider?: 'none' | 'okta' | 'azure_ad' | 'google_workspace'
  parentOrganizationId?: string
  newParentOrganizationName?: string
  branchLabel?: string
}
export interface CredentialsResponse {
  credential_id: string
  username: string
  password: string
  webhook_url: string
  warning: string
  grace_period_until?: string
}
// ── D-RPT-2 report builder v2 (AI writes SQL against the tenant's real schema) ──────────────────
export interface ReportAiSqlResponse {
  explanation: string
  sql: string | null
  sql_valid: boolean
  sql_reason: string | null
  schema_tables: number
  schema_columns: number
  model?: string
  usage?: { input_tokens: number | null; output_tokens: number | null; cost_usd: number | null }
}
export const reportAiSql = (input: {
  pharmacyId: string
  messages: { role: string; content: string }[]
  model?: string
  currentSql?: string
  lastError?: string
}) => request<ReportAiSqlResponse>('POST', '/reports/ai/sql', {
  // camelCase on the wire: the server uses PropertyNameCaseInsensitive, which matches
  // pharmacyId->PharmacyId but NOT snake_case pharmacy_id->PharmacyId. Sending snake_case made the
  // server see no pharmacy -> 422 pharmacy_required even though one was selected.
  pharmacyId: input.pharmacyId,
  messages: input.messages,
  model: input.model,
  currentSql: input.currentSql,
  lastError: input.lastError,
})

export interface ReportRunSqlResponse {
  columns: string[]
  rows: (unknown)[][]
  row_count: number
  truncated: boolean
  row_cap: number
  elapsed_ms: number
  estimated_cost: number
  estimated_rows: number
}
export const reportRunSql = (input: { pharmacyId: string; sql: string; rowCap?: number }) =>
  request<ReportRunSqlResponse>('POST', '/reports/run-sql', {
    pharmacyId: input.pharmacyId, sql: input.sql, rowCap: input.rowCap,
  })

export const listPharmacies = (opts?: { includeArchived?: boolean; onlyArchived?: boolean }) => {
  const params = new URLSearchParams()
  if (opts?.includeArchived) params.set('include_archived', '1')
  if (opts?.onlyArchived) params.set('only_archived', '1')
  const qs = params.toString()
  return request<{ items: PharmacyListItem[]; count: number }>('GET',
    `/pharmacies${qs ? '?' + qs : ''}`)
}

// WI-892
export const archivePharmacy = (id: string, reason?: string) =>
  request<{ id: string; archived: boolean; archived_at: string; reason: string | null }>(
    'POST', `/pharmacies/${id}/archive`, reason ? { reason } : {})

export const unarchivePharmacy = (id: string) =>
  request<{ id: string; archived: boolean }>('POST', `/pharmacies/${id}/unarchive`, {})

export const hardDeletePharmacy = (id: string, slug: string) =>
  request<{ id: string; slug: string; name: string; deleted: boolean; child_row_counts: Record<string, number>; child_rows_total: number }>(
    'DELETE', `/pharmacies/${id}?confirm=${encodeURIComponent(slug)}`)

// WI-894
export interface PharmacyManager {
  membership_id: string
  account_id: string
  email: string
  display_name: string
  is_primary: boolean
  granted_at: string
  granted_by_account_id: string | null
}
export const listPharmacyManagers = (id: string) =>
  request<{ pharmacy_id: string; members: PharmacyManager[]; count: number; self_is_member: boolean }>(
    'GET', `/pharmacies/${id}/managers`)
export const selfAssignPharmacyManager = (id: string) =>
  request<{ pharmacy_id: string; slug: string; name: string; membership_id: string; already_member: boolean }>(
    'POST', `/pharmacies/${id}/managers/self`, {})
export const selfUnassignPharmacyManager = (id: string) =>
  request<{ pharmacy_id: string; slug: string; name: string; unassigned?: boolean; not_a_member?: boolean }>(
    'DELETE', `/pharmacies/${id}/managers/self`)
export const listParentOrgs = () =>
  request<{ items: ParentOrganization[]; count: number }>('GET', '/parent-organizations')
export const getPharmacy = (id: string) =>
  request<PharmacyDetail>('GET', `/pharmacies/${id}`)
/**
 * D-CRX-79 — the letterhead identity only. GetPharmacy sums dbo.pharmacy_documents to report
 * storage usage, which a document renderer has no use for and which costs seconds on a tenant with
 * a lot of documents.
 */
export interface PharmacyLetterhead {
  pharmacy: {
    id: string; name: string | null; display_name_override: string | null
    logo_url: string | null; primary_color_hex: string | null
    address_line: string | null; city: string | null; state_code: string | null; zip_code: string | null
    primary_phone: string | null; primary_fax: string | null
    npi: string | null; state_license_number: string | null
  }
}
export const getPharmacyLetterhead = (id: string) =>
  request<PharmacyLetterhead>('GET', `/pharmacies/${id}/letterhead`)
export const createPharmacy = (input: CreatePharmacyInput) =>
  request<{ id: string; slug: string; onboarding_state: string; next_step: string; parent_organization_id: string | null }>(
    'POST', '/pharmacies', {
      name: input.name,
      npi: input.npi, ncpdp: input.ncpdp, dea: input.dea,
      email: input.email, primaryPhone: input.primaryPhone,
      addressLine: input.addressLine, city: input.city,
      stateCode: input.stateCode, zipCode: input.zipCode,
      baaReference: input.baaReference,
      baaEffectiveDate: input.baaEffectiveDate,
      baaExpiryDate: input.baaExpiryDate,
      cooApproverName: input.cooApproverName,
      cooApproverEmail: input.cooApproverEmail,
      ssoProvider: input.ssoProvider ?? 'none',
      parentOrganizationId: input.parentOrganizationId,
      newParentOrganizationName: input.newParentOrganizationName,
      branchLabel: input.branchLabel,
    })
export const invitePharmacyAdmin = (pharmacyId: string, email: string, displayName?: string) =>
  request<{ invite_id: string; email: string; expires_at: string; invite_link: string }>(
    'POST', `/pharmacies/${pharmacyId}/invite-admin`, { email, displayName })

// ADR-019 D-9: per-pharmacy module entitlement (CareRx, Queues) + time-boxed trials.
export type ModuleStatus = 'disabled' | 'active' | 'trial' | 'trial_expired'
export interface PharmacyModule {
  module_key: string
  label: string
  status: ModuleStatus
  effective_enabled: boolean
  trial_ends_at: string | null
  updated_at: string | null
  /** Set when this key is a sub-tab of another module (e.g. carerx_screening_gate -> carerx). */
  parent_key?: string | null
  /** Modules this one leans on (e.g. carerx -> drugreference). Advisory, never auto-enabled. */
  recommends?: string[]
}
export const listPharmacyModules = (pharmacyId: string) =>
  request<{ pharmacy_id: string; modules: PharmacyModule[] }>('GET', `/pharmacies/${pharmacyId}/modules`)
export const setPharmacyModule = (
  pharmacyId: string, moduleKey: string, input: { enabled: boolean; trial_days?: number },
) =>
  request<{ pharmacy_id: string; module_key: string; enabled: boolean; trial_ends_at: string | null }>(
    'PUT', `/pharmacies/${pharmacyId}/modules/${moduleKey}`, input)

// Self read of the caller's effective module entitlement (for nav gating). Map of module_key -> enabled.
export const getMyModules = () =>
  request<{ modules: Record<string, boolean> }>('GET', '/me/modules')

// Secure Share — pharmacy inbox of documents submitted in by providers/nurses (source='secure_share').
export interface SecureShareDoc {
  id: string; patient_id: string; patient_name?: string | null
  document_type?: string | null; title?: string | null; file_name?: string | null
  lifecycle_status?: string | null; size_bytes?: number | null
  received_at?: string | null; submitted_by_external?: boolean
  can_delete?: boolean
}
export const listSecureShareDocuments = (q?: string) =>
  // I-SS-401: bearer-only (omit cookie) — matches /auth/me + the upload, which authenticate
  // reliably; the cookie-included variant was returning 401 anonymous despite a valid token.
  // ASP-76: optional q runs the search server-side so documents beyond the TOP 500 cap
  // are still findable (client-side search alone can only see loaded rows).
  request<{ documents: SecureShareDoc[] }>('GET', `/secure-share/documents${q ? `?q=${encodeURIComponent(q)}` : ''}`, undefined, { credentials: 'omit' })

// Secure Share docs ARE patient_documents, so download uses the shared patient-document endpoint
// (legacy SAS or, for encrypted docs, decrypted bytes streamed through the Function).
export const downloadSecureShareDoc = (patientId: string, docId: string) =>
  fetchDocDownload(`/patients/${patientId}/documents/${docId}/download`)

// Move a Secure Share document through its lifecycle; notifies the submitting doctor (prescriber).
export const SECURE_SHARE_STATUSES = [
  'received', 'in_review', 'action_required', 'on_hold', 'completed', 'cancelled',
] as const
export const setSecureShareDocStatus = (docId: string, status: string) =>
  request<{ id: string; status: string; previous: string | null }>(
    'PUT', `/secure-share/documents/${docId}/status`, { status })

// Soft-delete a Secure Share document (owner, or pharmacy manager/admin). Bearer-only (I-SS-401).
export const deleteSecureShareDocument = (docId: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/secure-share/documents/${docId}`, undefined, { credentials: 'omit' })

// Document comment thread (provider <-> pharmacy). A provider may tag the pharmacy team (notify_pharmacy);
// the pharmacy may flag a comment for the provider (notify_provider).
export interface DocComment {
  id: string
  author_role: string
  author_name: string
  body: string
  notify_pharmacy: boolean
  notify_provider: boolean
  created_at: string
  is_me: boolean
}
export const listDocumentComments = (docId: string) =>
  request<{ comments: DocComment[] }>('GET', `/secure-share/documents/${docId}/comments`)
export const addDocumentComment = (docId: string, body: string, opts?: { notify_pharmacy?: boolean; notify_provider?: boolean }) =>
  request<{ id: string; created: boolean }>('POST', `/secure-share/documents/${docId}/comments`, { body, ...(opts || {}) })

// Create a placeholder patient (provider uploads for someone not yet enrolled; PioneerRx reconciles later).
export const createSecureSharePlaceholderPatient = (name: string, dateOfBirth: string) =>
  request<{ id: string; mrn: string; display_name: string; date_of_birth: string }>(
    'POST', '/secure-share/placeholder-patients', { name, date_of_birth: dateOfBirth })

// The placeholders THIS user has already uploaded for — so they can add more docs instead of duplicating.
export interface SecureSharePlaceholder { id: string; display_name: string | null; date_of_birth: string | null; mrn: string | null }
export const getMySecureSharePlaceholders = () =>
  // I-SS-401: bearer-only (omit cookie), same rationale as listSecureShareDocuments.
  request<{ items: SecureSharePlaceholder[] }>('GET', '/secure-share/my-placeholders', undefined, { credentials: 'omit' })

// Per-account RBAC: which providers may use Secure Share (managed under My Pharmacy → Secure Share tab).
export interface SecureShareAccessProvider { id: string; display_name: string | null; email: string | null; granted: boolean; expires_at: string | null }
export const getSecureShareAccess = () =>
  request<{ restricted: boolean; providers: SecureShareAccessProvider[] }>('GET', '/pharmacy/secure-share-access')
export const grantSecureShareAccess = (accountId: string, days?: number) =>
  request<{ account_id: string; granted: boolean; days: number | null }>('POST', `/pharmacy/secure-share-access/${accountId}`, days && days > 0 ? { days } : {})
export const revokeSecureShareAccess = (accountId: string) =>
  request<{ account_id: string; granted: boolean }>('DELETE', `/pharmacy/secure-share-access/${accountId}`)

// ── D-API-1: Data API configuration (Pharmacy Settings, gated on data_api.manage) ────────────────
export interface DataApiField { field: string; is_phi: boolean; classification: string }
export interface DataApiResourceCatalog { resource: string; version: string; fields: DataApiField[] }
export interface DataApiProvider { id: string; name: string; npi?: string | null }
export interface DataApiClientRow {
  id: string; name: string; purpose: string | null; status: string; cert_pinned: boolean;
  cert_expiry: string | null; baa_ref: string | null; baa_expiry: string | null; created_at: string;
  active_keys: number; grant_count: number; provider_count: number;
  contact_name: string | null; contact_email: string | null; contact_phone: string | null;
  rate_limit_per_min: number | null; daily_quota: number | null; max_page_size: number | null; query_timeout_sec: number | null
}
export interface DataApiReportRef { id: string; name: string }
export interface DataApiLimits { max_clients: number; max_keys_per_client: number; clients_used: number }
export interface DataApiBootstrap { clients: DataApiClientRow[]; catalog: DataApiResourceCatalog[]; providers: DataApiProvider[]; reports: DataApiReportRef[]; limits: DataApiLimits }
export interface DataApiKeyRow { id: string; key_prefix: string; created_at: string; expires_at: string | null; last_used_at: string | null; revoked: boolean; active: boolean }
export interface DataApiGrantRow { resource: string; field: string; mode: string }
export interface DataApiClientDetail { id: string; grants: DataApiGrantRow[]; provider_ids: string[]; report_ids: string[]; keys: DataApiKeyRow[] }
export interface DataApiClientBody {
  name?: string; purpose?: string; baaRef?: string; baaExpiry?: string; certFingerprint?: string; certExpiry?: string; status?: string;
  contactName?: string; contactEmail?: string; contactPhone?: string;
  rateLimitPerMin?: number; dailyQuota?: number; maxPageSize?: number; queryTimeoutSec?: number
}

export const getDataApiConfig = () => request<DataApiBootstrap>('GET', '/pharmacy/data-api')

// ── D-HOOK-1: outbound webhooks (a copy of each matching PioneerRx envelope, pushed to a partner endpoint) ──
export interface WebhookRow {
  id: string; client_id: string; name: string; url: string; auth_type: 'basic' | 'none'; auth_username: string | null
  password_set: boolean; signing_secret_set: boolean; timeout_sec: number; rate_limit_per_min: number
  status: 'pending' | 'active' | 'paused' | 'disabled'; consecutive_failures: number
  last_success_at: string | null; last_failure_at: string | null; last_error: string | null; created_at: string
  npis: string[]; group_ids: string[]
  queued: number; sending: number; sent_24h: number; failed_24h: number
}
export interface WebhookList { hooks: WebhookRow[]; provider_groups: { id: string; name: string }[]; limits: { max_webhooks: number; hooks_used: number } }
export interface WebhookBody {
  name?: string; url?: string; username?: string; password?: string; signingSecret?: string; clearSigningSecret?: boolean
  timeoutSec?: number; rateLimitPerMin?: number; status?: 'pending' | 'active' | 'paused' | 'disabled'
}
export interface WebhookDelivery {
  id: string; status: 'queued' | 'sending' | 'sent' | 'failed' | 'dead'; attempts: number; created_at: string
  next_attempt_at: string | null; last_attempt_at: string | null; sent_at: string | null
  response_status: number | null; response_ms: number | null; last_error: string | null
  matched_npi: string | null; matched_by: string; replay_of: string | null
  received_at: string; message_id: string | null; initiating_event_id: string | null; envelope_sha256: string; rx_number: string | null
}
export interface WebhookTestResult { ok: boolean; outcome: string; status: number | null; ms: number; error: string | null; response_snippet: string | null }
export const listWebhooks = () => request<WebhookList>('GET', '/pharmacy/data-api/hooks')
export const createWebhook = (clientId: string, b: WebhookBody & { name: string; url: string }) =>
  request<{ id: string; status: string }>('POST', `/pharmacy/data-api/clients/${clientId}/hooks`, b)
export const updateWebhook = (id: string, b: WebhookBody) => request<{ ok: boolean }>('PUT', `/pharmacy/data-api/hooks/${id}`, b)
export const setWebhookFilters = (id: string, npis: string[], providerGroupIds: string[]) =>
  request<{ ok: boolean; npis: number; provider_groups: number }>('PUT', `/pharmacy/data-api/hooks/${id}/filters`, { npis, providerGroupIds })
export const deleteWebhook = (id: string) => request<{ deleted: boolean }>('DELETE', `/pharmacy/data-api/hooks/${id}`)
export const testWebhook = (id: string) => request<WebhookTestResult>('POST', `/pharmacy/data-api/hooks/${id}/test`, {})
export const resumeWebhook = (id: string) => request<{ ok: boolean; status: string }>('POST', `/pharmacy/data-api/hooks/${id}/resume`, {})
export const replayWebhookDelivery = (id: string, deliveryId: string) =>
  request<{ id: string; status: string }>('POST', `/pharmacy/data-api/hooks/${id}/deliveries/${deliveryId}/replay`, {})
export const getWebhookDeliveryDetail = (id: string, deliveryId: string) =>
  request<{ id: string; response_snippet: string | null; last_error: string | null; response_status: number | null; response_ms: number | null; attempts: number; status: string }>(
    'GET', `/pharmacy/data-api/hooks/${id}/deliveries/${deliveryId}`)
export const listWebhookDeliveries = (id: string, p: { status?: string; npi?: string; q?: string; page: number; page_size: number; filter?: string[] }) => {
  const qs = new URLSearchParams()
  if (p.status) qs.set('status', p.status); if (p.npi) qs.set('npi', p.npi); if (p.q) qs.set('q', p.q)
  qs.set('page', String(p.page)); qs.set('page_size', String(p.page_size))
  for (const f of p.filter ?? []) if (f) qs.append('filter', f)
  return request<{ items: WebhookDelivery[]; total: number; page: number; page_size: number; total_pages: number }>('GET', `/pharmacy/data-api/hooks/${id}/deliveries?${qs}`)
}
export const drainWebhooksNow = () => request<{ processed: number }>('POST', '/pharmacy/data-api/hooks/drain-now', {})
export interface WebhookDeliveryAll extends WebhookDelivery { endpoint_id: string; hook_name: string; client_name: string; disclosure_id: string | null }
export interface WebhookDeliveriesAllParams { status?: string; hook_id?: string; npi?: string; q?: string; since_hours?: number; page: number; page_size: number; filter?: string[] }
const webhookDeliveriesQs = (p: WebhookDeliveriesAllParams) => {
  const qs = new URLSearchParams()
  if (p.status) qs.set('status', p.status); if (p.hook_id) qs.set('hook_id', p.hook_id); if (p.npi) qs.set('npi', p.npi); if (p.q) qs.set('q', p.q)
  if (p.since_hours) qs.set('since_hours', String(p.since_hours))
  qs.set('page', String(p.page)); qs.set('page_size', String(p.page_size))
  for (const f of p.filter ?? []) if (f) qs.append('filter', f)
  return qs
}
/** D-HOOK-1: EVERY envelope sent / waiting / failed to ANY partner hook — compliance, tracking, troubleshooting. */
export const listAllWebhookDeliveries = (p: WebhookDeliveriesAllParams) =>
  request<{ items: WebhookDeliveryAll[]; total: number; page: number; page_size: number; total_pages: number; summary: { queued: number; sending: number; sent: number; failed: number; dead: number } }>(
    'GET', `/pharmacy/data-api/deliveries?${webhookDeliveriesQs(p)}`)
/** Same filter as the list, as a CSV download (audited as webhook.export). */
export const exportWebhookDeliveriesCsv = async (p: Omit<WebhookDeliveriesAllParams, 'page' | 'page_size'>) => {
  const qs = webhookDeliveriesQs({ ...p, page: 1, page_size: 25 }); qs.set('format', 'csv')
  const res = await apiFetch(`/pharmacy/data-api/deliveries?${qs}`)
  if (!res.ok) throw await parseApiError(res)
  return res.blob()
}
export const getDataApiClient = (id: string) => request<DataApiClientDetail>('GET', `/pharmacy/data-api/clients/${id}`)
export const createDataApiClient = (b: DataApiClientBody & { name: string }) =>
  request<{ id: string; status: string }>('POST', '/pharmacy/data-api/clients', b)
export const updateDataApiClient = (id: string, b: DataApiClientBody) =>
  request<{ ok: boolean }>('PUT', `/pharmacy/data-api/clients/${id}`, b)
export const issueDataApiKey = (id: string, expiresInDays?: number) =>
  request<{ key: string; key_prefix: string; expires_at: string | null; id: string }>('POST', `/pharmacy/data-api/clients/${id}/keys`, { expiresInDays })
export const revokeDataApiKey = (id: string, keyId: string) =>
  request<{ revoked: boolean }>('POST', `/pharmacy/data-api/clients/${id}/keys/${keyId}/revoke`)
export const setDataApiKeyActive = (id: string, keyId: string, active: boolean) =>
  request<{ ok: boolean; active: boolean }>('POST', `/pharmacy/data-api/clients/${id}/keys/${keyId}/${active ? 'activate' : 'deactivate'}`)
export const setDataApiGrants = (id: string, resource: string, grants: { field: string; mode: string }[]) =>
  request<{ ok: boolean; granted: number }>('PUT', `/pharmacy/data-api/clients/${id}/grants`, { resource, grants })
export const setDataApiProviders = (id: string, providerIds: string[]) =>
  request<{ ok: boolean; providers: number }>('PUT', `/pharmacy/data-api/clients/${id}/providers`, { providerIds })
export const deleteDataApiClient = (id: string) =>
  request<{ deleted: boolean }>('DELETE', `/pharmacy/data-api/clients/${id}`)
export interface DataApiDisclosure { requested_at: string; resource: string; version: string | null; fields: string | null; provider_scope: string | null; row_count: number; status: number | null; patient_count: number }
/** §164.528 accounting of disclosures. Server-paged: six-year retention makes this the largest
 *  table on the surface, and capping it client-side would hide the older half of the record. */
export const getDataApiClientAudit = (id: string, opts?: { page?: number; page_size?: number; filter?: string[] }) => {
  const p = new URLSearchParams({ page: String(opts?.page ?? 0), page_size: String(opts?.page_size ?? 50) })
  for (const f of opts?.filter ?? []) if (f) p.append('filter', f)
  return request<{ disclosures: DataApiDisclosure[]; total: number; page: number; page_size: number }>(
    'GET', `/pharmacy/data-api/clients/${id}/audit?${p}`)
}
export const setDataApiReports = (id: string, reportIds: string[]) =>
  request<{ ok: boolean; reports: number }>('PUT', `/pharmacy/data-api/clients/${id}/reports`, { reportIds })

// Upload a document IN via Secure Share for one of this pharmacy's patients (multipart/form-data).
export async function submitSecureShareDocument(
  patientId: string,
  file: File,
  fields: { documentType: string; title?: string; notes?: string },
): Promise<{ id?: string; created?: boolean }> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('document_type', fields.documentType)
  if (fields.title) fd.append('title', fields.title)
  if (fields.notes) fd.append('notes', fields.notes)
  const res = await fetch(`${API_BASE}/secure-share/patients/${patientId}/documents`, {
    method: 'POST',
    credentials: 'omit',
    headers: { ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}) },
    body: fd,
  })
  if (!res.ok) {
    const text = await res.text()
    let parsed: any = {}
    try { parsed = JSON.parse(text) } catch {}
    throw new ApiError(res.status, parsed, parsed.error || parsed.message || text || `HTTP ${res.status}`)
  }
  return res.json()
}


// WI-897: invite any role to a pharmacy
export const invitePharmacyUser = (pharmacyId: string, email: string, roleName: string, displayName?: string) =>
  request<{
    invite_id: string
    email: string
    role_id: string
    role_name: string
    expires_at: string
    invite_link: string
  }>('POST', `/pharmacies/${pharmacyId}/invite-user`, { email, displayName, roleName })

export interface PharmacyUser {
  id: string
  email: string
  display_name: string
  status: string
  last_login_at: string | null
  created_at: string
  is_synthetic: boolean
}
export interface PharmacyPendingInvite {
  invite_id: string
  email: string
  display_name: string | null
  role_ids_json: string | null
  expires_at: string
  invited_by_account_id: string | null
}
export const listPharmacyUsers = (pharmacyId: string) =>
  request<{
    pharmacy_id: string
    users: PharmacyUser[]
    pending_invitations: PharmacyPendingInvite[]
    user_count: number
    pending_count: number
  }>('GET', `/pharmacies/${pharmacyId}/users`)

// WI-897: admin-trigger password reset (returns the link directly)
export const adminTriggerPasswordReset = (accountId: string) =>
  request<{
    account_id: string
    email: string
    display_name: string
    reset_link: string
    expires_at: string
    message: string
  }>('POST', `/ops/users/${accountId}/password-reset`, {})

// ---------- WI-898: SaaS user management ----------
export const assignUserPharmacy = (accountId: string, pharmacyId: string | null) =>
  request<{ account_id: string; pharmacy_id: string | null; changed: boolean }>(
    'POST', `/ops/users/${accountId}/assign-pharmacy`, { pharmacy_id: pharmacyId })

export const forceSignOutUser = (accountId: string) =>
  request<{ account_id: string; sessions_revoked: number }>(
    'POST', `/ops/users/${accountId}/force-sign-out`, {})

export const lockUser = (accountId: string) =>
  request<{ account_id: string; status: string; sessions_killed: number }>(
    'POST', `/ops/users/${accountId}/lock`, {})

export const unlockUser = (accountId: string) =>
  request<{ account_id: string; status: string }>(
    'POST', `/ops/users/${accountId}/unlock`, {})

export const resetUserMfa = (accountId: string) =>
  request<{ account_id: string; mfa_status: string; message: string }>(
    'POST', `/ops/users/${accountId}/reset-mfa`, {})

export interface UserSession {
  id: string
  ip: string | null
  user_agent: string | null
  created_at: string
  last_seen_at: string
  expires_at: string
  revoked_at: string | null
  revoke_reason: string | null
}
export const listUserSessions = (accountId: string) =>
  request<{ account_id: string; items: UserSession[]; count: number }>(
    'GET', `/ops/users/${accountId}/sessions`)

export interface UserActivityEvent {
  id: number
  action: string
  entity_type: string | null
  entity_id: string | null
  actor_role: string | null
  actor_account: string | null
  occurred_at: string
}
export const listUserActivity = (accountId: string) =>
  request<{ account_id: string; email: string; items: UserActivityEvent[]; count: number }>(
    'GET', `/ops/users/${accountId}/activity`)

export const hardDeleteUser = (accountId: string, confirmEmail: string) =>
  request<{ deleted: boolean; account_id: string; email: string }>(
    'DELETE', `/ops/users/${accountId}?confirm=${encodeURIComponent(confirmEmail)}`)

export interface ActiveSession {
  id: string
  account_id: string
  email: string | null
  display_name: string | null
  pharmacy_id: string | null
  pharmacy_name: string | null
  role: string | null
  ip: string | null
  user_agent: string | null
  created_at: string
  last_seen_at: string
  expires_at: string
  idle_seconds: number
  online: boolean
}
export interface ActiveSessionsResponse {
  generated_at: string
  online_window_minutes: number
  summary: {
    active_sessions: number
    active_users: number
    online_now_sessions: number
    online_now_users: number
    logins_last_hour: number
    total_active_users: number
    total_pharmacies: number
    pharmacies_with_activity: number
    capped: boolean
  }
  by_pharmacy: {
    pharmacy_id: string | null
    pharmacy_name: string
    active_sessions: number
    online_now: number
    users: number
  }[]
  sessions: ActiveSession[]
}
export const getActiveSessions = (onlineMinutes = 5) =>
  request<ActiveSessionsResponse>('GET', `/ops/active-sessions?online_minutes=${onlineMinutes}`)
export const generateIngestCredentials = (pharmacyId: string) =>
  request<CredentialsResponse>('POST', `/pharmacies/${pharmacyId}/credentials`)
export const rotateIngestCredentials = (pharmacyId: string) =>
  request<CredentialsResponse>('POST', `/pharmacies/${pharmacyId}/credentials/rotate`)
export interface UpdatePharmacyInput {
  name?: string
  npi?: string | null
  ncpdp?: string | null
  dea?: string | null
  email?: string | null
  primaryPhone?: string | null
  addressLine?: string | null
  city?: string | null
  stateCode?: string | null
  zipCode?: string | null
  ssoProvider?: 'none' | 'okta' | 'azure_ad' | 'google_workspace'
  branchLabel?: string | null
  // Branding L1 (WI-823)
  displayNameOverride?: string | null
  primaryColorHex?: string | null
  // D-POT-6: who signs the LOA / is the invoice contact (mig 454)
  docSignatoryName?: string | null
  docSignatoryTitle?: string | null
  // Storage pricing (SaaS-admin only)
  storagePricePerGbCents?: number | null
  storageIncludedGb?: number | null
}
export const updatePharmacy = (pharmacyId: string, input: UpdatePharmacyInput) =>
  request<{ id: string; changed: number; fields?: string[]; message?: string }>(
    'PATCH', `/pharmacies/${pharmacyId}`, input)
export async function uploadPharmacyLogo(pharmacyId: string, file: File) {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${API_BASE}/pharmacies/${pharmacyId}/logo`, {
    method: 'POST', credentials: 'omit',
    headers: { ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}) },
    body: fd,
  })
  if (!res.ok) {
    const text = await res.text()
    let parsed: any = {}
    try { parsed = JSON.parse(text) } catch {}
    throw new ApiError(res.status, parsed, parsed.error || parsed.message || text || `HTTP ${res.status}`)
  }
  return res.json() as Promise<{ id: string; logo_url: string }>
}
export const suspendPharmacy = (pharmacyId: string) =>
  request<{ id: string; onboarding_state: string }>('POST', `/pharmacies/${pharmacyId}/suspend`)
export const reactivatePharmacy = (pharmacyId: string) =>
  request<{ id: string; onboarding_state: string }>('POST', `/pharmacies/${pharmacyId}/reactivate`)

// ---------- Pharmacy self-service (WI-824) ----------
export interface MyPharmacyResponse {
  pharmacy: PharmacyDetail['pharmacy']
  active_credentials: PharmacyDetail['active_credentials']
  webhook_url: string | null
  can_rotate_creds: boolean
}
export const getMyPharmacy = () =>
  request<MyPharmacyResponse>('GET', '/pharmacies/me')
export interface MyStorageResponse {
  patient_bytes: number; carerx_bytes: number; pharmacy_doc_bytes: number; exempt_bytes: number
  quota_bytes: number; enforced: boolean; used_pct: number; warn: boolean; over: boolean
}
export const getMyStorage = () =>
  request<MyStorageResponse>('GET', '/me/storage')

export interface OpsStoragePharmacy {
  pharmacy_id: string; name: string; slug: string
  patient_bytes: number; carerx_bytes: number; pharmacy_doc_bytes: number; exempt_bytes: number
  quota_bytes: number; enforced: boolean; used_pct: number
  computed_at: string | null; rollup_stale: boolean
}
export interface OpsStorageResponse {
  pharmacies: OpsStoragePharmacy[]
  total_patient_bytes: number; total_carerx_bytes: number; total_pharmacy_doc_bytes: number
  total_quota_bytes: number
  oldest_computed_at: string | null; any_stale: boolean
}
export const getOpsStorage = () =>
  request<OpsStorageResponse>('GET', '/ops/storage')
export const recomputeOpsStorage = () =>
  request<{ pharmacies: number; failures: number; total_tenant_bytes: number; note: string }>('POST', '/ops/storage/recompute')
export const setOpsStoragePolicy = (pharmacyId: string, body: { quota_bytes?: number; enforced?: boolean }) =>
  request<{ pharmacy_id: string; quota_bytes: number | null; enforced: boolean | null }>('PUT', `/ops/storage/${pharmacyId}/policy`, body)
export const updateMyPharmacy = (input: {
  name?: string; npi?: string | null; ncpdp?: string | null; dea?: string | null
  email?: string | null; primaryPhone?: string | null
  addressLine?: string | null; city?: string | null; stateCode?: string | null; zipCode?: string | null
  displayNameOverride?: string | null; primaryColorHex?: string | null
}) =>
  request<{ id: string; changed: number; fields?: string[]; message?: string }>(
    'PATCH', '/pharmacies/me', input)
export const rotateMyIngestCredentials = () =>
  request<CredentialsResponse>('POST', '/pharmacies/me/credentials/rotate')

// --- Per-pharmacy outbound email config (I-978). Config only; real transport is
//     provisioned by Owl Consultations (provider credentials). ---
export interface EmailConfig {
  from_email: string | null
  sender_name: string | null
  reply_to: string | null
  notifications_enabled: boolean
  configured: boolean
  transport_status: string
}
export const getMyEmailConfig = () => request<EmailConfig>('GET', '/pharmacies/me/email-config')
export const putMyEmailConfig = (input: {
  from_email?: string | null; sender_name?: string | null; reply_to?: string | null; notifications_enabled?: boolean
}) => request<{ ok: true }>('PUT', '/pharmacies/me/email-config', input)

export type EmailConfigInput = { from_email?: string | null; sender_name?: string | null; reply_to?: string | null; notifications_enabled?: boolean }
// Admin assigns a sender email to a specific pharmacy.
export const getPharmacyEmailConfig = (pharmacyId: string) => request<EmailConfig>('GET', `/pharmacies/${pharmacyId}/email-config`)
export const putPharmacyEmailConfig = (pharmacyId: string, input: EmailConfigInput) =>
  request<{ ok: true }>('PUT', `/pharmacies/${pharmacyId}/email-config`, input)

export interface EmailTransport {
  configured: boolean
  smtp_host?: string | null
  smtp_port?: number | null
  smtp_security?: string | null
  smtp_username?: string | null
  has_password?: boolean
  smtp_enabled?: boolean
  from_email?: string | null
  sender_name?: string | null
  last_test_at?: string | null
  last_test_ok?: boolean | null
  last_test_error?: string | null
}
export type EmailTransportInput = {
  smtp_host?: string | null
  smtp_port?: number | null
  smtp_security?: string | null
  smtp_username?: string | null
  smtp_password?: string | null
  smtp_enabled?: boolean
  from_email?: string | null
  sender_name?: string | null
}
export const getPharmacyEmailTransport = (pharmacyId: string) =>
  request<EmailTransport>('GET', `/pharmacies/${pharmacyId}/email-transport`)
export const putPharmacyEmailTransport = (pharmacyId: string, input: EmailTransportInput) =>
  request<{ ok: true }>('PUT', `/pharmacies/${pharmacyId}/email-transport`, input)
export const testPharmacyEmailTransport = (pharmacyId: string, to: string) =>
  request<{ ok: boolean; error?: string | null }>('POST', `/pharmacies/${pharmacyId}/email-transport/test`, { to })

export interface EmailOutbox {
  counts: Record<string, number>
  recent: { kind: string; recipient: string; status: string; attempts: number; last_error?: string | null; created_at: string; sent_at?: string | null }[]
}
export const getPharmacyEmailOutbox = (pharmacyId: string) =>
  request<EmailOutbox>('GET', `/pharmacies/${pharmacyId}/email-outbox`)
export const drainPharmacyEmailOutbox = (pharmacyId: string) =>
  request<{ ok: boolean; error?: string; via?: string; sent: number; failed: number; processed: number }>('POST', `/pharmacies/${pharmacyId}/email-outbox/drain`)
export const retryPharmacyEmailOutbox = (pharmacyId: string) =>
  request<{ ok: boolean; error?: string; via?: string; retried: number; sent: number; failed: number; processed: number }>('POST', `/pharmacies/${pharmacyId}/email-outbox/retry`)

export interface SenderDomain {
  configured: boolean
  domain?: string
  from_local_part?: string
  status?: string
  dns_records?: { type: string; name: string; value: string }[] | null
  last_checked_at?: string | null
  last_error?: string | null
  verified_at?: string | null
}
export const getPharmacySenderDomain = (pharmacyId: string) =>
  request<SenderDomain>('GET', `/pharmacies/${pharmacyId}/sender-domain`)
export const putPharmacySenderDomain = (pharmacyId: string, input: { domain: string; from_local_part?: string; status?: string }) =>
  request<{ ok: true; domain: string; from_local_part: string; status: string }>('PUT', `/pharmacies/${pharmacyId}/sender-domain`, input)
export const deletePharmacySenderDomain = (pharmacyId: string) =>
  request<{ ok: true }>('DELETE', `/pharmacies/${pharmacyId}/sender-domain`)
export const provisionPharmacySenderDomain = (pharmacyId: string) =>
  request<{ ok: boolean; error?: string; status?: string; dns_records?: { type: string; name: string; value: string }[]; states?: Record<string, string> }>('POST', `/pharmacies/${pharmacyId}/sender-domain/provision`)
export const verifyPharmacySenderDomain = (pharmacyId: string) =>
  request<{ ok: boolean; error?: string; status?: string; dns_records?: { type: string; name: string; value: string }[]; states?: Record<string, string>; linked?: boolean }>('POST', `/pharmacies/${pharmacyId}/sender-domain/verify`)
export const testPharmacySenderDomain = (pharmacyId: string, to: string) =>
  request<{ ok: boolean; via?: string; from?: string; error?: string }>('POST', `/pharmacies/${pharmacyId}/sender-domain/test`, { to })

export interface EmailTemplate {
  key: string
  label: string
  is_custom: boolean
  subject: string
  body_text: string
  default_subject: string
  default_body: string
  variables: { name: string; description: string }[]
}
export const listPharmacyEmailTemplates = (pharmacyId: string) =>
  request<{ items: EmailTemplate[] }>('GET', `/pharmacies/${pharmacyId}/email-templates`)
export const putPharmacyEmailTemplate = (pharmacyId: string, key: string, subject: string, body_text: string) =>
  request<{ ok: true }>('PUT', `/pharmacies/${pharmacyId}/email-templates/${key}`, { subject, body_text })
export const resetPharmacyEmailTemplate = (pharmacyId: string, key: string) =>
  request<{ ok: true }>('DELETE', `/pharmacies/${pharmacyId}/email-templates/${key}`)
export const previewPharmacyEmailTemplate = (pharmacyId: string, key: string, subject?: string, body_text?: string) =>
  request<{ subject: string; body_text: string }>('POST', `/pharmacies/${pharmacyId}/email-templates/${key}/preview`, { subject, body_text })
// Admin-portal (platform) sender email.
export const getPlatformEmailConfig = () => request<EmailConfig>('GET', '/platform/email-config')
export const putPlatformEmailConfig = (input: EmailConfigInput) =>
  request<{ ok: true }>('PUT', '/platform/email-config', input)

// --- Per-pharmacy DocuSign API config (I-977). Config only; real envelope send
//     requires a signed BAA + DocuSign secrets in Key Vault. Secret is write-only. ---
export interface DocusignConfig {
  account_id: string | null
  base_uri: string | null
  integration_key: string | null
  impersonated_user_id: string | null
  has_secret: boolean
  enabled: boolean
  configured: boolean
  send_status: string
  // Key Vault secret NAME for this pharmacy's DocuSign Connect HMAC key (mig 421). Returned in the
  // clear — it names a secret, it is not one. webhook_verified is the derived answer to "are
  // inbound DocuSign callbacks actually signature-verified for us?"; when false the webhook fails
  // closed (503) and statuses only advance via "Check signature".
  connect_hmac_kv_ref?: string | null
  webhook_verified?: boolean
}
export const getMyDocusignConfig = () => request<DocusignConfig>('GET', '/pharmacies/me/docusign-config')
export const putMyDocusignConfig = (input: {
  account_id?: string | null; base_uri?: string | null; integration_key?: string | null
  impersonated_user_id?: string | null; secret_kv_ref?: string | null
  connect_hmac_kv_ref?: string | null; enabled?: boolean
}) => request<{ ok: true }>('PUT', '/pharmacies/me/docusign-config', input)

export interface IpAllowlistResponse {
  ip_allowlist: string | null
  ip_count: number
  cleared: boolean
  message: string
}
export const setMyIngestIpAllowlist = (ipAllowlist: string) =>
  request<IpAllowlistResponse>('PUT', '/pharmacies/me/credentials/ip-allowlist', { ip_allowlist: ipAllowlist })

// --- SaaS-admin ingest network security (Network Security tab) ---
export interface IngestSecurityVendor {
  vendor: string
  display_name: string
  has_credential: boolean
  ip_allowlist: string | null
  webhook_url: string | null
}
export interface IngestSecurityResponse {
  pharmacy_id: string
  vendors: IngestSecurityVendor[]
}
export const getIngestSecurity = (pharmacyId: string) =>
  request<IngestSecurityResponse>('GET', `/pharmacies/${pharmacyId}/ingest-security`)
export const setIngestSecurityIpAllowlist = (pharmacyId: string, ipAllowlist: string, vendor = 'pioneerrx') =>
  request<IpAllowlistResponse & { vendor: string }>(
    'PUT', `/pharmacies/${pharmacyId}/ingest-security/ip-allowlist`, { vendor, ip_allowlist: ipAllowlist })

// --- CareRx AI config (key + token limit) ---
export interface AiConfigResponse {
  pharmacy_id?: string
  key_configured: boolean
  key_last4: string | null
  monthly_token_limit: number | null
  key_configured_at?: string | null
  ai_enabled: boolean
  note?: string
}
// Admin: set key and/or token limit for a pharmacy. Omit api_key to change only the limit.
export const getAiConfig = (pharmacyId: string) =>
  request<AiConfigResponse>('GET', `/pharmacies/${pharmacyId}/ai-config`)
export const setAiConfig = (pharmacyId: string, opts: { apiKey?: string; monthlyTokenLimit?: number | null }) =>
  request<AiConfigResponse>('PUT', `/pharmacies/${pharmacyId}/ai-config`, {
    api_key: opts.apiKey, monthly_token_limit: opts.monthlyTokenLimit,
  })
// Pharmacy self-service: view status + update own key (cannot set the token limit).
export const getMyAiConfig = () =>
  request<AiConfigResponse>('GET', '/pharmacies/me/ai-config')
export const setMyAiKey = (apiKey: string) =>
  request<AiConfigResponse & { message: string }>('PUT', '/pharmacies/me/ai-config/key', { api_key: apiKey })

// --- SaaS-admin Operations dashboard (app-level metrics) ---
export interface OpsDashboard {
  generated_at: string
  window_days: number
  ai: {
    month_to_date: { input: number; output: number; cost: number; calls: number }
    by_pharmacy: Array<{
      pharmacy_id: string | null
      name: string
      calls: number
      input_tokens: number
      output_tokens: number
      total_tokens: number
      cost_usd: number
      monthly_token_limit: number | null
      pct_of_limit: number | null
    }>
  }
  ingest_queue: { parse_status: Record<string, number>; mirror_status: Record<string, number> }
  activity: { audit_events: number; top_actions: Array<{ action: string; count: number }> }
  per_customer_cost?: {
    note: string
    customers: Array<{ pharmacy_id: string; name: string; slug: string | null; tenant_db_cost_usd: number; ai_cost_usd: number; total_cost_usd: number }>
  }
  azure_cost?: { available: boolean; reason?: string; currency?: string; month_to_date_cost?: number; by_service?: Array<{ service: string; cost: number }>; by_resource?: Array<{ resource: string; resource_id: string; cost: number }> }
  azure_performance?: { available: boolean; reason?: string; window?: string; metrics?: Array<{ name: string; unit?: string; avg: number; sum: number; last: number; peak?: number; points: number }> }
  azure_sql_tier?: {
    available: boolean; reason?: string
    database?: string; sku?: string; tier?: string; family?: string; capacity?: number
    compute?: 'serverless' | 'provisioned'; auto_pause_minutes?: number | null
    min_vcores?: number | null; max_size_gb?: number | null
  }
  azure_sql_databases?: {
    available: boolean; reason?: string; server?: string
    databases?: Array<{ database: string; tier?: string; sku?: string; capacity?: number; compute?: 'serverless' | 'provisioned'; auto_pause_minutes?: number | null }>
  }
}
export const getOpsDashboard = (days = 30) =>
  request<OpsDashboard>('GET', `/ops/dashboard?days=${days}`)

// Cost trends (SaaS-admin). Platform total + SQL per month.
export interface CostHistory {
  available: boolean
  reason?: string
  months?: { month: string; total: number; sql: number }[]
}
export const getCostHistory = (months = 6) =>
  request<CostHistory>('GET', `/ops/cost-history?months=${months}`)

// Per-customer cost trends. Each tenant DB mapped to its pharmacy; plus a shared/platform series.
export interface CostHistoryByCustomer {
  available: boolean
  reason?: string
  months?: string[]
  customers?: { name: string; series: { month: string; cost: number }[] }[]
  shared?: { name: string; series: { month: string; cost: number }[] }
}
export const getCostHistoryByCustomer = (months = 6) =>
  request<CostHistoryByCustomer>('GET', `/ops/cost-history-by-customer?months=${months}`)

// D-SAAS-OBS-1 Phase 1 — profitability + licenses + alert contacts (SaaS-admin only)
export const opsProfitability = () => request<any>('GET', '/ops/profitability')
export const opsSetLicense = (pharmacyId: string, monthlyLicenseUsd: number, effectiveFrom?: string) =>
  request<any>('PUT', `/ops/licenses/${pharmacyId}`, { monthlyLicenseUsd, effectiveFrom })
export const opsAlertContacts = () => request<any>('GET', '/ops/alert-contacts')
export const opsAddAlertContact = (email: string, displayName?: string) =>
  request<any>('POST', '/ops/alert-contacts', { email, displayName })
export const opsRemoveAlertContact = (id: string) => request<any>('DELETE', `/ops/alert-contacts/${id}`)
export const opsResourceMetrics = () => request<any>('GET', '/ops/resource-metrics')

// D-COST-4 — background worker controls (SaaS-admin only).
// The schedule is what "06:00" MEANS for timers: host crons run in UTC and ask the server
// whether the configured local hour has arrived, so the zone is a stored setting rather than
// an App Service restart. The window is when gated workers may WAKE a tenant database at all.
export type WorkerSchedule = {
  time_zone: string
  /** false = the stored zone id did not resolve and workers are silently running in UTC. A fault, not a preference. */
  time_zone_resolved: boolean
  reconcile_hour: number
  window_enabled: boolean
  /** ISO day numbers, Mon=1..Sun=7, e.g. "1,2,3,4,5" */
  window_days: string
  window_start_hour: number
  /** exclusive: 19 means work stops at 18:59 */
  window_end_hour: number
  /** 0 = no limit. Staging rows older than this stop being retried; nothing is deleted. */
  pioneer_retry_max_age_days: number
  in_window_now: boolean
  local_now: string
  /** Server-rendered in the CONFIGURED zone. Never format local_now in the browser — that shows
   *  the admin's clock, not the pharmacies'. */
  local_now_text: string
  utc_now: string
  utc_now_text: string
}
export type TenantWorkSignal = {
  branch_id: string
  name: string | null
  work_kind: string
  signal_count: number
  first_signal_at: string
  last_signal_at: string
  last_drained_at: string | null
  /** how long the OLDEST piece of work has waited — a signal that never clears is a failing drain */
  waiting_minutes: number
}
export const opsWorkerSchedule = () => request<WorkerSchedule>('GET', '/ops/worker-schedule')
export const opsSetWorkerSchedule = (body: Partial<{
  timeZone: string; reconcileHour: number; windowEnabled: boolean
  windowDays: string; windowStartHour: number; windowEndHour: number
  pioneerRetryMaxAgeDays: number
}>) => request<WorkerSchedule & { ok: boolean }>('PUT', '/ops/worker-schedule', body)
export const opsTenantWork = () =>
  request<{ signals: TenantWorkSignal[]; healthy: boolean; note: string }>('GET', '/ops/tenant-work')

// --- SaaS-admin overview (landing dashboard: users + reliability) ---
export interface OpsOverview {
  generated_at: string
  users: {
    total: number
    by_status: Record<string, number>
    locked: number
    active_last_30d: number
    new_last_30d: number
    pharmacies: number
    recent_logins: Array<{ name: string; email: string; last_login_at: string | null; pharmacy: string | null }>
  }
  reliability: {
    failed_actions_24h: number
    denied_actions_24h: number
    stuck_ingest_events: number
    accounts_with_failed_logins: number
    recent_failures: Array<{ action: string; actor_role: string; occurred_at: string }>
    note: string
  }
  app_errors?: {
    available: boolean
    reason?: string
    window?: string
    failed_requests_5xx?: number
    top_exceptions?: Array<{ type: string; message: string; count: number }>
    recent_traces?: Array<{ timestamp: string; severity: 'error' | 'warning'; message: string }>
    note?: string
  }
}
export const getOpsOverview = () => request<OpsOverview>('GET', '/ops/overview')

// ---------- Pharmacy documents (BAA + COO approval) — WI-822 ----------
export interface PharmacyDocument {
  id: string
  kind: 'baa' | 'coo_approval' | 'notice_of_privacy' | 'other'
  title: string | null
  file_name: string
  content_type: string
  size_bytes: number
  effective_date: string | null
  expiry_date: string | null
  supersedes_id: string | null
  status: 'executed' | 'docusign_sent' | 'docusign_signed' | 'docusign_declined'
  docusign_envelope_id: string | null
  notes: string | null
  uploaded_at: string
  uploaded_by_name: string
  archived_at: string | null
  is_current: boolean
}
export const listPharmacyDocuments = (pharmacyId: string) =>
  request<{ items: PharmacyDocument[]; count: number }>(
    'GET', `/pharmacies/${pharmacyId}/documents`)

export interface UploadDocumentInput {
  file: File
  kind: 'baa' | 'coo_approval' | 'notice_of_privacy' | 'other'
  title?: string
  effectiveDate?: string
  expiryDate?: string
  supersedesId?: string
  notes?: string
}
export async function uploadPharmacyDocument(pharmacyId: string, input: UploadDocumentInput) {
  const fd = new FormData()
  fd.append('file', input.file)
  fd.append('kind', input.kind)
  if (input.title)         fd.append('title', input.title)
  if (input.effectiveDate) fd.append('effective_date', input.effectiveDate)
  if (input.expiryDate)    fd.append('expiry_date', input.expiryDate)
  if (input.supersedesId)  fd.append('supersedes_id', input.supersedesId)
  if (input.notes)         fd.append('notes', input.notes)
  const res = await fetch(`${API_BASE}/pharmacies/${pharmacyId}/documents`, {
    method: 'POST', credentials: 'omit',
    headers: { ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}) },
    body: fd,
  })
  if (!res.ok) {
    const text = await res.text()
    let parsed: any = {}
    try { parsed = JSON.parse(text) } catch {}
    throw new ApiError(res.status, parsed, parsed.error || parsed.message || text || `HTTP ${res.status}`)
  }
  return res.json() as Promise<{ id: string; pharmacy_id: string; kind: string; file_name: string; size_bytes: number; uploaded_at: string }>
}
export const downloadPharmacyDocument = (docId: string) =>
  `${API_BASE}/pharmacy-documents/${docId}/download`
// Tokened download: fetch the SAS URL with the per-tab Bearer (correct user), then the
// caller navigates to it. Avoids the cookie-navigation cross-user bug and CSP connect-src.
export const getPharmacyDocumentDownloadUrl = (docId: string) =>
  request<{ url: string }>('GET', `/pharmacy-documents/${docId}/download?format=json`)
export const archivePharmacyDocument = (docId: string) =>
  request<{ id: string; archived: boolean }>('POST', `/pharmacy-documents/${docId}/archive`)


// ---------- PioneerRx event stream (admin) ----------
export interface PioneerRxEvent {
  id: string
  parsed_at: string
  message_id: string
  sent_on_utc: string | null
  initiating_event_id: string | null
  pharmacy_name: string | null
  patient_first_name: string | null
  patient_last_name: string | null
  rx_number: string | null
  refill_number: number | null
  script_type: string | null
  prescribed_drug_name: string | null
  dispensed_drug_name: string | null
  is_substitution: boolean
  current_rx_status_text: string | null
  current_rx_tx_status_text: string | null
  total_price_paid: number | null
  patient_paid_amount: number | null
  primary_payor_name: string | null
}
export const listPioneerRxEvents = (limit?: number) =>
  request<{ items: PioneerRxEvent[]; count: number; limit: number }>(
    'GET', `/pioneerrx/events${limit ? `?limit=${limit}` : ''}`)

// ---------- Patient self-scoped Rx ----------
export interface PatientRx {
  id: string
  rx_number: string | null
  refill_number: number
  drug_name: string
  drug_strength: string
  drug_form: string | null
  sig: string
  quantity: number
  quantity_unit: string
  refills_authorized: number
  refills_used: number
  state: string
  payer_state: string
  created_at: string
  last_state_change_at: string
  last_pioneerrx_sync_at: string | null
  is_controlled: boolean
  schedule: string | null
  prescriber_name: string
  comment_count: number
  pharmacy_id: string
  pharmacy_name: string
  branch_label: string | null
  ship_status?: string | null
  ship_delivered_at?: string | null
}
export interface PatientBranch {
  id: string
  name: string
  branch_label: string | null
}
export const listPatientRx = (branch?: string) =>
  request<{ items: PatientRx[]; count: number; branches: PatientBranch[]; selected_branch: string | null }>(
    'GET', `/patient/rx${branch ? `?branch=${branch}` : ''}`)

// ---------- Refills ----------
export interface RefillRequest {
  id: string
  rx_item_id: string
  drug_name: string
  drug_strength: string
  patient_id: string
  patient_name: string
  patient_mrn: string
  requested_by_account: string
  requested_at: string
  status: 'pending' | 'fulfilled' | 'denied' | 'cancelled'
  decided_by_account: string | null
  decided_at: string | null
  decision_reason: string | null
  result_rx_id: string | null
}
export const listRefills = () =>
  request<{ items: RefillRequest[]; count: number; truncated?: boolean }>('GET', '/refills')

export const createRefill = (rx_item_id: string) =>
  request<{ id: string; status: string }>('POST', '/refills', { rx_item_id })

// ---------- Audit ----------
export interface AuditEntry {
  id: number
  actor_role: string
  actor_account: string
  actor_session: string | null
  actor_ip: string | null
  action: string
  entity_type: string
  entity_id: string
  before_state: string | null
  after_state: string | null
  metadata: string | null
  occurred_at: string
}
export const getAuditLog = (filters: { actor?: string; action?: string; entity_type?: string; entity_id?: string; limit?: number; exclude_system?: boolean } = {}) => {
  const qs = new URLSearchParams()
  if (filters.actor)       qs.set('actor', filters.actor)
  if (filters.action)      qs.set('action', filters.action)
  if (filters.entity_type) qs.set('entity_type', filters.entity_type)
  if (filters.entity_id)   qs.set('entity_id', filters.entity_id)
  if (filters.limit)       qs.set('limit', String(filters.limit))
  if (filters.exclude_system) qs.set('exclude_system', '1')
  const path = qs.toString() ? `/audit?${qs.toString()}` : '/audit'
  return request<{ items: AuditEntry[]; count: number; limit: number }>('GET', path)
}

// ---------- Audit search (D-AUDIT-2) ----------
// Every call names its scope: a bound admin's is fixed server-side (pharmacyId is ignored);
// SaaS admin and support pass one pharmacy id, or 'platform'. Rows are never listed across
// pharmacies (owner, 2026-09-30: "must have tenant isolation at all times").
export interface AuditRow {
  id: number
  occurred_at: string
  actor_role: string
  actor_account: string
  action: string
  entity_type: string
  entity_id: string
  subject_patient: string | null
  subject_rx: string | null
}
export interface AuditSearchPage {
  items: AuditRow[]; total: number; page: number; page_size: number; total_pages: number
  truncated: boolean; scope: string
}
export interface AuditFieldChange { field: string; before: string | null; after: string | null }
export interface AuditEntryDetail {
  id: number; occurred_at: string; actor_role: string; actor_account: string
  actor_session: string | null; actor_ip: string | null
  action: string; entity_type: string; entity_id: string
  before_state: string | null; after_state: string | null; metadata: string | null
  changes: AuditFieldChange[]
}
export interface AuditCatalog {
  actions: { value: string; count: number }[]
  entity_types: { value: string; count: number }[]
  window_days: number; scope: string
}
export interface AuditSubjectHit { kind: 'patient' | 'rx'; id: string; label: string; dob: string | null }
export interface AuditSearchArgs {
  pharmacyId?: string
  page: number
  pageSize: number
  filter: string[]
  excludeSystem: boolean
  patientId?: string
  rxNumber?: string
  /** One record's history: both or neither. */
  recordType?: string
  recordId?: string
}
const auditQs = (pharmacyId: string | undefined, extra: Record<string, string | string[] | undefined> = {}) => {
  const qs = new URLSearchParams()
  if (pharmacyId) qs.set('pharmacy_id', pharmacyId)
  for (const [k, v] of Object.entries(extra)) {
    if (v === undefined || v === '') continue
    if (Array.isArray(v)) v.forEach(x => qs.append(k, x))
    else qs.set(k, v)
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}
export const searchAudit = (a: AuditSearchArgs) =>
  request<AuditSearchPage>('GET', `/audit/search${auditQs(a.pharmacyId, {
    page: String(a.page), page_size: String(a.pageSize), filter: a.filter,
    exclude_system: a.excludeSystem ? '1' : undefined, patient_id: a.patientId, rx_number: a.rxNumber,
    record_type: a.recordType, record_id: a.recordId,
  })}`)
export const getAuditEntry = (id: number, pharmacyId?: string) =>
  request<AuditEntryDetail>('GET', `/audit/entries/${id}${auditQs(pharmacyId)}`)
export const getAuditCatalog = (pharmacyId?: string) =>
  request<AuditCatalog>('GET', `/audit/catalog${auditQs(pharmacyId)}`)
export const lookupAuditSubjects = (q: string, pharmacyId?: string) =>
  request<{ items: AuditSubjectHit[] }>('GET', `/audit/subjects${auditQs(pharmacyId, { q })}`)

// ---------- Admin operations ----------
export interface DemoResetResponse {
  ok: boolean
  before: { rxItems: number; rxFills: number; refillRequests: number; rxStateLog: number; auditLog: number }
  after:  { rxItems: number; rxFills: number; refillRequests: number; rxStateLog: number; auditLog: number }
  message: string
}
export const demoReset = () => request<DemoResetResponse>('POST', '/ops/demo-reset')

// ====================================================================
// WI-895: public per-pharmacy branding (no auth)
// ====================================================================
export interface PharmacyBranding {
  id: string
  name: string
  display_name: string
  slug: string
  logo_url: string | null
  primary_color_hex: string | null
  sso_provider: string
}

export const getPharmacyBranding = (slug: string) =>
  request<PharmacyBranding>('GET', `/public/pharmacy-branding/${encodeURIComponent(slug)}`)

/** Resolve branding by opaque url_code (uniform 404 for unknown). Preferred over slug; see S-migration. */
export const getPharmacyBrandingByCode = (code: string) =>
  request<PharmacyBranding>('GET', `/public/pharmacy-branding-by-code/${encodeURIComponent(code)}`)

export const addDemoRequest = (body: {
  name: string; email: string; organization?: string; message?: string; company_website?: string
}) => request<{ ok: boolean }>('POST', '/public/demo-request', body)

// ---------- View Builder ----------
export interface ViewFieldCatalogItem {
  id: number; source_table: string; column_name: string; display_label: string
  data_type: string; sensitivity: string; category: string | null
  is_filterable: boolean; is_sortable: boolean; sort_order: number
  /** D-CV-7 A.2: not offered for NEW picks (duplicate of a catalog column, a column this pharmacy's
   * database lacks, or a raw identifier). Still returned so an existing view's selection stays
   * visible and can be removed. `hidden_reason` says why; `duplicate_of` names the catalog twin. */
  hidden?: boolean; hidden_reason?: string | null; duplicate_of?: number | null
  /** WS3 follow-up: set only when another OFFERED entry carries the same label (two Pioneer-path
   * mappings both called "Name"): the source that tells them apart, e.g. "Prescriber". */
  source_hint?: string | null
}
export interface PharmacyView {
  id: string; name: string; description: string | null; purpose_of_use: string
  is_default: boolean; sort_column: string | null; sort_direction: string | null
  created_at: string; updated_at: string; column_count: number
  filter_count: number; assignment_count: number
  hidden?: boolean
  /** P-VD-1: present only when the list was fetched with dupCheck — the OLDER view this
   * view's definition is identical to (columns+rules+visibility+sort; names excluded). */
  duplicate_of?: { id: string; name: string } | null
}
// RCA-CV-5 L1: a platform admin operating the builder for a selected pharmacy passes its id, or the
// server can only return the base catalog — no module-gated rows and none of that pharmacy's
// mapped / custom-field columns. Bound pharmacy actors omit it; the server ignores it for them.
export const getViewCatalog = (pharmacyId?: string) =>
  request<{ items: ViewFieldCatalogItem[]; count: number }>(
    'GET', pharmacyId ? `/views/catalog?pharmacy_id=${pharmacyId}` : '/views/catalog')
// ASP-78: selectable values for one catalog field — workbench-configured options where
// they exist, otherwise the distinct values present in the column. `source` tells the
// caller why a list is empty so the UI can fall back to free text honestly.
export const getViewFieldOptions = (fieldId: number, pharmacyId?: string) =>
  request<{ options: string[]; source: string }>(
    'GET', `/views/catalog/${fieldId}/options${pharmacyId ? `?pharmacy_id=${pharmacyId}` : ''}`)

// ---- Report builder (ADR-020) ----
export interface ReportColumnInput { fieldId: number; agg: string }
export interface ReportFilterInput { fieldId: number; op: string; value: string }
export interface ReportRunSpec {
  pharmacyId?: string; rowCap?: number; breakGlass?: boolean
  preview?: boolean; previewRole?: string
  columns: ReportColumnInput[]; filters?: ReportFilterInput[]
  computed?: string[]; groupBy?: number[]
  sourceReportId?: string
}
export interface ReportColumnMeta { label: string; source_table: string; column_name: string; agg: string; cls?: string }
export interface ReportRunResult {
  columns: string[]; columns_meta: ReportColumnMeta[]; masked_columns: string[]
  preview_role?: string | null; sql?: string
  rows: Record<string, any>[]; count: number; runtime_ms: number
}
export interface SavedReportSummary { id: string; name: string; purpose_of_use: string; created_at: string }

export const runReport = (spec: ReportRunSpec) =>
  request<ReportRunResult>('POST', '/reports/run', spec)
export const previewReportSql = (spec: ReportRunSpec) =>
  request<{ sql: string; preview: true }>('POST', '/reports/run', { ...spec, preview: true })
export const saveReport = (input: ReportRunSpec & { name: string; purposeOfUse: string }) =>
  request<{ id: string }>('POST', '/reports', input)
export const getSavedReport = (id: string) =>
  request<{ id: string; pharmacy_id: string; name: string; purpose_of_use: string; spec: any }>('GET', `/reports/${encodeURIComponent(id)}`)
export const updateReport = (id: string, input: ReportRunSpec & { name: string; purposeOfUse: string }) =>
  request<{ id: string; name: string }>('PUT', `/reports/${encodeURIComponent(id)}`, input)
export const deleteReport = (id: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/reports/${encodeURIComponent(id)}`)
export const deleteMySavedReport = (id: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/reports/mine/${encodeURIComponent(id)}`)
export const listReports = (pharmacyId?: string) =>
  request<{ reports: SavedReportSummary[] }>('GET',
    `/reports${pharmacyId ? `?pharmacy=${encodeURIComponent(pharmacyId)}` : ''}`)

// Pharmacy-facing: a tenant user's OWN saved reports (server hard-scopes to their pharmacy).
export const getMySavedReports = () =>
  request<{ reports: SavedReportSummary[] }>('GET', '/reports/mine')
export const runMySavedReport = (id: string) =>
  request<ReportRunResult>('POST', '/reports/mine/run', { id })

// ---- Report builder AI key (Owl Consultations; structure-only, no PHI) ----
export interface ReportAiStatus {
  ai_enabled: boolean; key_source: string; scope: string; phi_to_model: boolean
  expected_keyvault_secret: string; expected_app_setting: string; note?: string
}
export const getReportAiStatus = () => request<ReportAiStatus>('GET', '/reports/ai/status')
export const setReportAiKey = (apiKey: string) =>
  request<{ ok: boolean; key_source: string; key_last4: string; note: string }>('POST', '/reports/ai/key', { apiKey })
export const clearReportAiKey = () =>
  request<{ ok: boolean; key_source: string }>('DELETE', '/reports/ai/key')

export interface ReportAiSpec {
  columns: { fieldId: number; agg: string }[]
  filters: { fieldId: number; op: string; value: string }[]
  computed: string[]; groupBy: number[]
}
export interface ReportAiChatResult {
  reply: string; parsed: boolean; spec: ReportAiSpec; dropped: string[]
  usage: { input_tokens: number | null; output_tokens: number | null; cost_usd: number | null }
  model: string
}
export const reportAiChat = (messages: { role: string; content: string }[], pharmacyId?: string, model?: string) =>
  request<ReportAiChatResult>('POST', '/reports/ai/chat', { messages, pharmacyId, model })
export interface ReportAiModel { id: string; label: string; in_price: number; out_price: number; is_default: boolean }
export const getReportAiModels = () =>
  request<{ models: ReportAiModel[]; default: string }>('GET', '/reports/ai/models')

export interface ReportAiPrompt {
  prompt: string
  is_default: boolean
  default_prompt: string
  updated_by: string | null
  updated_at: string | null
}
export const getReportAiPrompt = () => request<ReportAiPrompt>('GET', '/reports/ai/prompt')
export const saveReportAiPrompt = (prompt: string) =>
  request<{ prompt: string; is_default: boolean; saved: boolean }>('PUT', '/reports/ai/prompt', { prompt })

// ── Pre-run impact check (complexity score + DB-impact heuristic) ──
export interface ReportImpact {
  showplan_available: boolean
  cost: number | null
  estimated_rows: number | null
  tables: { name: string; rows: number }[]
  total_scan_rows: number
  has_filter: boolean
  heavy: boolean
  summary: string
}
export const reportEstimate = (pharmacyId: string, sql: string) =>
  request<ReportImpact>('POST', '/reports/estimate', { pharmacyId, sql })

// ── Publish a report to a pharmacy (admin), and manage it ──
export interface PublishedReport { id: string; name: string; is_active: boolean; created_at: string; updated_at: string }
export const reportPublish = (pharmacyId: string, name: string, sql: string) =>
  request<{ id: string; name: string; is_active: boolean }>('POST', '/reports/publish', { pharmacyId, name, sql })
export const listPublishedReports = (pharmacyId: string) =>
  request<{ items: PublishedReport[] }>('GET', `/reports/published/list?pharmacyId=${encodeURIComponent(pharmacyId)}`)
export const getPublishedReport = (id: string) =>
  request<{ id: string; pharmacy_id: string; name: string; sql: string; is_active: boolean }>('GET', `/reports/published/${id}`)
export const editPublishedReport = (id: string, name: string, sql: string) =>
  request<{ id: string; name: string; saved: boolean }>('PUT', `/reports/published/${id}`, { name, sql })
export const recallPublishedReport = (id: string) =>
  request<{ id: string; is_active: boolean }>('POST', `/reports/published/${id}/recall`, {})

// ── Pharmacy-facing: reports published to my pharmacy, and running them ──
export interface MyPublishedReport { id: string; name: string; updated_at: string }
export interface PharmacyReportRunResponse {
  columns: string[]
  rows: unknown[][]
  row_count: number
  truncated: boolean
  row_cap: number
  elapsed_ms: number
  page: number
  page_size: number
  paged: boolean
  has_more: boolean
  total_count?: number | null
}
export const myPublishedReports = () =>
  request<{ items: MyPublishedReport[] }>('GET', '/reports/my-published/list')

// The report view is fully SERVER-SIDE: filter/search/sort/group/date are pushed into the query and
// the result is paged, so the browser only ever holds one page.
export interface ReportRunQuery {
  page?: number
  pageSize?: number
  allColumns?: string[]
  search?: string
  // Each filter is EITHER a free-text `val` (contains) OR a set of exact `vals` (checkbox picker -> IN).
  filters?: { col: string; val?: string; vals?: string[] }[]
  dateCol?: string | null
  from?: string
  to?: string
  sortCol?: string | null
  sortDesc?: boolean
  groupBy?: string | null
  sumCols?: string[]
  wantCount?: boolean
}
export const runMyPublishedReport = (id: string, q: ReportRunQuery = {}) =>
  request<PharmacyReportRunResponse>('POST', '/reports/my-published/run', { id, ...q })

// Distinct values of one column across the WHOLE report, so the UI can offer a checkbox value picker.
// `too_many` = more than the server cap; the UI then falls back to a text filter for that column.
export interface ReportDistinctResponse { values: string[]; too_many: boolean }
export const reportDistinctValues = (id: string, column: string, search?: string) =>
  request<ReportDistinctResponse>('POST', '/reports/my-published/distinct', { id, column, search })

// COMPLETE CSV export (server-side, NOT capped at the on-screen preview). Returns { blob, filename }
// so a published revenue report can tie to the books. The filename ends _PARTIAL if the export hit
// the runaway backstop.
async function postForFile(path: string, payload: unknown): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}) },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    let b: any = {}
    try { b = await res.json() } catch { /* non-JSON error body */ }
    const e: any = new Error(b?.message || 'Export failed'); e.body = b; throw e
  }
  const cd = res.headers.get('Content-Disposition') || ''
  const m = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(cd)
  const filename = m ? decodeURIComponent(m[1]) : 'report.csv'
  return { blob: await res.blob(), filename }
}
export const exportBuilderReportCsv = (pharmacyId: string, sql: string) =>
  postForFile('/reports/export-csv', { pharmacyId, sql })
export const exportMyPublishedReportCsv = (id: string) =>
  postForFile('/reports/my-published/export', { id })
export interface ReportAiUsage {
  today: { calls: number; cost_usd: number }
  month: { calls: number; cost_usd: number }
  total: { calls: number; cost_usd: number; input_tokens: number; output_tokens: number }
  by_model?: { model: string; calls: number; cost_usd: number }[]
}
export const getReportAiUsage = () => request<ReportAiUsage>('GET', '/reports/ai/usage')
export const listViews = (pharmacyId?: string, opts?: { dupCheck?: boolean }) => {
  const qs = new URLSearchParams()
  if (pharmacyId) qs.set('pharmacy_id', pharmacyId)
  if (opts?.dupCheck) qs.set('dup_check', '1')   // P-VD-1: settings list only — computes identical-view badges
  const s = qs.toString()
  return request<{ items: PharmacyView[]; count: number }>('GET', `/views${s ? `?${s}` : ''}`)
}
export const createView = (body: {
  name: string; description?: string; purpose_of_use: string
  sort_column?: string; sort_direction?: string
  columns: { field_id: number; custom_label?: string }[]
  filters?: { field_id: number; operator: string; value_json?: string; group_no?: number; conjunction?: string }[]
}, pharmacyId?: string) =>
  // CV-5: /views list frames are SWR-cached — drop them on every mutation or the
  // settings page's post-save reload is served the pre-write list.
  request<{ id: string }>('POST', pharmacyId ? `/views?pharmacy_id=${pharmacyId}` : '/views', body)
    .then(r => { swrInvalidate('/views'); return r })
/** D-VIEWS-SRV-1 WS3: one funnel selection, keyed by the PHYSICAL column_name (columns_meta / caps),
 *  never the display label. `(blank)` means "no value". Dates carry a day as YYYY-MM-DD. */
export interface ViewAdHocFilter { column: string; op: 'in' | 'gte' | 'lte' | 'contains'; values: string[] }
/** Wire form: `filter=<column>:<op>:<value>`, ONE value per parameter — a value may contain a comma
 *  ("Gardena Hospital, LP"), so the Rx list's comma-joined form is not reused here. */
export const encodeViewFilters = (filters: ViewAdHocFilter[]): string[] =>
  filters.flatMap(f => f.values.map(v => `${f.column}:${f.op}:${v}`))
/** WS3: the values one column holds across the WHOLE view (its rules + the caller's other funnels). */
export const getViewFacet = (viewId: string, opts: { column: string; q?: string; filters?: ViewAdHocFilter[] }) => {
  const p = new URLSearchParams({ column: opts.column })
  if (opts.q) p.set('q', opts.q)
  for (const f of encodeViewFilters(opts.filters ?? [])) p.append('filter', f)
  return request<RxFacetResponse>('GET', `/views/${viewId}/facets?${p.toString()}`)
}

export const executeView = (id: string, opts?: { limit?: number; offset?: number; countOnly?: boolean; sort?: string; dir?: 'asc' | 'desc'; filters?: ViewAdHocFilter[] }) => {
  const qs = new URLSearchParams()
  if (opts?.limit) qs.set('limit', String(opts.limit))
  if (opts?.offset) qs.set('offset', String(opts.offset))
  // WS3: funnel selections travel with the page request and its total.
  for (const f of encodeViewFilters(opts?.filters ?? [])) qs.append('filter', f)
  // D-VIEWS-SRV-1 WS1: a header click overrides the view's saved sort for this request. The
  // server validates the name against THIS view's own columns and ignores anything else, so an
  // older API that does not read these params simply serves the saved sort — the same answer
  // the grid showed before, never a wrong one.
  if (opts?.sort) { qs.set('sort', opts.sort); qs.set('dir', opts.dir === 'desc' ? 'DESC' : 'ASC') }
  // BADGE-10S: total without rows/redaction/execution-log — pair with limit:1 so an
  // older API (param ignored) still answers with a 1-row page carrying the same total.
  if (opts?.countOnly) qs.set('count_only', '1')
  const path = qs.toString() ? `/views/${id}/data?${qs.toString()}` : `/views/${id}/data`
  return request<{ view_id: string; view_name: string; columns: string[]; columns_meta?: { label: string; source_table: string; column_name: string }[]; rows: Record<string, any>[]; count: number; total?: number; runtime_ms: number }>('GET', path)
}
// D-BADGE-1: every badge total the caller may see, in ONE call — flat cost regardless of how
// many views the pharmacy configures. A view whose count failed server-side arrives as null
// (its badge renders blank; one broken view never blanks every badge).
export interface ViewCountsResponse {
  counts: Record<string, { total: number; cached: boolean } | null>
  ttl_seconds: number
  generated_at: string
}
export const getViewCounts = () => request<ViewCountsResponse>('GET', '/views/counts')
export const deleteView = (id: string, pharmacyId?: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', pharmacyId ? `/views/${id}?pharmacy_id=${pharmacyId}` : `/views/${id}`)
    .then(r => { swrInvalidate('/views'); return r })
export const setViewHidden = (id: string, hidden: boolean, pharmacyId?: string) =>
  request<{ id: string; hidden: boolean }>('POST', pharmacyId ? `/views/${id}/hidden?pharmacy_id=${pharmacyId}` : `/views/${id}/hidden`, { hidden })
    .then(r => { swrInvalidate('/views'); return r })

// View detail (for edit)
export interface ViewDetail {
  view: { id: string; name: string; description: string | null; purpose_of_use: string; sort_column: string | null; sort_direction: string | null }
  columns: { field_id: number; position: number; custom_label: string | null; display_label: string }[]
  filters: { field_id: number; operator: string; value_json: string | null; display_label: string; group_no?: number; conjunction?: string }[]
  assignments: { account_id: string; email: string | null; display_name: string | null }[]
}
export const getView = (id: string, pharmacyId?: string) =>
  request<ViewDetail>('GET', pharmacyId ? `/views/${id}?pharmacy_id=${pharmacyId}` : `/views/${id}`)
export const updateView = (id: string, body: {
  name?: string; description?: string; purpose_of_use?: string
  columns?: { field_id: number; custom_label?: string }[]
  filters?: { field_id: number; operator: string; value_json?: string; group_no?: number; conjunction?: string }[]
}, pharmacyId?: string) =>
  request<{ id: string; updated: boolean }>('PUT', pharmacyId ? `/views/${id}?pharmacy_id=${pharmacyId}` : `/views/${id}`, body)
    .then(r => { swrInvalidate('/views'); return r })
export const updateViewAssignments = (id: string, accountIds: string[], pharmacyId?: string) =>
  request<{ id: string; assigned: number }>('PUT', pharmacyId ? `/views/${id}/assignments?pharmacy_id=${pharmacyId}` : `/views/${id}/assignments`, { account_ids: accountIds })
    .then(r => { swrInvalidate('/views'); return r })

// Users list (for RBAC)
export interface UserItem {
  id: string; email: string; display_name: string; status: string; role_ids: string | null
}
export const listUsers = () =>
  request<{ items: UserItem[] }>('GET', '/users')

// Tab access control
export const getMyTabs = () => request<{ visible_tabs: string[] }>('GET', '/tabs/my')
export const getUserTabAccess = (accountId: string) =>
  request<{ configs: { tab_key: string; rbac_enabled: boolean }[]; assigned_tabs: string[]; custom_views: { id: string; name: string }[] }>('GET', `/tabs/user/${accountId}`)
export const updateUserTabAccess = (accountId: string, tabKeys: string[]) =>
  request<{ account_id: string; tabs: number }>('PUT', `/tabs/user/${accountId}`, { tab_keys: tabKeys })

// System tabs (built-in) — per-tab access, for the SaaS-admin view manager (D-VIEWADMIN-1)
export interface SystemTabAccess { tab_key: string; rbac_enabled: boolean; hidden: boolean; assigned: string[] }
export const getSystemTabsAccess = (pharmacyId?: string) =>
  request<{ tabs: SystemTabAccess[] }>('GET', pharmacyId ? `/tabs/system?pharmacy_id=${pharmacyId}` : '/tabs/system')
export const setSystemTabAccess = (tabKey: string, accountIds: string[], pharmacyId?: string) =>
  request<{ tab_key: string; assigned: number }>('PUT', pharmacyId ? `/tabs/system/${tabKey}/access?pharmacy_id=${pharmacyId}` : `/tabs/system/${tabKey}/access`, { account_ids: accountIds })
    .then(r => { swrInvalidate('/tabs'); return r })
export const setSystemTabHidden = (tabKey: string, hidden: boolean, pharmacyId?: string) =>
  request<{ tab_key: string; hidden: boolean }>('POST', pharmacyId ? `/tabs/system/${tabKey}/hidden?pharmacy_id=${pharmacyId}` : `/tabs/system/${tabKey}/hidden`, { hidden })
    .then(r => { swrInvalidate('/tabs'); return r })

// Field mappings
export interface FieldMapping { id: number; owlrx_table: string; owlrx_column: string; owlrx_label: string; pioneer_path: string | null; is_default: boolean; is_active: boolean; card_key?: string | null; data_type?: string }

// Resolve custom/mapped view-builder field values for many records at once (list tables / queues).
// Returns a map: record_id -> { "<card_key>.<owlrx_column>": value }.
// D-PERF-7 Z2: chunked transparently. Callers were posting the ENTIRE loaded row set
// (StationQueue: up to 5,000 ids) in one request — P95 30s with 177 timeouts over 3 days —
// and the server silently Take(1000)'d, so rows beyond the first 1,000 NEVER resolved values
// (a correctness bug, not just latency). Chunks of 200, concurrency 2, results merged;
// response shape to callers is byte-identical, now complete for every id.
const CFV_CHUNK = 200
const CFV_CONCURRENCY = 2
export const batchCustomFieldValues = async (surface: string, recordIds: string[], pharmacyId?: string) => {
  const ids = Array.from(new Set(recordIds)).filter(Boolean)
  const post = (chunk: string[]) =>
    request<{ values: Record<string, Record<string, string | null>> }>(
      'POST', '/custom-field-values/batch',
      { surface, record_ids: chunk, ...(pharmacyId ? { pharmacy_id: pharmacyId } : {}) })
  if (ids.length <= CFV_CHUNK) return post(ids)
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += CFV_CHUNK) chunks.push(ids.slice(i, i + CFV_CHUNK))
  const values: Record<string, Record<string, string | null>> = {}
  let next = 0
  const worker = async () => {
    while (next < chunks.length) {
      const mine = chunks[next++]
      const r = await post(mine)
      Object.assign(values, r.values ?? {})
    }
  }
  await Promise.all(Array.from({ length: Math.min(CFV_CONCURRENCY, chunks.length) }, worker))
  return { values }
}
export interface PioneerPath { section: string; path: string; example: string }
export const listFieldMappings = () => request<{ items: FieldMapping[]; count: number }>('GET', '/field-mappings')
export const seedFieldMappings = () => request<{ seeded: number }>('POST', '/field-mappings/seed')
export const createFieldMapping = (body: { owlrx_table?: string; owlrx_column?: string; owlrx_label: string; pioneer_path?: string; card_key?: string }, pharmacyId?: string) =>
  request<{ id: number }>('POST', `/field-mappings${pharmacyId ? `?pharmacy_id=${encodeURIComponent(pharmacyId)}` : ''}`, body)
// ASP-38: unbound SaaS-admin actors MUST pass pharmacy_id or the server 422s (silently
// swallowed by callers) — the "Save does not reflect" report.
export const updateFieldMapping = (id: number, body: { pioneer_path?: string | null; is_active?: boolean }, pharmacyId?: string) =>
  request<{ id: number; updated: boolean }>('PUT', `/field-mappings/${id}${pharmacyId ? `?pharmacy_id=${encodeURIComponent(pharmacyId)}` : ''}`, body)
export const deleteFieldMapping = (id: number, pharmacyId?: string) => request<{ id: number; deleted: boolean }>('DELETE', `/field-mappings/${id}${pharmacyId ? `?pharmacy_id=${encodeURIComponent(pharmacyId)}` : ''}`)
export const listPioneerPaths = () => request<{ paths: PioneerPath[] }>('GET', '/field-mappings/pioneer-paths')
// Canonical built-in column -> PioneerRx path catalog (DefaultMappings). Read-only; lets the
// view builder show each non-custom field's real PioneerRx source instead of "standard".
export interface FieldDefault { owlrx_table: string; owlrx_column: string; owlrx_label: string; pioneer_path: string | null }
export const listFieldMappingDefaults = () => request<{ defaults: FieldDefault[] }>('GET', '/field-mappings/defaults')
// List mappings for a specific pharmacy (SaaS admin builder). Falls back to the
// actor's pharmacy when no id is given.
export const listFieldMappingsFor = (pharmacyId?: string) =>
  request<{ items: FieldMapping[]; count: number }>('GET', `/field-mappings${pharmacyId ? `?pharmacy_id=${pharmacyId}` : ''}`)

// ---- Prescription view layout (builder) ----
export interface RxViewField {
  /** ASP-43: dropdown options for custom fields (config_json field meta). */
  options?: string[]
  /** ASP-43: display-level default when no value set. */
  default_value?: string
  id?: number; owlrx_table: string; owlrx_column: string; owlrx_label: string
  pioneer_path: string | null; card_position: number; visible: boolean; custom: boolean
  data_type?: string
}
export type ViewRegion = 'header' | 'body' | 'footer'
export interface RxCardConfig {
  informational_only?: boolean; region?: ViewRegion; header_band?: boolean
  provider_hidden?: string[]; subtext_hidden?: string[]
  // D-HOME-2 part B — home surface only:
  metric?: {
    source: string; agg: string; measure?: string
    filters?: { field: string; op: string; value?: string | number; values?: (string | number)[] }[]
    window?: { field: string; last_days: number }
    group_by?: string; display?: 'number' | 'tiles'
  }
  pulse?: { hidden?: string[]; labels?: Record<string, string>; order?: string[] }
  // ASP-53 — home surface: custom-view tile config (saved view id + display name).
  view?: { view_id?: string; view_name?: string }
  // D-HOME-3 — per-pharmacy parameters for the built-in action cards (validated
  // server-side in HomeCardParams; absent = historical defaults).
  params?: { hold_hours?: number; aging_days?: number; rejected_hours?: number; pa_states?: string[]; volume_days?: number }
}
export interface RxViewCard {
  card_key: string; title: string; position: number; visible: boolean
  kind: string; required_permission: string | null
  allowed_roles?: string[]; denied_roles?: string[]; config?: RxCardConfig | null
  fields: RxViewField[]
}
export interface RxViewLayout { source: 'default' | 'custom'; pharmacy_id: string; surface?: string; cards: RxViewCard[] }

// D-VIEW-1: the identity band at the top of a detail view is the DEFAULT header.
// A pharmacy may override which band fields show by adding a header-region card
// flagged `header_band` whose visible fields form the allowlist. Returns null when
// no override exists → callers render the band exactly as before (no drift).
export function headerBandAllow(cards: RxViewCard[] | undefined | null): string[] | null {
  const band = (cards ?? []).find(c => (c.config?.region ?? 'body') === 'header' && c.config?.header_band === true)
  if (!band) return null
  return (band.fields ?? []).filter(f => f.visible).map(f => f.owlrx_column)
}
// Header-region cards that are NOT the identity-band override (rendered as normal cards).
export function headerExtraCards(cards: RxViewCard[] | undefined | null): RxViewCard[] {
  return (cards ?? []).filter(c => (c.config?.region ?? 'body') === 'header' && c.config?.header_band !== true)
}
// True iff the pharmacy has a CUSTOM header for this surface that omits `col`.
// Default header (no header-region cards) → false (hide nothing). This lets list
// tables / queues honor what a pharmacy removed from a surface's header, so a
// field hidden in the header is hidden everywhere it appears for that surface.
export function headerFieldHidden(layout: RxViewLayout | null | undefined, col: string): boolean {
  const hdr = (layout?.cards ?? []).filter(c => (c.config?.region ?? 'body') === 'header')
  if (hdr.length === 0) return false
  for (const c of hdr) for (const f of (c.fields ?? [])) {
    if (f.visible && f.owlrx_column === col) return false
  }
  return true
}
const layoutQ = (pharmacyId?: string, surface?: string) => {
  const p: string[] = []
  if (pharmacyId) p.push(`pharmacy_id=${pharmacyId}`)
  if (surface && surface !== 'rx') p.push(`surface=${surface}`)
  return p.length ? `?${p.join('&')}` : ''
}
export const getRxViewLayout = (pharmacyId?: string, surface?: string) =>
  request<RxViewLayout>('GET', `/rx-view-layout${layoutQ(pharmacyId, surface)}`)

// ---- RX-PERF-7: session TTL cache for static-ish CONFIG fetches ----
// Every queue-tab mount used to refetch the same unchanging config from scratch: workbench
// field-defs, THREE rx-view-layout surfaces, field-mappings, plus the layout's views + my-tabs
// — ~8 requests of pure config per tab click (all visible in the prod waterfall). Config
// changes are admin-rare; a short TTL keeps edits converging within 2 minutes (or a hard
// reload) while tab switching costs zero config round-trips. The cache stores the PROMISE so
// concurrent mounts share one in-flight request; failures evict so retries stay live.
const cfgCache = new Map<string, { at: number; p: Promise<unknown> }>()
export const CFG_CACHE_TTL_MS = 120_000
export function cachedCfg<T>(key: string, fetcher: () => Promise<T>, ttlMs: number = CFG_CACHE_TTL_MS): Promise<T> {
  const hit = cfgCache.get(key)
  if (hit && Date.now() - hit.at < ttlMs) return hit.p as Promise<T>
  const p = fetcher()
  p.catch(() => { if (cfgCache.get(key)?.p === p) cfgCache.delete(key) })
  cfgCache.set(key, { at: Date.now(), p })
  return p
}

// D-HOME-2 part B: resolve the pharmacy's visible kind='home_metric' cards to values
// (aggregate numbers / grouped tiles only — never row-level data). Called by OwnerHome
// only when the saved home layout actually contains a metric card.
export interface HomeMetricGroup { group: string; value: number | null }
export interface HomeMetricResult {
  card_key: string; title: string; agg?: string
  value?: number | null; group_by?: string; groups?: HomeMetricGroup[]; error?: string
}
export const getHomeMetrics = () =>
  request<{ cards: HomeMetricResult[] }>('GET', '/dashboards/home-metrics')
export const putRxViewLayout = (cards: RxViewCard[], pharmacyId?: string, surface?: string) =>
  request<{ saved: boolean; cards: number }>('PUT', `/rx-view-layout${layoutQ(pharmacyId, surface)}`, { cards })
export const initializeRxViewLayout = (pharmacyId?: string, surface?: string) =>
  request<{ initialized: number }>('POST', `/rx-view-layout/initialize${layoutQ(pharmacyId, surface)}`)

// ---------- Custom field values (typed values staff enter per record) ----------
export interface CustomFieldValues { can_write: boolean; can_modify: boolean; values: Record<string, string | null> }
export const getCustomFieldValues = (surface: string, recordId: string) =>
  request<CustomFieldValues>('GET', `/custom-field-values?surface=${encodeURIComponent(surface)}&record_id=${encodeURIComponent(recordId)}`)
// D-VIEWMAP-1 Phase 2: resolved PioneerRx values for patient/prescriber pioneer_path fields
// (read-time resolver). Keyed card_key.owlrx_column, same shape as CustomFieldValues.values.
export interface EntityMappedValues { surface: string; record_id: string; values: Record<string, string | null> }
export const getEntityMappedValues = (surface: string, recordId: string) =>
  request<EntityMappedValues>('GET', `/entity-mapped-values?surface=${encodeURIComponent(surface)}&record_id=${encodeURIComponent(recordId)}`)
export const putCustomFieldValue = (input: { surface: string; record_id: string; card_key: string; field_column: string; value: string | null; pharmacy_id?: string }) =>
  request<{ saved: boolean; modified: boolean }>('PUT', '/custom-field-values', input)

// ---------- Patient documents (Document Tracker on the patient profile) ----------
export interface PatientDocument {
  id: string
  pharmacy_id: string
  patient_id: string
  rx_id?: string | null
  prescriber_id?: string | null
  parent_document_id?: string | null
  document_type: string
  lifecycle_status: string
  outcome: string
  signature_status: string
  countersignature_required: boolean
  title?: string | null
  notes?: string | null
  file_name?: string | null
  due_date?: string | null
  received_at?: string | null
  last_status_change_at?: string | null
  cancelled_at?: string | null
  cancel_reason?: string | null
  created_at: string
  updated_at: string
  audience?: string[]
  expiration_date?: string | null
  has_file?: boolean
  size_bytes?: number | null
  content_type?: string | null
}
export interface PatientDocumentList { can_write: boolean; documents: PatientDocument[] }
export interface CreatePatientDocumentInput {
  document_type: string
  lifecycle_status?: string
  outcome?: string
  signature_status?: string
  title?: string
  notes?: string
  rx_id?: string
  prescriber_id?: string
  parent_document_id?: string
  received_at?: string
  due_date?: string
  expiration_date?: string
  audience?: string[]
  pharmacy_id?: string
}
export const getPatientDocuments = (patientId: string) =>
  request<PatientDocumentList>('GET', `/patients/${patientId}/documents`)
export const createPatientDocument = (patientId: string, input: CreatePatientDocumentInput) =>
  request<{ id: string; created: boolean }>('POST', `/patients/${patientId}/documents`, input)
export const uploadPatientDocument = async (patientId: string, form: FormData) => {
  const res = await apiFetch(`/patients/${patientId}/documents/upload`, { method: 'POST', credentials: 'include', body: form })
  if (!res.ok) throw await parseApiError(res)
  return res.json() as Promise<{ id: string; created: boolean; file_name: string }>
}
export const downloadPatientDocument = (patientId: string, docId: string) =>
  fetchDocDownload(`/patients/${patientId}/documents/${docId}/download`)
export const deletePatientDocument = (patientId: string, docId: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/patients/${patientId}/documents/${docId}`)

export interface MyPharmacyDocument {
  id: string; patient_id: string; patient_name: string; prescriber_name: string
  title: string; document_type: string
  size_bytes: number; received_at: string | null; expiration_date: string | null
  is_expired: boolean; has_file: boolean
  uploaded_by?: string | null
}
export interface MyPharmacyDocumentList { can_delete: boolean; documents: MyPharmacyDocument[] }
export const getMyPharmacyDocuments = () =>
  request<MyPharmacyDocumentList>('GET', '/me/documents')
export const deleteMyPharmacyDocument = (docId: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/me/documents/${docId}`)

// RBAC reference reads for the builder's View-as + per-card gating (token-authed).
export const rbacListRoles = () => request<{ items: { id: string; name: string; is_system: boolean }[] }>('GET', '/roles')
export const rbacListPermissions = () => request<{ items: { key: string }[] }>('GET', '/permissions')
export const rbacFieldCatalog = () => request<{ fields: { owlrx_table: string; owlrx_column: string; default_access: string }[] }>('GET', '/field-catalog')
export const rbacRoleFieldRules = (roleId: string) => request<{ rules: { owlrx_table: string; owlrx_column: string; access: string }[] }>('GET', `/roles/${roleId}/field-rules`)
export const rbacRoleDetail = (roleId: string) => request<{ permissions: string[] }>('GET', `/roles/${roleId}`)
// Deep re-extract: sweeps EVERY active PioneerRx mapping (detail cards + list columns, all rx
// screens) over the pharmacy's stored envelopes. pharmacyId for platform admins on the admin domain.
// ASP-38 resumable apply: the SWA edge kills ~30s requests, so the server processes mapping
// SLICES; this loops until next_offset is null, aggregating counts. One retry per slice —
// the first call after a serverless-DB resume can time out at the edge; the retry lands warm.
export const applyFieldMappingsAll = async (
  pharmacyId?: string,
  opts?: { auto?: boolean; onProgress?: (done: number, total: number) => void },
): Promise<{ rx_count: number; values_written: number; errors: number; first_error?: string | null; mappings_count: number; skipped?: boolean; envelope_count?: number }> => {
  let offset = 0
  let agg = { rx_count: 0, values_written: 0, errors: 0, first_error: null as string | null, mappings_count: 0, skipped: undefined as boolean | undefined, envelope_count: undefined as number | undefined }
  // The ceiling used to be a flat 50 slices (7,500 Rx). ASP has 68,423, so a manual re-extract
  // stopped at 11% and reported success. It now follows the server's cursor to the end; the cap
  // only bounds a runaway (4,000 slices = 600,000 Rx).
  for (let i = 0; i < 4000; i++) {
    let r: any
    try { r = await applyFieldMappings(pharmacyId, { ...opts, offset }) }
    catch { r = await applyFieldMappings(pharmacyId, { ...opts, offset }) }   // one retry (cold resume)
    agg.rx_count = r.rx_count ?? agg.rx_count
    agg.values_written += r.values_written ?? 0
    agg.errors += r.errors ?? 0
    agg.first_error = agg.first_error ?? r.first_error ?? null
    agg.mappings_count = r.total_mappings ?? r.mappings_count ?? agg.mappings_count
    if (r.skipped) { agg.skipped = true; agg.envelope_count = r.envelope_count; break }
    const done = r.next_offset ?? agg.mappings_count
    opts?.onProgress?.(Math.min(done, agg.mappings_count), agg.mappings_count)
    if (r.next_offset == null) break
    offset = r.next_offset
  }
  return agg
}

export const applyFieldMappings = (pharmacyId?: string, opts?: { auto?: boolean; offset?: number }) =>
  request<{ rx_count: number; envelopes_scanned: number; mappings_count: number; values_written: number; errors: number; skipped?: boolean; envelope_count?: number; first_error?: string | null; total_mappings?: number; next_offset?: number | null }>(
    'POST', (() => { const qs = [pharmacyId ? `pharmacy_id=${encodeURIComponent(pharmacyId)}` : '', opts?.auto ? 'auto=1' : '', opts?.offset != null ? `offset=${opts.offset}` : ''].filter(Boolean); return `/field-mappings/apply${qs.length ? '?' + qs.join('&') : ''}` })())

// ---------- D-MAP-1: mapped-column backfill, as a server job ----------
export interface MappingBackfillRun {
  id: number
  trigger: 'manual' | 'save' | 'auto'
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  rx_total: number | null
  rx_scanned: number
  rx_with_envelope: number
  values_written: number
  errors: number
  first_error: string | null
  mappings_count: number | null
  requested_at: string
  started_at: string | null
  finished_at: string | null
  /** Why it is not moving right now; null while it works. */
  wait_reason?: 'db_asleep' | 'db_busy' | 'db_slow' | 'db_state_unknown' | 'outside_hours' | null
  wait_detail?: string | null
  waiting_since?: string | null
}
export interface MappingBackfillSettings {
  auto_enabled: boolean
  time_zone: string
  start_hour: number
  end_hour: number
  /** Seven 0/1 characters, Sunday first. */
  work_days: string
  /** Back off when the database's own CPU is above this percent. */
  cpu_ceiling_pct: number
  /** Share of the time the job may spend working. */
  duty_pct: number
  in_working_hours?: boolean
}
export interface MappingBackfillStatus {
  settings: MappingBackfillSettings
  active: MappingBackfillRun | null
  last_completed: MappingBackfillRun | null
  runs: MappingBackfillRun[]
}
const backfillQs = (pharmacyId?: string, extra: string[] = []) => {
  const qs = [pharmacyId ? `pharmacy_id=${encodeURIComponent(pharmacyId)}` : '', ...extra].filter(Boolean)
  return qs.length ? `?${qs.join('&')}` : ''
}
export const getMappingBackfill = (pharmacyId?: string) =>
  request<MappingBackfillStatus>('GET', `/field-mappings/backfill${backfillQs(pharmacyId)}`)
/** Queues a run (or joins the one going) and works on it for ~20 s before answering. */
export const startMappingBackfill = (pharmacyId?: string, trigger: 'manual' | 'save' = 'manual') =>
  request<MappingBackfillStatus>('POST', `/field-mappings/backfill${backfillQs(pharmacyId, trigger === 'save' ? ['trigger=save'] : [])}`)
export const cancelMappingBackfill = (pharmacyId?: string) =>
  request<MappingBackfillStatus>('POST', `/field-mappings/backfill/cancel${backfillQs(pharmacyId)}`)
export const saveMappingBackfillSettings = (pharmacyId: string | undefined, s: Partial<MappingBackfillSettings>) =>
  request<MappingBackfillStatus>('PUT', `/field-mappings/backfill/settings${backfillQs(pharmacyId)}`, s)

// ---------- Shipping (Sprint 18) ----------
export interface ShippingCarrier {
  id: string
  carrier: string
  display_name: string
  base_url: string | null
  api_website: string | null
  account_number: string | null
  default_service_level: string | null
  has_credentials: boolean
  /** D-SHIP-23: names of the stored secrets (never their values). */
  credential_keys?: string[]
  credentials_unencrypted: boolean
  default_signature: boolean
  default_cold_chain: boolean
  require_quote_confirm?: boolean
  /** D-SHIP-COD: send the outstanding copay to the courier as COD. Default false. */
  send_cod?: boolean
  is_test_mode: boolean
  is_active: boolean
  last_verified_at: string | null
  last_verify_error: string | null
}
export interface ShipmentSummary {
  id: string
  rx_item_id: string | null
  rx_fill_item_id?: string | null
  is_combined?: boolean
  carrier: string
  carrier_shipment_id?: string | null
  tracking_number: string | null
  status: string
  require_signature: boolean
  cold_chain: boolean
  delivered_at: string | null
  picked_up_at?: string | null
  shipped_at: string | null
  last_tracked_at: string | null
  patient_name: string | null
  patient_id?: string | null
  rx_number: string | null
  drug_name: string | null
  is_manual?: boolean
  imported_from_courier_at?: string | null
  carrier_amount?: number | null
  created_by?: string | null
  /** Imported orders: courier piece Reference (portal Ref#) — the real Rx. */
  carrier_reference?: string | null
  /** Imported orders: who placed the order with the courier (not the OwlRx importer). */
  courier_entered_by?: string | null
  /** Pioneer claims copay (PatientPayAmountPaid) — COLLECT $X when > 0. */
  copay_amount?: number | null
  /** Carrier service type (NEXTDAY, STAT, ...) for created + imported orders. */
  service_level?: string | null
  // ── COD round-trip (D-SHIP-COD). Three numbers, all distinct on purpose:
  //    copay_amount        what the patient OWES
  //    cod_amount_sent     what the carrier was ASKED to collect (e-Courier CodAmount)
  //    cod_amount_received what the driver REPORTS taking (e-Courier AmountReceived)
  /** Dollars sent to the carrier as COD at create. NULL = no COD was sent. */
  cod_amount_sent?: number | null
  /** Dollars the carrier reports collecting. */
  cod_amount_received?: number | null
  /** RAW e-Courier CodStatus (e.g. "2"). Undecoded on purpose — the enum is undocumented. */
  cod_status?: string | null
  /** Server-decoded "the driver took the money". Never re-derive this from cod_status here. */
  cod_collected?: boolean
  cod_collected_at?: string | null
  /** D-SOC-6: the site-of-care facility this shipment was sent to, when one was chosen. */
  site_of_care_facility_id?: string | null
  site_of_care_facility?: string | null
}
export interface ShipmentDetail {
  id: string
  carrier: string
  carrier_shipment_id: string | null
  tracking_number: string | null
  status: string
  service_level: string | null
  require_signature: boolean
  cold_chain: boolean
  saturday_delivery: boolean
  adult_signature: boolean
  special_instructions: string | null
  carrier_status_text: string | null
  delivered_at: string | null
  pod_signature_name: string | null
  pod_image_url: string | null
  shipped_at: string | null
  last_tracked_at: string | null
}
export interface ShipmentEvent {
  status: string
  carrier_status_text: string | null
  description: string | null
  location: string | null
  occurred_at: string
}
export interface ShipInput {
  carrier_account_id: string
  service_level?: string
  /** D-SHIP-23: pickup branch for this order; the account's branch when absent. */
  pickup_location_id?: string
  require_signature?: boolean
  adult_signature?: boolean
  cold_chain?: boolean
  saturday_delivery?: boolean
  special_instructions?: string
}

export const listShippingCarriers = () =>
  request<{ items: ShippingCarrier[]; count: number; supported_carriers: string[] }>('GET', '/shipping/carriers')
/** Partial update — only provided fields change (server COALESCEs). */
export const updateShippingCarrier = (id: string, body: {
  display_name?: string; account_number?: string; base_url?: string; api_website?: string
  default_service_level?: string; default_signature?: boolean; default_cold_chain?: boolean
  is_test_mode?: boolean; is_active?: boolean; require_quote_confirm?: boolean
  /** D-SHIP-COD: per-account gate for sending CodAmount to the carrier. */
  send_cod?: boolean
}) =>
  request<{ id: string; updated: boolean }>('PATCH', `/shipping/carriers/${id}`, body)

export const testShippingCarrier = (id: string) =>
  request<{ ok: boolean; detail: string; token_hint: string | null }>('POST', `/shipping/carriers/${id}/test`)
export const quoteShipment = (body: ShipInput & { ship_to?: object; reference?: string }) =>
  request<{ ok: boolean; amount: number | null; currency: string; service_level: string | null; detail: string }>('POST', '/shipping/quote', body)
export const shipRx = (rxId: string, body: ShipInput) =>
  request<{ id: string; shipped: boolean; carrier: string; carrier_shipment_id: string | null; tracking_number: string | null; label_url: string | null; status: string }>('POST', `/rx/${rxId}/ship`, body)
export const getRxShipment = (rxId: string, refresh = false) =>
  request<{ shipment: ShipmentDetail | null; events?: ShipmentEvent[] }>('GET', `/rx/${rxId}/shipment${refresh ? '?refresh=1' : ''}`)

export interface RxDelivery {
  id: string
  carrier: string
  service_level: string | null
  tracking_number: string | null
  status: string
  carrier_status_text: string | null
  shipped_at: string | null
  delivered_at: string | null
  last_tracked_at: string | null
  pod_signature_name: string | null
}
export const getRxDelivery = (rxId: string) =>
  request<{ shipment: RxDelivery | null; events: ShipmentEvent[] }>('GET', `/rx/${rxId}/delivery`)
// ── D-RXFILTER-4: the shipments worklist, always paged ──────────────────────────────────────
//
// `listShipments` (unpaginated, server-capped at the newest 1,000) is GONE. It existed so the
// browser could filter and sort a set locally, which is exactly why the cap had to exist at all —
// you cannot page a set you are about to filter in the browser. Every filter is now a query
// parameter, so "filtered" and "paged" have stopped being mutually exclusive and there is no
// shape of this call that silently withholds rows.

/** The bespoke worklist facets — the ones that are genuinely not columns of the table. */
export interface ShipmentListFacets {
  q?: string
  from?: string
  to?: string
  type?: 'rx' | 'manual' | 'imported'
  mapping?: 'mapped' | 'unmapped'
  combined?: boolean
  /** Server-side per-patient filter (direct link, primary Rx, or co-shipped member). */
  patientId?: string
  /**
   * D-SOC-6: site-of-care filter. A facility id, `any` (went to some facility) or `none`.
   * Filters on the recorded facility link (mig 428), not on the address text.
   */
  siteOfCare?: string
}

const shipmentParams = (f: ShipmentListFacets, filters?: RxFilter[]) => {
  const p = new URLSearchParams()
  if (f.q) p.set('q', f.q)
  if (f.from) p.set('from', f.from)
  if (f.to) p.set('to', f.to)
  if (f.type) p.set('type', f.type)
  if (f.mapping) p.set('mapping', f.mapping)
  if (f.combined) p.set('combined', '1')
  if (f.patientId) p.set('patient_id', f.patientId)
  if (f.siteOfCare) p.set('site_of_care', f.siteOfCare)   // D-SOC-6
  for (const x of encodeRxFilters(filters ?? [])) p.append('filter', x)
  return p
}

export const listShipmentsPaged = (opts: ShipmentListFacets & {
  page: number; pageSize: number
  /** Registry column filters — status, carrier, service, handling, COD, created-by, ranges. */
  filters?: RxFilter[]
  /** Registry column key. The server validates it against its own registry; unknown keys fall
   *  back to the default order rather than reaching SQL. */
  sortKey?: string | null
  sortDir?: 'asc' | 'desc'
}) => {
  const p = shipmentParams(opts, opts.filters)
  p.set('page', String(opts.page))
  p.set('page_size', String(opts.pageSize))
  if (opts.sortKey) { p.set('order_by', opts.sortKey); p.set('order_dir', opts.sortDir ?? 'desc') }
  return request<{
    items: ShipmentSummary[]; count: number; total?: number | null
    page: number; page_size: number; total_pages?: number | null; truncated?: boolean
  }>('GET', `/shipments?${p.toString()}`)
}

/** The server registry's capability report for this list. */
export const getShipmentColumnCaps = () =>
  request<{ items: RxColumnCap[]; count: number }>('GET', '/shipments/columns')

/** Options for ONE column, scoped to the tenant and to every OTHER active filter. */
export const getShipmentFacet = (opts: { column: string; q?: string; filters?: RxFilter[] }) => {
  const p = new URLSearchParams({ column: opts.column })
  if (opts.q) p.set('q', opts.q)
  for (const f of encodeRxFilters(opts.filters ?? [])) p.append('filter', f)
  return request<RxFacetResponse>('GET', `/shipments/facets?${p.toString()}`)
}

export interface ShipmentsSummary {
  total_count: number
  // null = redacted (caller lacks shipping.config — money policy)
  total_carrier_amount: number | null
  total_rate_amount: number | null
  delivered_count: number
  exception_count: number
  active_count: number
  manual_count: number
  imported_count: number
  rx_count: number
  avg_delivery_minutes: number | null
}
// The tiles run the SAME predicate as the table beneath them — the server composes one WHERE for
// the page, the COUNT, the CSV and this. Passing anything less here is how the old summary came
// to ignore the search box entirely.
export const shipmentsSummary = (opts: ShipmentListFacets & { filters?: RxFilter[] }) => {
  const s = shipmentParams(opts, opts.filters).toString()
  return request<ShipmentsSummary>('GET', `/shipments/summary${s ? `?${s}` : ''}`)
}

export interface ShipmentCreator { id: string; display_name: string }
// courier_names: courier-side order creators (entered-by) on imported orders
export const reconcileCarrierOrder = (id: string) =>
  request<{ id: string; order_number: string; matched_by?: string; already?: boolean }>('POST', `/shipments/${id}/reconcile-carrier-order`)

// D-SHIP-8: on-demand status refresh. Per-shipment: pull current status + POD from
// the courier NOW. Bulk: refresh all of this pharmacy's non-delivered shipments.
export const refreshShipmentStatus = (id: string) =>
  request<{ id: string; updated: boolean; old_status?: string; new_status?: string; delivered?: boolean; delivered_at?: string; reason?: string }>('POST', `/shipments/${id}/refresh-status`)

export const fetchShipmentDocuments = (id: string) =>
  request<{ fetched: number; photos: number; pdfs: number; detail?: string | null }>('POST', `/shipping/shipments/${id}/fetch-documents`)
export const refreshActiveShipments = () =>
  request<{ checked_count: number; advanced: number; delivered: number; failed: number; capped: boolean }>('POST', `/shipping/refresh-active`)

/** Download the generated Proof-of-Delivery document (role-aware: $ only for managers). */
export const rxPodDocUrl = (rxId: string) => `${API_BASE}/rx/${rxId}/pod-doc`
export const shipmentPodDocUrl = (shipmentId: string) => `${API_BASE}/shipping/shipments/${shipmentId}/pod-doc`

// AUTHED POD/tracker downloads (RCA 2026-07-17): auth is a per-tab Bearer token,
// so window.open(bareUrl) navigates WITHOUT the Authorization header and the
// endpoint sees "anonymous". These fetch through apiFetch (Bearer attached,
// 401->cookie reauth retry) and open the resulting blob in a new tab instead.
// Present an authed document. A synchronously-opened blank window is itself
// popup-blocked in some browsers (about:blank#blocked), so we DON'T rely on a
// pre-opened window. Instead: fetch the authed blob, then try to open the blob
// URL in a new tab; if the browser blocks that, fall back to a programmatic
// <a download> click, which is not popup-gated. Either way the user gets the
// document — no pre-opened window, no about:blank.
// ASP-59 hotfix (Patrick 2026-07-22): POD must just open in a NEW TAB — no overlay step.
// Browsers only allow window.open during transient user activation, and the courier fetch
// outlives it. Solution: open the tab SYNCHRONOUSLY (inside the click, activation intact)
// with a small loading shell, fetch the document, then steer that tab to the blob/SAS URL.
// Popup-blocked (w === null) falls back to the in-page viewer, which needs no activation.
const openDocNewTab = async (p: Promise<{ url: string; file_name: string }>) => {
  const w = window.open('', '_blank')
  if (w) {
    try {
      w.document.write('<title>Loading document…</title><p style="font:14px system-ui;padding:24px;color:#334155">Loading document…</p>')
      w.document.close()
    } catch { /* cross-origin guard — steering below still works */ }
  }
  try {
    const d = await p
    if (w && !w.closed) w.location.href = d.url
    else showDocViewer(d.url, d.file_name || 'document.pdf')
  } catch (e: any) {
    // Surface the real failure IN the tab — a silently-closed tab reads as "nothing happened".
    if (w && !w.closed) {
      try {
        w.document.body.innerHTML = `<div style="font:14px system-ui;padding:24px;color:#991b1b">Could not load the document.<br><span style="color:#475569">${String(e?.message ?? e).replace(/[<>]/g, '')}</span></div>`
      } catch { try { w.close() } catch { /* gone */ } }
    }
    throw e
  }
}

// In-page document viewer (RCA 2026-07-17 #2). Browsers only allow NEW-TAB
// opens during transient user activation (~5s after a click in Chrome). The
// courier POD is fetched live from the carrier and routinely takes longer, so
// by the time the blob was ready, activation had EXPIRED and EVERY open path —
// window.open OR a programmatic <a target=_blank> click — was blocked
// (about:blank#blocked). Verified in Chromium: navigator.userActivation.isActive
// is false after a 6.5s await. The only pattern needing NO activation is
// rendering in-page: show the PDF in an overlay iframe. The overlay's own
// Download / Open-in-tab buttons carry FRESH activation, so they always work.
function showDocViewer(url: string, fileName: string) {
  const ov = document.createElement('div')
  ov.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,.55);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px'
  const panel = document.createElement('div')
  panel.style.cssText = 'background:#fff;border-radius:12px;max-width:960px;width:100%;height:92vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.35)'
  const bar = document.createElement('div')
  bar.style.cssText = 'display:flex;align-items:center;gap:8px;padding:10px 14px;border-bottom:1px solid #e2e8f0;background:#f8fafc'
  const title = document.createElement('div')
  title.textContent = fileName
  title.style.cssText = 'flex:1;font-size:13px;font-weight:600;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap'
  const mkBtn = (label: string) => {
    const b = document.createElement('button')
    b.type = 'button'; b.textContent = label
    b.style.cssText = 'padding:6px 12px;border-radius:8px;border:1px solid #cbd5e1;background:#fff;color:#0f172a;font-size:12.5px;font-weight:600;cursor:pointer'
    return b
  }
  const dl = mkBtn('⬇ Download')
  dl.onclick = () => {   // fresh activation → allowed
    const a = document.createElement('a')
    a.href = url; a.download = fileName; a.rel = 'noopener'
    document.body.appendChild(a); a.click(); a.remove()
  }
  const nt = mkBtn('Open in new tab')
  nt.onclick = () => { window.open(url, '_blank') }   // fresh activation → allowed
  const cl = mkBtn('✕ Close')
  const close = () => { ov.remove(); if (url.startsWith('blob:')) URL.revokeObjectURL(url); document.removeEventListener('keydown', onKey) }
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
  cl.onclick = close
  ov.addEventListener('click', e => { if (e.target === ov) close() })
  document.addEventListener('keydown', onKey)
  const frame = document.createElement('iframe')
  frame.src = url
  frame.title = fileName
  frame.style.cssText = 'flex:1;border:0;width:100%;background:#525659'
  bar.appendChild(title); bar.appendChild(dl); bar.appendChild(nt); bar.appendChild(cl)
  panel.appendChild(bar); panel.appendChild(frame)
  ov.appendChild(panel)
  document.body.appendChild(ov)
}
// SIGNED-LINK OPEN (Patrick 2026-07-17): mint a short-lived tokened URL (an
// instant authed call, well inside the click's activation window) and open it
// as a REAL /api page in a new tab. That restores the page's own buttons: the
// document is server-served (no SPA-CSP inheritance killing inline print) and
// its Courier POD link carries the token, so both work from plain navigation.
// Falls back to the blob path if the signing key is unavailable.
const openViaLink = async (mintPath: string, blobPath: string) => {
  try {
    const r = await request<{ url: string | null; fallback?: boolean }>('GET', mintPath)
    if (r.url) { window.open(r.url, '_blank'); return }
  } catch { /* fall through to blob path */ }
  await openDocNewTab(fetchDocDownload(blobPath))
}
export const openRxPodDoc = (rxId: string, _win?: Window | null) =>
  openViaLink(`/rx/${rxId}/pod-link`, `/rx/${rxId}/pod-doc`)
export const openShipmentPodDoc = (shipmentId: string, _win?: Window | null) =>
  openViaLink(`/shipping/shipments/${shipmentId}/pod-link`, `/shipping/shipments/${shipmentId}/pod-doc`)
export const shipmentCourierPodUrl = (shipmentId: string) => `${API_BASE}/shipping/shipments/${shipmentId}/courier-pod`
export const openShipmentCourierPod = async (shipmentId: string, _win?: Window | null) => {
  try {
    const r = await request<{ courier_url: string | null }>('GET', `/shipping/shipments/${shipmentId}/pod-link`)
    if (r.courier_url) { window.open(r.courier_url, '_blank'); return }
  } catch { /* fall through to blob path */ }
  await openDocNewTab(fetchDocDownload(`/shipping/shipments/${shipmentId}/courier-pod`))
}

export const shipmentCreators = () =>
  request<{ items: ShipmentCreator[]; courier_names?: string[] }>('GET', '/shipments/creators')

// The export runs the identical predicate as the on-screen list, column filters INCLUDED. They
// used to be page-scoped display filters that the export could not see, so a CSV pulled under an
// active funnel silently contained more rows than the screen showed.
export const shipmentsExportCsvUrl = (opts: ShipmentListFacets & { filters?: RxFilter[] }) => {
  const s = shipmentParams(opts, opts.filters).toString()
  return `/api/shipments/export.csv${s ? `?${s}` : ''}`
}

export interface CarrierLookupAddress {
  name: string | null
  line1: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
}
export interface CarrierLookupResult {
  found: boolean
  carrier: string
  carrier_account_id?: string   // D-SHIP-23: the account that found it, so Save imports from the same one
  detail: string | null
  order_number?: string
  belongs_to_account?: boolean
  status?: string
  carrier_status_text?: string | null
  service?: string | null
  service_code?: string | null
  reference?: string | null
  recipient_name?: string | null
  pickup?: CarrierLookupAddress | null
  destination?: CarrierLookupAddress | null
  created_at?: string | null
  pickup_scheduled_at?: string | null
  delivery_scheduled_at?: string | null
  dispatched_at?: string | null
  picked_up_at?: string | null
  delivered_at?: string | null
  pod_at?: string | null
  pod_signature_name?: string | null
  amount_charged?: number | null
  fees_json?: string | null
  declared_value?: number | null
  pieces?: number | null
  weight?: number | null
  notes?: string | null
  events?: { status: string; carrier_status_text?: string | null; description: string | null; location: string | null; occurred_at: string }[]
}
export const carrierOrderLookup = (orderNumber: string) =>
  request<CarrierLookupResult>('GET', `/shipping/carrier-lookup?order=${encodeURIComponent(orderNumber)}`)

export interface ImportCarrierShipmentResult {
  id: string
  created: boolean
  refreshed: boolean
  patient_id: string | null
  patient_linked: boolean
  link_reason: string | null
  status: string
  order_number: string
}
// ── Carrier order scan (backfill all account orders → local DB) ──────────────
export interface CarrierScanJob {
  id: string; status: string; probed?: number; matched?: number; errors?: number
  range_start?: number; range_end?: number; cursor_at?: number
  created_at?: string; completed_at?: string | null
  /** True for a courier-wide census job (migration 335) rather than a
   *  single-pharmacy backfill. */
  is_census?: boolean
}
export interface CarrierScanOrder {
  id: string; carrier_order_number: string; recipient_name: string | null
  order_status: string | null; created_date: string | null
  destination_zip: string | null; imported_shipment_id: string | null
}
export const startCarrierScan = (
  carrier_account_id: string,
  opts: { days?: number; from?: string; to?: string
          mode?: 'census'; range_start?: number; range_end?: number },
) => request<{ job_id: string }>('POST', '/shipping/carrier-scan', { carrier_account_id, ...opts })

// ─── Courier-wide census report (migration 335) ──────────────────────────────
// Volume per originating pharmacy for a census job. Carries NO patient fields —
// a census stores none. `has_pod` drives the POD button, which streams live.
export interface CensusPharmacyRow {
  pharmacy: string
  order_count: number; delivered_count: number; pod_count: number; piece_count: number
  charged_total: number; quote_total: number; received_total: number
  first_order: string | null; last_order: string | null
}
export interface CensusOrderRow {
  order_number: string; order_status: string | null
  created_date: string | null; delivered_date: string | null
  service: string | null; pieces: number | null
  amount_quote: number | null; amount_received: number | null; amount_charged: number | null
  cod_status: string | null; has_pod: boolean
}
export interface CensusReport {
  job: { range_start: number; range_end: number; cursor_at: number
         status: string; probed: number; matched: number; errors: number }
  pharmacies: CensusPharmacyRow[]
  count: number
  /** False when NO order in the job reported a COD status — the column then means
   *  "the carrier did not supply this", not "nothing was collected". */
  cod_reported: boolean
}
export const getCensusReport = (jobId: string) =>
  request<CensusReport>('GET', `/shipping/carrier-scan/${jobId}/census`)
export const getCensusPharmacyOrders = (jobId: string, pharmacy: string) =>
  request<{ pharmacy: string; orders: CensusOrderRow[]; count: number }>(
    'GET', `/shipping/carrier-scan/${jobId}/census?pharmacy=${encodeURIComponent(pharmacy)}`)
export const getCarrierScanStatus = (jobId: string) =>
  request<CarrierScanJob>('GET', `/shipping/carrier-scan/${jobId}`)
export const listCarrierScans = () =>
  request<{ items: CarrierScanJob[] }>('GET', '/shipping/carrier-scan')
export const cancelCarrierScan = (jobId: string) =>
  request<{ id: string; cancelled: boolean }>('POST', `/shipping/carrier-scan/${jobId}/cancel`)
export const pauseCarrierScan = (jobId: string) =>
  request<{ id: string; paused: boolean }>('POST', `/shipping/carrier-scan/${jobId}/pause`)
export const resumeCarrierScan = (jobId: string) =>
  request<{ id: string; resumed: boolean }>('POST', `/shipping/carrier-scan/${jobId}/resume`)

// List-scoped carrier-order backfill: import specific historical orders that never
// reached the portal. Insert-only — the server skips any order already present, so a
// repeated call cannot duplicate. `dry_run` counts what would be imported and makes no
// carrier call, so it cannot report whether the carrier still holds the orders.
export interface CarrierOrderBackfillResult {
  processed: number
  inserted: number
  skipped_existing: number
  unavailable: number
  would_insert: number
  errors: number
  error_samples?: string[]
  dry_run: boolean
}
export const backfillCarrierOrdersFromList = (input: {
  carrier_account_id: string
  orders: string[]
  dry_run?: boolean
  limit?: number
}) =>
  request<CarrierOrderBackfillResult>('POST', '/shipping/carrier-orders/backfill-from-list', input)
export interface CarrierScanLogEntry {
  id: number; at: string; level: string; message: string
  probed_from?: number | null; probed_to?: number | null
  matched?: number | null; errors?: number | null
}
export const listCarrierScanLog = (jobId: string) =>
  request<{ items: CarrierScanLogEntry[] }>('GET', `/shipping/carrier-scan/${jobId}/log`)
export const tickCarrierScan = () =>
  request<{ probed: number; matched: number }>('POST', '/shipping/carrier-scan/tick')
export const listCarrierScanOrders = (jobId: string) =>
  request<{ items: CarrierScanOrder[] }>('GET', `/shipping/carrier-scan/${jobId}/orders`)

export const importCarrierShipment = (orderNumber: string, carrierAccountId?: string) =>
  request<ImportCarrierShipmentResult>('POST', `/shipping/shipments/import`, { order_number: orderNumber, carrier_account_id: carrierAccountId })

// ─── Global cross-pharmacy lookup (Shipping Admin only) ──────────────────────
// Privileged: bypasses the pharmacy-ownership check that gates the regular
// carrier-lookup endpoint. Returns FULL PHI plus the destination pharmacy
// name ('result_pharmacy_name'). Every call is audited server-side.
export interface GlobalLookupResult {
  found: boolean
  carrier: string
  detail: string | null
  order_number?: string
  result_pharmacy_name?: string | null
  status?: string
  carrier_status_text?: string | null
  service?: string | null
  service_code?: string | null
  reference?: string | null
  recipient_name?: string | null
  pickup?: CarrierLookupAddress | null
  destination?: CarrierLookupAddress | null
  created_at?: string | null
  pickup_scheduled_at?: string | null
  delivery_scheduled_at?: string | null
  dispatched_at?: string | null
  picked_up_at?: string | null
  delivered_at?: string | null
  pod_at?: string | null
  pod_signature_name?: string | null
  amount_charged?: number | null
  declared_value?: number | null
  pieces?: number | null
  weight?: number | null
  notes?: string | null
  events?: { status: string; carrier_status_text?: string | null; description: string | null; location: string | null; occurred_at: string }[]
  // OwlRx-side enrichment (null when the order belongs to a pharmacy not in OwlRx)
  local_match?: boolean
  drug_name?: string | null
  rx_number?: string | null
  copay?: number | null
}
// ─── Month-end close (D-FIN-1) ───────────────────────────────────────────────
// A CLOSED period is served from a written snapshot and never recomputed — that
// immutability is the requirement the whole feature exists for.
export interface MonthEndPeriod {
  period: string; shipments: number; closed: boolean
  closed_at: string | null
  revenue_total: number | null; gross_profit_total: number | null; fill_count: number | null
}
export interface MonthEndRow {
  rx: string | null; rx_number: string | null; fill_number: number | null
  ndc: string | null; drug: string | null; prescriber: string | null; payer: string | null
  date_filled: string | null; shipped_at: string | null; qty: number | null
  patient_responsibility: number; acquisition_cost: number
  ingredient_cost_paid: number | null; dispensing_fee_paid: number | null
  revenue: number; adjudication_fees: number | null; gross_profit: number
}
export interface MonthEndRevenue {
  period: string; period_start: string; period_end: string
  closed: boolean
  close: { closed_at: string; fill_count: number; revenue_total: number
           gross_profit_total: number; note: string | null } | null
  basis: string
  totals: { fill_count: number; revenue: number; acquisition: number
            patient_responsibility: number; gross_profit: number; gross_margin_pct: number | null }
  adjudication_fees_available: boolean
  adjudication_fees_note: string
  rows: MonthEndRow[]
}
export interface BilledNotShippedRow {
  rx: string | null; drug: string | null; payer: string | null
  date_filled: string | null; amount_paid: number
  patient_responsibility: number | null; acquisition_cost: number | null
}
export const getMonthEndPeriods = () =>
  request<{ items: MonthEndPeriod[]; count: number }>('GET', '/reports/month-end/periods')
export const getMonthEndRevenue = (period: string) =>
  request<MonthEndRevenue>('GET', `/reports/month-end/shipped-revenue?period=${encodeURIComponent(period)}`)
export const closeMonthEnd = (period: string, note?: string) =>
  request<{ ok: boolean; period: string; fill_count: number; revenue_total: number
            gross_profit_total: number; message: string }>(
    'POST', '/reports/month-end/close', { period, note })
export const getBilledNotShipped = (year: number) =>
  request<{ year: number; basis: string; treatment: string; excluded: string
            totals: { count: number; amount_paid: number }; rows: BilledNotShippedRow[] }>(
    'GET', `/reports/month-end/billed-not-shipped?year=${year}`)

// ─── PMS integration (D-PMS-1) ───────────────────────────────────────────────
// Vendor-scoped: the capability list is transcribed from the vendor's own API
// spec, so the page shows what we actually call, not a fixed assumption.
export interface PmsCapability { area: string; method: string; path: string; owlrx_use: string }
export interface LibertyPmsStatus {
  vendor: string
  display_name: string
  configured: boolean
  enabled: boolean
  mode: 'synthetic' | 'sandbox' | 'live'
  base_url: string
  customer_npi: string | null
  credentials_present: boolean
  auth: { schemes: string[]; note: string }
  spec: { url: string; openapi: string; paths_published: number; read_on: string }
  fill_grain_native: boolean
  writeback_supported: boolean
  capabilities: PmsCapability[]
  last_verified_at: string | null
  last_verify_error: string | null
}
export interface LibertyPmsTest {
  vendor: string; base_url: string; reachable: boolean
  http_status: number | null; auth_required: boolean; elapsed_ms: number
  summary: string; detail: string | null
}
export const getLibertyPmsStatus = () =>
  request<LibertyPmsStatus>('GET', '/carerx/integrations/liberty/status')
export const testLibertyPms = () =>
  request<LibertyPmsTest>('POST', '/carerx/integrations/liberty/test')

export const globalOrderLookup = (orderNumber: string) =>
  request<GlobalLookupResult>('GET', `/shipping/global-lookup?order=${encodeURIComponent(orderNumber)}`)

// The courier POD PDF for a global-lookup result. Streams the bytes (the server never stores
// another pharmacy's POD into our tenant rows), so this always resolves to a blob object URL.
export const globalOrderPod = (orderNumber: string) =>
  fetchDocDownload(`/shipping/global-lookup/pod?order=${encodeURIComponent(orderNumber)}`)

// ─── Shipping report (own pharmacy) ──────────────────────────────────────────
export interface ShippingReport {
  from: string
  to: string
  bucket: 'day' | 'month' | 'year'
  basis?: 'shipped' | 'delivered'
  totals: {
    order_volume: number
    total_cost: number
    delivered_count: number
    exception_count: number
    avg_delivery_minutes: number | null
  }
  series: { bucket: string; count: number; cost: number }[]
  by_carrier: { carrier: string; count: number; cost: number }[]
  by_service?: { service: string; count: number; cost: number; delivered_count: number; avg_delivery_minutes: number | null }[]
  by_patient?: { patient_id: string; display_name: string; count: number; cost: number }[]
  patient_summary?: { distinct_patients: number; attributed_cost: number; unattributed_cost: number; unattributed_count: number; avg_cost_per_patient: number }
}
export const getShippingReport = (from?: string, to?: string, basis?: 'shipped' | 'delivered', group?: 'auto' | 'day' | 'month' | 'year') => {
  const qs = new URLSearchParams()
  if (from) qs.set('from', from)
  if (to) qs.set('to', to)
  if (basis) qs.set('basis', basis)
  if (group && group !== 'auto') qs.set('group', group)
  const s = qs.toString()
  return request<ShippingReport>('GET', `/shipments/report${s ? `?${s}` : ''}`)
}

export interface ShipmentDetailRecord {
  id: string
  carrier: string
  carrier_shipment_id: string | null
  tracking_number: string | null
  status: string
  service_level: string | null
  require_signature: boolean
  cold_chain: boolean
  saturday_delivery: boolean
  adult_signature: boolean
  special_instructions: string | null
  carrier_status_text: string | null
  delivered_at: string | null
  pod_signature_name: string | null
  shipped_at: string | null
  picked_up_at: string | null
  last_tracked_at: string | null
  rx_item_id: string | null
  rx_refs?: { rx_number: string; rx_id: string }[]
  patient_id: string | null
  is_manual: boolean
  is_combined?: boolean
  imported_from_courier_at: string | null
  carrier_amount: number | null
  carrier_service_code: string | null
  recipient_name: string | null
  recipient_line1: string | null
  recipient_city: string | null
  recipient_state: string | null
  recipient_zip: string | null
  recipient_phone: string | null
  /** D-SOC-6: the site-of-care facility this shipment was sent to, when one was chosen. */
  site_of_care_facility_id?: string | null
  site_of_care_facility?: string | null
  /** D-SOC-6: the destination differs from the patient's own address (redirected delivery). */
  destination_is_redirect?: boolean
  rx_number: string | null
  /** Imported orders: courier piece Reference (portal Ref#) — the real Rx. */
  carrier_reference?: string | null
  created_by: string | null
  pod_image_data: string | null
  pod_image_mime: string | null
  tracker_url: string | null
  carrier_order_confirmed?: boolean   // D-SHIP-23: the carrier's own id rule, so the UI never guesses its format
  courier_pod_pdf?: boolean           // D-SHIP-23: the carrier issues its own POD PDF (e-Courier)
  order_label?: string | null         // D-SHIP-23: the order number people read (RoadPas ORD-…)
  // D-SOC-4 F-2: a site-of-care block stood here with no column, no projection and no consumer.
  // Removed 2026-09-20; see docs/audits/REVIEW-2026-09-20-site-of-care.md §2.
  facility_phone?: string | null
}
export interface ShipmentDetailEvent {
  status: string
  carrier_status_text: string | null
  description: string | null
  location: string | null
  occurred_at: string
}
export interface ShipmentPodDocument {
  id: string
  doc_type: string
  carrier_file_id: string | null
  file_name: string | null
  content_type: string | null
  data_base64: string | null
  captured_at: string | null
}
export const getShipmentDetail = (id: string) =>
  request<{ shipment: ShipmentDetailRecord; events: ShipmentDetailEvent[]; documents: ShipmentPodDocument[] }>('GET', `/shipping/shipments/${id}`)

export interface CreateShipmentInput {
  allow_relabel?: boolean
  relabel_reason_code?: string
  relabel_reason_text?: string
  carrier_account_id: string
  /** D-SHIP-23: pickup branch for this order; the account's branch when absent. */
  pickup_location_id?: string
  rx_item_id?: string
  rx_items?: { rx_item_id: string; mismatch_override?: boolean; override_reason?: string; scanned_code?: string }[]
  shipment_kind?: 'rx' | 'manual' | 'facility'   // D-SHIP-FAC-1: 'facility' = one package per facility ADDRESS, many patients
  add_to_address_list?: boolean
  address_label?: string
  reference?: string
  service_level?: string
  require_signature?: boolean
  adult_signature?: boolean
  cold_chain?: boolean
  saturday_delivery?: boolean
  special_instructions?: string
  description?: string
  // manual address (when no rx_item_id)
  recipient_name?: string
  line1?: string
  line2?: string
  city?: string
  state?: string
  zip?: string
  phone?: string
  /**
   * D-SHIP-COD-MANUAL: operator-entered collect-on-delivery amount. MANUAL PACKAGES ONLY —
   * the server rejects it on Rx-linked orders, where the amount is derived from claims.
   * Requires the copay.override permission. Becomes the order's copay_amount, so the label,
   * the Copay column and the courier all show the same number.
   */
  cod_amount?: number
  /**
   * D-SOC-6 (mig 428): the site-of-care facility this package is going to, when the operator
   * picked one from the directory. Applies to BOTH destination paths — the facility's address
   * still travels in the fields above; this records WHICH facility so the delivery can be
   * queried and counted instead of string-matched. Rejected with 422 facility_not_found if the
   * facility is unknown or inactive. Distinct from `shipment_kind: 'facility'` (D-SHIP-FAC-1),
   * which is about one package covering many patients at one address.
   */
  facility_id?: string
}
export interface ScanCheckResult {
  verdict: 'ok' | 'mismatch' | 'duplicate' | 'not_shippable' | 'not_found' | 'not_facility'
  // D-SHIP-FAC-1: which gate tripped — 'patient' (Rx shipment) or 'address' (facility shipment)
  mismatch_kind?: 'patient' | 'address'
  expected_facility_name?: string | null
  actual_facility_name?: string | null
  facility_name?: string | null
  rx_item_id?: string
  rx_number?: string
  drug_name?: string | null
  state?: string | null
  patient_id?: string
  patient_name?: string | null
  actual_patient_name?: string | null
  copay?: number | null
  collected?: boolean
  // 86cb27r8q: acquisition_cost > threshold for THIS scanned Rx. Present so the signature
  // suggestion no longer depends on which Rx happened to be scanned first.
  require_signature_suggested?: boolean
  existing_order_number?: string | null
}
export interface ShipContext {
  rx: { id: string; rx_number: string | null; drug_name: string | null; state: string | null; require_signature_suggested?: boolean }
  patient: { id: string; display_name: string | null; date_of_birth: string | null; primary_phone: string | null; email: string | null; delivery_notes?: string | null; address_line: string | null; city: string | null; state_code: string | null; postal_code: string | null }
  addresses: { id: string; label: string | null; recipient: string | null; line1: string; city: string; state_code: string | null; postal_code: string; phone: string | null }[]
  copay?: number | null   // ASP-87: patient pay, collected at delivery by the courier
  copay_collected?: boolean       // D-SHIP-COPAY-COLLECTED: sale completed in PioneerRx
  copay_paid_amount?: number | null   // Rx.PatientPaidAmount — dollars actually paid
}
export interface PioneerWriteback {
  enabled: boolean; is_test_mode: boolean; base_url: string | null
  has_credentials: boolean; updated_at: string | null
  api_type: string; cert_thumbprint: string | null
}
export const getPioneerWriteback = () =>
  request<PioneerWriteback>('GET', '/shipping/pioneer-writeback')
export const putPioneerWriteback = (body: {
  enabled: boolean; is_test_mode: boolean; base_url: string; api_type: string
  vendor_key?: string; data_exchange_id?: string
  employee_id?: string; shared_secret?: string; cert_thumbprint?: string
}) =>
  request<{ saved: boolean; credentials_rotated: boolean }>('PUT', '/shipping/pioneer-writeback', body)
export const testPioneerWriteback = () =>
  request<{ status: string; http_code?: number; message: string; server_cert_thumbprint?: string | null }>('POST', '/shipping/pioneer-writeback/test')

export const getShipContext = (rxId: string) =>
  request<ShipContext>('GET', `/shipping/rx/${rxId}/ship-context`)

export const shipmentScanCheck = (rx_number: string, primary_rx_item_id?: string, shipment_kind?: 'facility') =>
  request<ScanCheckResult>('POST', '/shipping/shipments/scan-check', { rx_number, primary_rx_item_id, shipment_kind })

/** ASP-60: admit a row-selected Rx by id through the same scan-check verdict gate. */
export const shipmentScanCheckById = (rx_item_id_for_check: string, primary_rx_item_id?: string, shipment_kind?: 'facility') =>
  request<ScanCheckResult>('POST', '/shipping/shipments/scan-check', { rx_item_id_for_check, primary_rx_item_id, shipment_kind })

/** ASP-90: one bounded reconcile pass for a pending_confirmation shipment (Refresh button). */
export const confirmShipmentOrder = (id: string) =>
  request<{ confirmed: boolean; order_number?: string | null; status?: string | null; detail?: string }>(
    'POST', `/shipping/shipments/${id}/confirm-order`, {})

/** D-SHIP-17 Option 1: combine into an existing order — add an Rx to an already-labeled shipment (no cancel, same order number). */
export const addRxToShipment = (shipmentId: string, body: { rx_item_id: string; rx_fill_item_id?: string; scanned_code?: string }) =>
  request<{ id: string; combined: boolean; added_rx_number?: string; already_present?: boolean }>('POST', `/shipping/shipments/${shipmentId}/add-rx`, body)

export interface ActiveShipment {
  shipment_id: string
  order_number: string | null
  status: string
  /** false for pending_confirmation — the order is real but has no number to reprint under yet. */
  combinable?: boolean
  shipped_at: string | null
  // D-SHIP-FAC-1: patient_name is needed in Facility mode — one package holds several RESIDENTS'
  // prescriptions, so the open-package list has to say whose each Rx is.
  rx: { rx_item_id: string | null; rx_number: string | null; drug_name: string | null; patient_name?: string | null }[]
}
/** D-SHIP-17: patient's still-open (pre-pickup) shipments, for the active-shipment panel. Read-only, polled. */
export const activeShipmentsForPatient = (patientId: string) =>
  request<{ shipments: ActiveShipment[] }>('GET', `/shipping/shipments/active-for-patient?patient_id=${encodeURIComponent(patientId)}`)
/** D-SHIP-FAC-1: the FACILITY's still-open packages — any resident's Rx can be combined into one. */
export const activeShipmentsForFacility = (facilityId: string) =>
  request<{ shipments: ActiveShipment[] }>('GET', `/shipping/shipments/active-for-patient?facility_id=${encodeURIComponent(facilityId)}`)

export const createShipment = (body: CreateShipmentInput) =>
  request<{ id: string; created: boolean; is_manual: boolean; rx_item_id: string | null; rx_state_advanced: boolean; carrier: string; tracking_number: string | null; status: string }>('POST', '/shipping/shipments', body)
/** D-SHIP-3 WS1: verify a cancel_pending shipment against the carrier (finalizes, keeps waiting, or reverts + alerts). */
export const verifyShipmentCancel = (id: string) =>
  request<{ id: string; status: string; cancelled?: boolean; pending?: boolean; cancel_failed?: boolean; message?: string }>(
    'POST', `/shipping/shipments/${id}/verify-cancel`, {})

export const cancelShipment = (id: string) =>
  request<{ id: string; status: string; cancelled?: boolean; message?: string }>('POST', `/shipping/shipments/${id}/cancel`)

export const deleteOrderAdmin = (id: string) =>
  request<{ id: string; status: string; carrier_confirmed?: boolean; message?: string }>('POST', `/shipping/shipments/${id}/delete-order`)

export interface ShipmentLabelData {
  carrier: string
  tracking_number: string | null
  order_ref: string | null
  require_signature: boolean
  cold_chain: boolean
  saturday: boolean
  adult_signature: boolean
  instructions: string | null
  is_manual: boolean
  recipient_name: string | null
  to_line1: string | null
  to_city_state_zip: string | null
  to_phone: string | null
  rx_number: string | null
  rx_numbers?: string[]          // ASP-18: every Rx in the package (fill suffix where known)
  shipment_kind?: 'rx' | 'manual' | 'facility' | null   // D-SHIP-FAC-1
  rx_lines?: { rx_number: string | null; patient_name: string | null; drug_name: string | null }[]   // facility kind: who gets what
  status?: string | null         // ASP-90: pending_confirmation blocks printing
  service_level?: string | null  // ASP label v2: NEXTDAY / SAMEDAY row
  ship_date?: string | null      // ASP label v2: creation date MM/DD/YYYY
  copay: number | null
  copay_items?: { rx_number: string; copay: number; collected?: boolean; due?: number }[]   // per-Rx copay + paid/collect status + amount still due
  copay_collected?: boolean       // D-SHIP-COPAY-COLLECTED: all shipped members' sales completed
  copay_paid_amount?: number | null   // sum of Rx.PatientPaidAmount across members
  copay_state?: 'none' | 'paid' | 'needed'   // D-SHIP-COPAY-MULTI: the copay slot state
  copay_collect_total?: number   // driver-collect amount (pending Rx only)
  copay_unitemised?: number      // debt with no printable rx_number — printed as its own Balance line
  pharmacy_name: string
  pharmacy_logo_url: string | null
  from_line: string | null
  // D-SOC-2/3: the site of care the label prints. Deleted on 2026-09-20 (D-SOC-4 F-2) as
  // unbacked; BACKED now by SiteOfCareLabelResolver over dbo.rx_site_of_care (mig 400).
  facility_type?: string | null
  facility_type_label?: string | null
  facility_name?: string | null
  facility_phone?: string | null
}
export const getShipmentLabelData = (id: string) =>
  request<ShipmentLabelData>('GET', `/shipping/shipments/${id}/label-data`)
// Lightweight Rx search for the shipment picker (reuses GET /api/rx?q=)
export interface RxSearchRow { id: string; rx_number: string | null; drug_name: string | null; patient_name?: string | null; state: string }
export const searchRx = (q: string) =>
  request<{ items: RxSearchRow[] }>('GET', `/rx?q=${encodeURIComponent(q)}`)

export interface LabelConfig {
  size: '4x6' | '4x3' | 'letter'
  logo_mode: 'pharmacy' | 'custom' | 'none'
  custom_logo_url: string | null
  show_handling_flags: boolean
  show_instructions: boolean
  show_from_block: boolean
  show_copay: boolean
  show_sort_qr: boolean
  barcode_value: 'tracking' | 'order'
  accent_hex: string
  consult_message: string | null
  show_consult_message: boolean
  show_refrigerate_banner: boolean
  base_font_px?: number   // D-SHIP-7: user base font size (10–40, default 14); auto-fit still guarantees one label
  custom_elements: { id: string; text: string; region: 'header' | 'body' | 'footer'; emphasis: 'normal' | 'bold' | 'banner'; size?: number | null; align?: 'left' | 'center' | 'right' | null }[]
  instruction_presets?: string[]   // ASP-88: quick-text checkboxes in the create-shipment drawer
}
export const getLabelConfig = () =>
  request<{ config: LabelConfig; is_default: boolean; pharmacy_logo_url: string | null; pharmacy_name: string | null }>('GET', '/shipping/label-config')
export const putLabelConfig = (config: LabelConfig) =>
  request<{ saved: boolean; config: LabelConfig }>('PUT', '/shipping/label-config', config)

// ── CareRx (clinical documentation module) ──────────────────────────────────
export interface CareRxAssessment {
  id: string; patient_id: string; patient_name?: string; mrn?: string
  prescriber_id?: string; prescriber_name?: string
  assessment_type: string; status: string; current_step: number; total_steps: number
  assigned_nurse_id?: string; visit_date?: string; drug_name?: string; diagnosis?: string
  signature_status?: string; signed_at?: string; created_at: string; updated_at: string
}
export const listCareRxAssessments = (params?: { patient_id?: string; status?: string }) => {
  const q = new URLSearchParams()
  if (params?.patient_id) q.set('patient_id', params.patient_id)
  if (params?.status) q.set('status', params.status)
  const qs = q.toString()
  return request<{ items: CareRxAssessment[]; count: number }>('GET', `/carerx/assessments${qs ? '?' + qs : ''}`)
}
export const createCareRxAssessment = (body: {
  patient_id: string; prescriber_id?: string; drug_name?: string; diagnosis?: string; visit_date?: string; payload?: string
  baseline_labs?: CareRxLab[]
}) => request<{ id: string; status: string; labs_upserted?: number }>('POST', '/carerx/assessments', body)
export interface CareRxLab {
  id?: string
  panel?: string | null
  results_collected_at?: string | null
  no_results?: boolean
  analyte: string
  value_text?: string | null
  value_num?: number | null
  unit?: string | null
  ref_range?: string | null
  abnormal_flag?: string | null
  drawn_at?: string | null
  status?: string | null
  due_date?: string | null
  validity_days?: number | null
  expires_at?: string | null
  required_for_start?: boolean | null
  required_for_start_drug?: string | null
  coverage_status?: string | null
  coverage_notes?: string | null
  draw_status?: string | null
  draw_scheduled_at?: string | null
  draw_location?: string | null
  draw_assigned_to?: string | null
}
export const listCareRxLabs = (patientId: string) =>
  request<{ patient_id: string; items: CareRxLab[]; count: number }>('GET', `/carerx/patients/${patientId}/labs`)

export interface CareRxLabWorklistRow { id: string; patient_id: string; patient_name: string | null; mrn: string | null; analyte: string; panel: string | null; status: string | null; drawn_at: string | null; expires_at: string | null; validity_days: number | null; required_for_start: boolean; draw_status: string | null; coverage_status: string | null; abnormal_flag: string | null; draw_scheduled_at: string | null }
export const listCareRxLabWorklist = () =>
  request<{ items: CareRxLabWorklistRow[]; count: number }>('GET', '/carerx/labs/worklist')

export interface CareRxPcwQueueRow { id: string; patient_id: string; patient_name: string | null; mrn: string | null; status: string | null; therapy_determination: string | null; locked_at: string | null; updated_at: string; drug_name: string | null; therapy_id?: string | null; therapy_archetype?: string | null }
export const listCareRxPcwQueue = () =>
  request<{ items: CareRxPcwQueueRow[]; count: number }>('GET', '/carerx/pcw/queue')

export interface CareRxHoldRow { id: string; patient_id: string; patient_name: string | null; mrn: string | null; status: string; reason: string | null; trigger_analyte: string | null; opened_at: string | null; therapy_id?: string | null; therapy_drug?: string | null; cycle_no?: number | null }
export interface CareRxScreeningRow { id: string; patient_id: string; patient_name: string | null; mrn: string | null; analyte: string; result: string; result_date: string | null; therapy_id?: string | null; therapy_drug?: string | null; cycle_no?: number | null }
export const listCareRxScreeningWorklist = () =>
  request<{ holds: CareRxHoldRow[]; screenings: CareRxScreeningRow[]; count: number }>('GET', '/carerx/screening/worklist')
export const updateCareRxLabDraw = (labId: string, body: { coverage_status?: string; coverage_notes?: string; draw_status?: string; draw_scheduled_at?: string; draw_location?: string; draw_assigned_to?: string; lab_company?: string; draw_followup_at?: string; results_collected_at?: string; no_results?: boolean }) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/labs/${labId}/draw`, body)
export interface DrugMatrixEntry {
  id: string
  genericName: string
  brandName: string
  drugClass: string
  route: string
  indications: string[]
  requiredBaselabs: string[]
  optionalLabs: string[]
  remsProgram?: string | null
  pregnancyCategory?: string | null
  teratogen: boolean
  blackBoxWarnings: string[]
  commonDDIs: string[]
  dosingReference?: string | null
  infusionDuration?: string | null
  monitoringParameters: string[]
}
export const getCareRxDrugMatrix = () =>
  request<{ items: DrugMatrixEntry[]; count: number }>('GET', '/carerx/drug-matrix')
export const matchCareRxDrug = (medication: string) =>
  request<{ medication: string; match: DrugMatrixEntry | null }>('GET', `/carerx/drug-matrix/match?medication=${encodeURIComponent(medication)}`)
export interface PcwAlert { type: string; severity: string; message: string }
export interface PcwRequiredLab { name: string; status: string; drawn_at?: string | null; expires_at?: string | null }
// D-CRX-24: the POT lane — start / queue / claim / complete on carerx_visits.
export interface CareRxPotQueueItem {
  id: string; status: string; visit_date: string | null; visit_type: string;
  patient_id: string; patient_name: string | null; mrn: string | null;
  drug_name: string | null; therapy_id: string;
  agency_name: string | null; agency_id: string | null;
  nurse_name: string | null; nurse_id: string | null;
  notes: string | null; scheduled_at: string | null; claimed_at: string | null;
}
export const startCareRxPot = (therapyId: string, body: {
  visit_date: string; visit_type?: string; agency_id?: string; nurse_id?: string; notes?: string;
}) =>
  request<{ visit_id: string; status: string; medication: string | null; prescriber: string | null;
            organization: { id: string; name: string } | null; nurse: { id: string; name: string } | null;
            note: string | null }>('POST', `/carerx/therapies/${therapyId}/pot`, body)
// D-CRX-26 S3: the POT clinical instrument (schema-driven form) client.
// D-CRX-26 S4: fetch a CareRx document's HTML and open it for viewing / browser print-to-PDF.
/** Open a stored CareRx document in a new tab. Lab 2026-10-20: the POT, enrollment and consent
 *  documents are PDFs (application/pdf), and writing their bytes with document.write showed raw
 *  "%PDF-1.7 … endobj" text; HTML documents (invoice/LOA today) happened to work. A blob URL keeps
 *  the server's content type, so both render. Pass a window claimed inside the click gesture
 *  when a popup blocker is a concern (CareRxBilling does the same). */
export const openCareRxDocument = async (docId: string, target?: Window | null) => {
  const res = await apiFetch(`/carerx/documents/${docId}`)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  if (target) target.location.href = url
  else window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export const getCareRxPotForm = (visitId: string) =>
  request<{ visit_id: string; visit_status: string;
            schema: { id: string; code: string; version: number; scope: string; definition: unknown } | null;
            form: { id: string; version: number; status: string; sections: string | null;
                    section_status: string | null; attestation: string | null; content_hash: string | null;
                    updated_at: string; completed_at: string | null; pinned_refs?: string | null } | null }>(
    'GET', `/carerx/visits/${visitId}/pot-form`)
export const putCareRxPotForm = (visitId: string, body: { sections?: string; section_status?: string }) =>
  request<{ id: string; version: number; status: string; updated_at: string }>('PUT', `/carerx/visits/${visitId}/pot-form`, body)
export const completeCareRxPotForm = (visitId: string, body: { signature_data_url: string }) =>
  request<{ id: string; version: number; status: string; content_hash: string }>('POST', `/carerx/visits/${visitId}/pot-form/complete`, body)

export const listCareRxPotQueue = () =>
  request<{ items: CareRxPotQueueItem[]; count: number }>('GET', '/carerx/pot/queue')
// D-CRX-47: the "ready to start a POT" queue — enrollment-passed + consent-signed patients.
export interface PotEligibleRow {
  therapy_id: string; patient_id: string; patient_name: string | null; mrn: string | null
  drug_name: string | null; prescriber: string | null; enrollment_status: string | null; consent_ok: boolean
}
export const listCareRxPotEligible = () =>
  request<{ items: PotEligibleRow[]; count: number }>('GET', '/carerx/pot/eligible')
export const claimCareRxPot = (visitId: string) =>
  request<{ id: string; status: string }>('PUT', `/carerx/visits/${visitId}/claim`, {})
// D-CRX-47 POT-8: the login-scoped nurse view — visits assigned to the current account.
export interface NurseVisitRow { id: string; visit_date: string | null; visit_type: string | null; status: string; duration_hrs: number | null; patient_name: string | null; mrn: string | null; drug_name: string | null; form_done: boolean; patient_id: string }
export const listCareRxMyVisits = () => request<{ items: NurseVisitRow[]; count: number }>('GET', '/carerx/pot/my-visits')
// Go-live #5: an open visit that will not happen is cancelled (reason required, audited) — never completed.
export const cancelCareRxVisit = (visitId: string, reason: string) =>
  request<{ id: string; status: 'cancelled'; reason: string }>('PUT', `/carerx/visits/${visitId}/cancel`, { reason })
export const completeCareRxPot = (visitId: string, body: {
  duration_hrs: number; mileage?: number; after_hours?: boolean; weekend?: boolean; holiday?: boolean; notes?: string;
  visit_type?: string; city?: string; state_code?: string; zip?: string;
}) =>
  request<{ id: string; status: string; note: string; document_id?: string }>('PUT', `/carerx/visits/${visitId}/complete`, body)
// D-CRX-47 POT-3: duplicate a POT into a fresh scheduled visit (attestation/signature cleared).
export const duplicateCareRxPot = (visitId: string) =>
  request<{ visit_id: string; duplicated_from: string; status: string; form_cloned: boolean; note: string }>(
    'POST', `/carerx/visits/${visitId}/duplicate-pot`, {})
// D-CRX-22: batch specialty flags for the patients screen (badge next to name).
export const getCareRxSpecialtyFlags = (ids: string[]) =>
  request<{ flags: Record<string, { specialty: boolean; formulary_match: boolean; sample_drug: string | null }> }>(
    'GET', `/carerx/patients/specialty-flags?ids=${ids.join(',')}`)

// D-CRX-21: patient-profile enrollment hint (specialty rx detection via the D-CRX-15 link).
export interface CareRxEnrollmentHint {
  patient_id: string; specialty: boolean; count: number; note: string | null;
  candidates: { formulary_id: string; name: string; therapy_archetype: string; category: string | null; rx_drug_name: string | null }[];
}
export const getCareRxEnrollmentHint = (patientId: string) =>
  request<CareRxEnrollmentHint>('GET', `/carerx/patients/${patientId}/enrollment-hint`)

// D-CRX-19: divergence worklist + ProtocolDef editor.
export interface CareRxDivergence {
  id: string; divergence_type: string; status: string; detail: string | null;
  rx_number: string | null; prx_fill_number: number | null; assigned_role: string | null;
  detected_at: string; resolution: string | null; resolution_reason: string | null; resolved_at: string | null;
  therapy_id: string | null; drug_name: string | null; cycle_no: number | null;
  patient_id: string | null; patient_name: string | null; mrn: string | null; fill_id: string | null;
}
export const listCareRxDivergences = (status?: 'open' | 'resolved' | 'all') =>
  request<{ items: CareRxDivergence[]; count: number }>('GET', `/carerx/divergences${status ? `?status=${status}` : ''}`)
export const resolveCareRxDivergence = (id: string, body: { resolution: 'match' | 'void' | 'cancel' | 'delay'; reason: string }) =>
  request<{ id: string; resolution: string }>('PUT', `/carerx/divergences/${id}/resolve`, body)

export interface CareRxProtocolDef {
  id: string; scope: 'global' | 'pharmacy'; code: string; name: string; version: number;
  steps: string | null; monitoring: string | null; reinitiation_criteria: string | null;
  active: boolean; updated_at: string;
}
export const listCareRxProtocolDefs = () =>
  request<{ items: CareRxProtocolDef[]; count: number }>('GET', '/carerx/protocol-defs')
export const updateCareRxProtocolDef = (id: string, body: {
  name?: string; steps?: string; monitoring?: string; reinitiation_criteria?: string; active?: boolean;
}) =>
  request<{ id: string; code: string; copy_on_write: boolean; version: number }>('PUT', `/carerx/protocol-defs/${id}`, body)

// D-CRX-18: the enrollment door — twin gate + duplicate guard + therapy + enrollment + PCW in one call.
export interface CareRxEnrollmentCandidate { rx_id: string; patient_id: string; patient_name: string | null; mrn: string | null; drug_name: string | null; payer_state: string | null; created_at: string | null; formulary_id: string | null; date_of_birth: string | null; prescriber_name: string | null; is_synthetic: boolean; /** One row per patient per prescription (owner ruling 2026-10-01). */ rx_number: string | null; /** D-CRX-87 §4e: the date the Rx start date gates on (yyyy-MM-dd); null when Pioneer sent none. */ date_written?: string | null; /** ASP #9: language name (English included); null when Pioneer has none. */ preferred_language?: string | null; /** D-CRX-87b: the open same-drug enrollment this Rx would attach to (combining pharmacies only). */ attach?: CombineTarget | null }
export interface CareRxDuplicatePlanRow { id: string; patient_id: string; patient_name: string | null; drug_name: string | null; status: string; created_at: string; group_count: number }
export const listCareRxDuplicatePlans = () => request<{ items: CareRxDuplicatePlanRow[]; count: number }>('GET', '/carerx/assessments/duplicates')
export const voidCareRxAssessment = (id: string, reason: string) => request<{ id: string; status: string }>('POST', `/carerx/assessments/${id}/void`, { reason })
// D-PERF-1 P2: the candidate feed is server-paged on the canonical envelope. `total`/`total_pages`
// count PATIENTS (the feed's visible row groups a patient's drugs), `count` the rows on this page.
// Omitting page/page_size keeps the legacy bounded response for any old bundle still in a browser.
export interface CareRxCandidatePage {
  items: CareRxEnrollmentCandidate[]; count: number; unclassified_drugs: number; unqualified_count?: number
  /** ASP #14: prescriptions dismissed from this list (restorable). */
  dismissed_count?: number
  total?: number; page: number; page_size: number; total_pages?: number; truncated?: boolean
  /** D-CRX-87 §2: the pharmacy's Rx start date and the qualified Rx it keeps off this list (all null when unset). */
  rx_start_date?: string | null; rx_start_set_by?: string | null; rx_start_set_at?: string | null; before_start_count?: number | null
}
export const listCareRxEnrollmentCandidates = (opts?: { page?: number; pageSize?: number; q?: string; filter?: string[]; signal?: AbortSignal }) => {
  const qs = new URLSearchParams()
  if (opts?.page) qs.set('page', String(opts.page))
  if (opts?.pageSize) qs.set('page_size', String(opts.pageSize))
  if (opts?.q?.trim()) qs.set('q', opts.q.trim())
  for (const f of opts?.filter ?? []) if (f) qs.append('filter', f)
  const s = qs.toString()
  return request<CareRxCandidatePage>('GET', `/carerx/enrollment/candidates${s ? `?${s}` : ''}`, undefined, { signal: opts?.signal })
}
// ---- D-CRX-42 Slice 1: Rx-anchored picker feed (DAW text verbatim; R-4 unresolved) ----
export interface EnrollRxRow { rx_number: string | null; drug_name: string | null; ndc: string | null; daw_text: string | null; daw_code: number | null; substitution_allowed: boolean | null; pa_approved: boolean; drug_matched: boolean; claim_status: string | null; rx_status: string | null; sig: string | null; formulary_id: string | null; specialty_tag: string | null; enrollable: boolean; has_active_therapy?: boolean; /** ASP #4: PioneerRx ICD-10 codes on this Rx, primary first. */ diagnosis_codes?: string | null; /** ASP 2026-10-11 (#3): fill-aware dispensed quantity + days supply. */ quantity?: string | null; days_supply?: number | null; /** Patrick 2026-10-08: 'escript_note' = read from the prescriber's note, unverified. */ diagnosis_source?: 'pioneer' | 'escript_note' | null; /** D-CRX-87 §4e: written before the pharmacy's Rx start date — greyed, still pickable. */ before_start_date?: boolean; /** D-CRX-87b: the open same-drug enrollment this Rx would attach to (combining pharmacies only). */ attach?: CombineTarget | null }
// UAT 2026-07-29: the door's drug dropdown reads Drug Reference (D-CRX-43/R-19); value resolves to the linked formulary anchor.
export interface EnrollDrugOption { drug_reference_id: string; name: string; formulary_id: string | null; linked: boolean; care_plan_frequency?: string | null }
export const enrollmentDrugList = () =>
  request<{ items: EnrollDrugOption[]; count: number }>('GET', '/carerx/enrollment/drug-list')
export interface EnrollPatientBanner { allergies: string | null; allergy_alert_type: string | null; height_inches: number | null; weight_ounces: number | null; address_line: string | null; city: string | null; state_code: string | null; zip_code: string | null }
export interface LabCheckItem {
  analyte: string; last_date: string | null; current: boolean
  /** D-CRX-95: with the lab reader on, the printed result (date + value from the same row) and where it came from. */
  result_date?: string | null; result_value?: string | null; result_source?: LabResultSource | null
}
export type LabResultSource = 'order' | 'screening' | 'result'
/** `reader`/`list_edit` are present only when the pharmacy's lab reader is on (D-CRX-95); absent = today's form. */
export interface LabCheckResponse { items: LabCheckItem[]; reader?: 'on'; list_edit?: boolean }
/** `analytes` (reader on only): answer for these labs instead of the drug's list — a lab the form is adding. */
export const enrollmentLabCheck = (patientId: string, formularyId: string, analytes?: string[]) =>
  request<LabCheckResponse>('GET', `/carerx/enrollment/lab-check?patient_id=${patientId}&formulary_id=${formularyId}`
    + (analytes?.length ? `&analytes=${encodeURIComponent(analytes.join(','))}` : ''))

// ---- D-CRX-95: an enrollment's own lab list (add / waive / restore, reason required) ----
export type LabReasonCode = 'prescriber_request' | 'not_clinically_indicated' | 'done_elsewhere' | 'contraindicated' | 'other'
export interface LabRequirementItem { analyte: string; reason_code: LabReasonCode; reason_text?: string }
export interface TherapyLab {
  analyte: string; label: string; source: 'formulary' | 'added'; reason_code: LabReasonCode | null; reason_text: string | null
  evidence_date: string | null; current: boolean; result_date: string | null; result_value: string | null
  result_source: LabResultSource | null; result_from_this_enrollment: boolean
}
export interface TherapyWaivedLab { analyte: string; label: string; reason_code: LabReasonCode | null; reason_text: string | null; by: string | null; at: string | null }
export const getTherapyLabs = (therapyId: string) =>
  request<{ list_edit: boolean; labs: TherapyLab[]; waived: TherapyWaivedLab[] }>('GET', `/carerx/therapies/${therapyId}/labs`)
export const changeTherapyLabRequirement = (therapyId: string, body: { action: 'add' | 'waive' | 'restore' } & LabRequirementItem) =>
  request<{ therapy_id: string; required: string[]; missing_labs: string[]; in_tracker: boolean }>('POST', `/carerx/therapies/${therapyId}/lab-requirements`, body)
export const listEnrollmentRx = (patientId: string) =>
  request<{ patient: EnrollPatientBanner; items: EnrollRxRow[]; count: number; rx_start_date?: string | null }>('GET', `/carerx/enrollment/rx-list?patient_id=${patientId}`)

export const createCareRxEnrollment = (body: {
  patient_id: string; formulary_id?: string; drug_name?: string; therapy_id?: string;
  twin_verified?: boolean; override?: boolean; override_reason?: string;
  // D-CRX-42: site of care is required by the server (SC-1); facility per type config; rx traceability.
  site_of_care?: string; facility_id?: string; rx_number?: string;
  therapy_type?: string; priority?: string; consultation_needed?: boolean;
  labs?: { analyte: string; collected: boolean; collected_on?: string; no_results?: boolean; results_on?: string }[];
  /** D-CRX-95: labs added to / waived from this enrollment on the form; written with the create, or not at all. */
  lab_requirements?: { added?: LabRequirementItem[]; waived?: LabRequirementItem[] };
  // D-CRX-72 — per-patient adjustments to the drug's administration configuration.
  premed_override?: string; kit_override?: string; flush_override?: string; rate_rule_override?: string;
  // D-CRX-89 — a different library protocol for THIS patient (care-plan override ids + reason).
  protocol_override?: AdminOverrideBody['protocol_override'];
  // D-CRX-80 — height/weight the pharmacy measured, when PioneerRx carries none. Written to the
  // enrollment's mig-297 snapshot columns, never to dbo.patients.
  height_inches?: number; weight_ounces?: number; clear_height?: boolean; clear_weight?: boolean;
  // D-CRX-80 (mig 388) — ICD-10 entered at enrollment. Prints in §03; PioneerRx's projected
  // codes take precedence whenever it sends them.
  diagnosis_codes?: string; primary_diagnosis_code?: string;
  // Owner 2026-10-04: the enrollment's pharmacist (required; the server defaults to the creator when
  // they are one). Lands on carerx_therapy.assigned_rph_id — the same field Clinical Review assigns.
  assigned_rph_id?: string;
  // D-CRX-86 — the pregnancy radio, sent only when the operator changed it (screening UNIV-09 / UNIV-11).
  pregnancy_status?: string;
}) =>
  request<{ patient_id: string; therapy_id: string; pcw_id: string; pcw_existing: boolean; assigned_rph_id?: string | null;
            drug_name: string | null; therapy_archetype: string; requires_enrollment_signature: boolean;
            routing?: 'lab_tracking' | 'clinical_review'; missing_labs?: string[];
            pregnancy_screening?: PregnancyScreeningResult | null }>(
    'POST', '/carerx/enrollments', body)

export const generateCareRxPlan = (pcwId: string) =>
  request<{ pcw_id: string; assessment_id: string; status?: string; already_generated?: boolean; note?: string }>(
    'POST', `/carerx/pcw/${pcwId}/generate-plan`, {})
export const createCareRxPcw = (body: { patient_id: string; therapy_id?: string; assessment_id?: string }) =>
  request<{ id: string; existing: boolean }>('POST', '/carerx/pcw', body)
export const getCareRxPcw = (id: string) =>
  request<{ pcw: Record<string, any> }>('GET', `/carerx/pcw/${id}`)
export const updateCareRxPcw = (id: string, body: {
  status?: string; sections?: string; determination?: string
  pa_status?: string; pa_ref?: string; pa_expiry?: string; copay_assist?: string
  dispense_lot?: string; counsel_method?: string; mark_dispensed?: boolean
  care_goals?: string; accept_lock?: boolean; unlock?: boolean
}) => request<{ id: string; updated: boolean; determined: boolean }>('PUT', `/carerx/pcw/${id}`, body)
export const getCareRxPcwForPatient = (patientId: string, drug?: string) =>
  request<{ patient_id: string; drug: string; drug_info: DrugMatrixEntry | null; required_labs: PcwRequiredLab[]; gate_clear: boolean; pcw: Record<string, any> | null; alerts: PcwAlert[] }>(
    'GET', `/carerx/pcw/patient/${patientId}${drug ? `?drug=${encodeURIComponent(drug)}` : ''}`)
export const getCareRxAssessment = (id: string) =>
  request<{ assessment: Record<string, any> }>('GET', `/carerx/assessments/${id}`)
export const updateCareRxAssessment = (id: string, body: {
  status?: string; current_step?: number; payload?: string; drug_name?: string; diagnosis?: string
  visit_date?: string; prescriber_id?: string; assigned_nurse_id?: string; signature_status?: string
  baseline_labs?: CareRxLab[]
}) => request<{ id: string; updated: boolean; labs_upserted?: number }>('PUT', `/carerx/assessments/${id}`, body)

export const renderCareRxDocument = (assessmentId: string) =>
  request<{ document_id: string; content_hash: string; byte_size: number; stored: boolean; store_error?: string }>(
    'POST', `/carerx/assessments/${assessmentId}/document`)
export const listCareRxDocuments = (patientId: string) =>
  request<{ items: any[]; count: number }>('GET', `/carerx/patients/${patientId}/documents`)

// ── CareRx v2 therapy lanes (§10) + twin gate (§3.1) ──
export type CareRxTherapyLane = {
  id: string; state: string; is_terminal: boolean; cycle_no: number
  drug_name: string | null; formulary_id: string | null
  started_at: string | null; ended_at: string | null; discharge_reason: string | null; created_at: string
  dispensed?: number; authorized?: number | null; remaining?: number | null
  anchor_warning?: string | null  // UAT #3: persisted Rx anchor violation (mig 303)
  care_delivery_mode?: string | null
  rx_number?: string | null  // the prescription this enrollment is for (one enrollment per Rx)
}
// UAT 2026-07-29 #5: composed read for the DRAFT enrollment PDF (print view).
// The response shape lives in ./carerxEnrollment (E-05 freeze: no new exports here) and mirrors
// the C# DTOs in CareRxEnrollmentDraftDto.cs (E-19).
export const getCareRxEnrollmentDraft = (therapyId: string) =>
  request<EnrollmentDraft>('GET', `/carerx/therapies/${therapyId}/enrollment-draft`)
/**
 * QA of #2604 — the THERAPY-grain half of an enrollment edit.
 *
 * The enrollment door renders one component for create and for edit, so these controls appear on
 * both; only create ever persisted them. Therapy type, priority, consultation-needed, facility,
 * ICD-10 codes and the operator-measured height/weight were editable in the edit panel and dropped
 * on Save. This writes through the same statements the create path runs.
 *
 * Every field is optional and an omitted field is never a clear. Patient-grain extras (dose/sig,
 * outreach, notes, dates, city/zip, lab provider) still go to putCareRxEnrollment.
 */
export const saveCareRxEnrollmentIntake = (therapyId: string, body: {
  therapy_type?: string; priority?: string; consultation_needed?: boolean; facility_id?: string
  diagnosis_codes?: string; primary_diagnosis_code?: string
  height_inches?: number; weight_ounces?: number; clear_height?: boolean; clear_weight?: boolean
  pregnancy_status?: string   // D-CRX-86, as on create
}) => request<{ therapy_id: string; ok: boolean; pregnancy_screening?: PregnancyScreeningResult | null }>(
  'PUT', `/carerx/therapies/${therapyId}/enrollment-intake`, body)
// D-CRX-42 G-8: outreach 5-column model (therapy grain).
export interface OutreachLogRow { attempted_at: string; method: string | null; outcome: string | null; notes: string | null; source: string }
export interface OutreachState { therapy_id: string; status: string | null; outcome: string | null; callback_on: string | null; attempts: number; ltfu_floor: number; ltfu_candidate: boolean; log: OutreachLogRow[] }
export const getCareRxOutreach = (therapyId: string) =>
  request<OutreachState>('GET', `/carerx/therapies/${therapyId}/outreach`)
export const logCareRxOutreachAttempt = (therapyId: string, body: { method?: string; notes?: string }) =>
  request<{ logged: boolean }>('POST', `/carerx/therapies/${therapyId}/outreach/attempt`, body)
export const setCareRxOutreach = (therapyId: string, body: { status?: string; outcome?: string; callback_on?: string }) =>
  request<{ saved: boolean }>('PUT', `/carerx/therapies/${therapyId}/outreach`, body)
export const listCareRxOutreachWorklist = () =>
  request<{ items: any[]; count: number; ltfu_floor: number }>('GET', '/carerx/outreach/worklist')
// G-33/G-34: lab providers directory (Pharmacy Settings) + enrollment selector.
export interface LabProvider { id: string; name: string; contacts: string | null; website: string | null; address: string | null
  draw_walk_in: boolean; draw_scheduled: boolean; draw_clinic: boolean; draw_phlebotomist: boolean; draw_nurse: boolean; active: boolean }
export interface LabProviderBody { name: string; contacts?: string | null; website?: string | null; address?: string | null
  draw_walk_in?: boolean; draw_scheduled?: boolean; draw_clinic?: boolean; draw_phlebotomist?: boolean; draw_nurse?: boolean; active?: boolean }
export const listLabProviders = (all = false) =>
  request<{ items: LabProvider[]; count: number }>('GET', `/lab-providers${all ? '?all=1' : ''}`)
export const createLabProvider = (body: LabProviderBody) => request<{ id: string }>('POST', '/lab-providers', body)
export const updateLabProvider = (id: string, body: Partial<LabProviderBody>) => request<{ saved: boolean }>('PUT', `/lab-providers/${id}`, body)
// D-CRX-44: Lab Tracker (enrollment lab-violation flow).
export interface LabTrackerRow { therapy_id: string; patient_id: string; patient_name: string; drug_name: string | null
  rx_number: string | null; status: string; lab_provider_id: string | null; provider_name: string | null
  patient_outreach_status: string | null; patient_outreach_on: string | null
  provider_outreach_status: string; provider_outreach_on: string | null
  lab_count: number; open_count: number; draw_types: string | null; follow_up: string | null; next_action: string
  redraw_count: number
  /** ASP #28: staff's typed next action (beside the derived one) and the notes it is written from. */
  next_action_text?: string | null; next_action_by?: string | null; next_action_at?: string | null
  clinical_notes?: string | null; last_review_note?: string | null; last_review_by?: string | null; last_review_at?: string | null }
export interface LabTrackerLab { id: string; analyte: string; lab_company: string | null; draw_type: string | null
  draw_status: string; locked: boolean; redraw_reason: string | null; redraw_on: string | null; is_redraw_order: boolean
  // D-CRX-73 review: the tracker had no date of any kind. collected_on is the DRAW date,
  // results_on the date results came back — the one the 12-month currency clock runs from (R-3).
  collected_on: string | null; results_on: string | null; due_date: string | null }
export interface LabTrackerDetail { therapy_id: string; patient_id: string; drug_name: string | null; rx_number: string | null
  lab_provider_id: string | null; offered_draw_types: string[]; labs: LabTrackerLab[]
  patient_outreach_status: string | null; patient_outreach_on: string | null; follow_up: string | null }
// D-CRX-62 wiring (owner review 2026-08-13): the step-document endpoints existed API-only —
// no button could reach them. Generate returns the stored document id (open via openCareRxPdfDocument).
export const generateCareRxStepDoc = (therapyId: string, kind: 'clinical-review' | 'lab-status') =>
  request<{ document_id: string; kind: string; byte_size: number; store_error: string | null }>(
    'POST', `/carerx/therapies/${therapyId}/documents/${kind}`, {})
export const listLabTracker = () => request<{ items: LabTrackerRow[]; count: number; cap?: number; truncated?: boolean }>('GET', '/carerx/lab-tracker')
export const recomputeLabViolation = (therapyId: string, labs?: { analyte: string; collected: boolean; collected_on?: string; no_results?: boolean; results_on?: string }[]) =>
  request<{ therapy_id: string; missing_labs: string[]; in_tracker: boolean }>('POST', `/carerx/therapies/${therapyId}/recompute-labs`, { labs: labs ?? [] })
export const getLabTrackerDetail = (therapyId: string) => request<LabTrackerDetail>('GET', `/carerx/lab-tracker/${therapyId}`)
export const setLabTrackerProvider = (therapyId: string, labProviderId: string | null) =>
  request<{ saved: boolean }>('PUT', `/carerx/lab-tracker/${therapyId}/provider`, { lab_provider_id: labProviderId })
export const setLabTrackerProviderOutreach = (therapyId: string, body: { status?: string; outreach_on?: string }) =>
  request<{ saved: boolean }>('PUT', `/carerx/lab-tracker/${therapyId}/provider-outreach`, body)
export const setLabTrackerDates = (therapyId: string, body: { patient_outreach_status?: string; patient_outreach_on?: string; follow_up_on?: string }) =>
  request<{ saved: boolean }>('PUT', `/carerx/lab-tracker/${therapyId}/dates`, body)
export const updateLabTrackerLab = (labId: string, body: { lab_company?: string; draw_type?: string; draw_status?: string; collected_on?: string; results_on?: string }) =>
  request<{ saved: boolean }>('PUT', `/carerx/lab-tracker/labs/${labId}`, body)
export const bulkLabTrackerLabs = (body: { lab_ids: string[]; lab_company?: string; draw_type?: string; draw_status?: string; collected_on?: string; results_on?: string }) =>
  request<{ updated: number }>('PUT', '/carerx/lab-tracker/labs/bulk', body)
export const redrawLabTrackerLab = (labId: string, body: { reason: string; redraw_on: string }) =>
  request<{ locked_lab_id: string; new_order_id: string }>('POST', `/carerx/lab-tracker/labs/${labId}/redraw`, body)

// ── D-CRX-45 Clinical Review (therapy-grain, V2) ──
export interface ClinicalReviewRow {
  therapy_id: string; rx_number: string | null; patient_id: string; patient_name: string | null;
  drug_name: string | null; cr_status: string; determination: string; intake_notes: string | null;
  next_infusion: string | null; /** ASP 2026-10-11 (#8) */ deliver_on?: string | null; assigned_rph_id: string | null; assigned_at: string | null;
  priority: string; allergies: string | null; therapy_type: string | null; care_plan_type: string | null;
  rph_name: string | null;
  /** ASP #9: the patient's language name (English included); null when Pioneer has none (server-decided). */
  preferred_language?: string | null;
  // D-CRX-64 labs flag — computed server-side from the same two counts the approval gate uses,
  // so the row badge can never promise something Approve then refuses.
  labs_flag?: 'not_required' | 'missing' | 'partial' | 'complete';
  /** Open (unresolved) Screening & Hold rows for this therapy — a forwarded RX stays in this list. */
  open_holds?: number;
  required_analytes?: number; results_entered?: number; analytes_with_results?: number;
  /** ASP #25/#33: the enrollment's clinical notes and its latest review note (mig 436). */
  clinical_notes?: string | null; last_review_note?: string | null;
  last_review_by?: string | null; last_review_at?: string | null;
}
/** view 'decided' (ASP 2026-10-11 #15): finished reviews — approved and Active, or denied. */
/**
 * CareRx wave-1: the Clinical Review list's query. `view` picks the queue (omitted), the decided
 * archive, or the Conditional tile's set. Sort and the two date ranges run in SQL before the cap;
 * the server whitelists `sort` (anything else is the default priority-then-newest order).
 */
export interface ClinicalReviewListQuery {
  view?: 'decided' | 'conditional'
  sort?: 'next_infusion' | 'deliver_on'; dir?: 'asc' | 'desc'
  /** YYYY-MM-DD, inclusive. */
  next_infusion_from?: string; next_infusion_to?: string; deliver_on_from?: string; deliver_on_to?: string
}
export function clinicalReviewListQs(q: ClinicalReviewListQuery = {}): string {
  const p = new URLSearchParams()
  const keys = ['view', 'sort', 'dir', 'next_infusion_from', 'next_infusion_to', 'deliver_on_from', 'deliver_on_to'] as const
  for (const k of keys) {
    // A direction without a column means nothing to the server; never send it alone.
    if (k === 'dir' && !q.sort) continue
    const v = q[k]
    if (v) p.set(k, v)
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}
export const listClinicalReview = (q?: ClinicalReviewListQuery) =>
  request<{ items: ClinicalReviewRow[]; count: number; cap?: number; truncated?: boolean }>('GET', `/carerx/clinical-review${clinicalReviewListQs(q)}`)
export const setClinicalReviewStatus = (therapyId: string, status: string) =>
  request<{ ok: boolean; status: string }>('PUT', `/carerx/clinical-review/${therapyId}/status`, { status })
export const setClinicalReviewDetermination = (therapyId: string, determination: string, denial_reason?: string,
  extras?: { approve_without_results?: boolean; no_results_reason?: string }) =>
  request<{ ok: boolean; determination: string; routed: string }>('PUT', `/carerx/clinical-review/${therapyId}/determination`, { determination, denial_reason, ...(extras ?? {}) })
export const assignClinicalReview = (therapyId: string, body: { rph_id?: string; self?: boolean }) =>
  request<{ ok: boolean; assigned_rph_id: string }>('PUT', `/carerx/clinical-review/${therapyId}/assign`, body)
export const setClinicalReviewPriority = (therapyId: string, priority: string) =>
  request<{ ok: boolean; priority: string }>('PUT', `/carerx/clinical-review/${therapyId}/priority`, { priority })

// ── D-CRX-45 Phase 3 dial-in ──
export interface CrLabResult {
  id: string; analyte: string; value_num: number | null; value_text: string | null;
  pos_neg: string | null; comments: string | null; draw_date: string | null;
  results_date: string | null; within_12mo: boolean | null; reviewed_at: string | null;
}
export interface CrDetailTherapy {
  therapy_id: string; rx_number: string | null; drug_name: string | null; patient_id: string;
  patient_name: string | null; dob: string | null; allergies: string | null; city: string | null;
  cr_status: string; determination: string; priority: string; care_plan_type: string | null;
  assigned_rph_id: string | null; denial_reason: string | null; intake_notes: string | null;
  consultation_needed: boolean | null; next_infusion: string | null; dose_sig: string | null;
  rph_name: string | null;
  /** ASP #33: the enrollment's clinical notes (intake_notes above = screening-override justifications). */
  clinical_notes?: string | null;
}
export interface CrMatchedRule { rule_ref: string; condition: string; trigger: string | null; hold_decision: string | null; scope: string | null }
export const getClinicalReviewDetail = (therapyId: string) =>
  request<{ therapy: CrDetailTherapy; /** Patrick 2026-10-08: the therapy's ICD-10 and where it came from. */ diagnosis?: { codes: string; source: 'pioneer' | 'enrollment' | 'escript_note'; /** ASP #10: "L40.0 — Psoriasis vulgaris; …" (ICD-10-CM names). */ names?: string | null } | null; results: CrLabResult[]; required_analytes: string[]; screening_analytes?: string[] }>('GET', `/carerx/clinical-review/${therapyId}`)
export const saveClinicalReviewLabResult = (therapyId: string, body: { analyte: string; value_num?: number | null; value_text?: string; pos_neg?: string; comments?: string; draw_date?: string; results_date?: string; within_12mo?: boolean }) =>
  request<{ ok: boolean; matched_rule: CrMatchedRule | null }>('POST', `/carerx/clinical-review/${therapyId}/lab-results`, body)
/**
 * D-CRX-80 — record MANY results at once. A drug can require twenty analytes and results were one
 * request each, so a pharmacist working a panel made twenty round trips, every one a chance to be
 * interrupted and leave the therapy part-resulted. One transaction, all or nothing; `positives`
 * comes back so the caller can raise the CR-V5 screening prompt for all of them together.
 */
export const saveCrLabResultsBulk = (
  therapyId: string,
  results: { analyte: string; value_num?: number | null; value_text?: string; pos_neg?: string; comments?: string; draw_date?: string; results_date?: string }[],
) =>
  request<{ ok: boolean; saved: number; positives: string[] }>(
    'POST', `/carerx/clinical-review/${therapyId}/lab-results/bulk`, { results })
// `manual: true` is the pharmacist forwarding on their own judgement with no rule match behind
// it — `reason` is then required and becomes the hold's decision text (source = 'manual').
export const clinicalReviewScreenDecision = (therapyId: string, body: { rule_ref?: string; analyte?: string; trigger?: string; hold_decision?: string; move_to_screening: boolean; override_reason?: string; manual?: boolean; reason?: string }) =>
  request<{ ok: boolean; moved: boolean; override_recorded?: boolean; source?: string }>('POST', `/carerx/clinical-review/${therapyId}/screen-decision`, body)
export const clinicalReviewRedraw = (therapyId: string, body: { analyte: string; reason: string; redraw_on?: string }) =>
  request<{ ok: boolean; locked_lab_id: string; new_order_id: string; returned_to_tracker: boolean }>('POST', `/carerx/clinical-review/${therapyId}/redraw`, body)

// ── D-CRX-45 Phase 4 Screening & Hold (own tab) ──
// One HOLD event. Several can sit on the same enrollment — three positive analytes are three
// holds, not three RXs to work.
export interface ScreeningHoldEvent {
  id: string; rule_ref: string | null; trigger_text: string | null; result_text: string | null
  hold_decision: string | null; source: string | null; status: string
  resolution: string | null; created_at: string | null
}
// One row of the Hold tab = one THERAPY, with every hold it carries listed inside it.
export interface ScreeningHoldRow {
  id: string | null; therapy_id: string; rx_number: string | null; patient_name: string | null;
  drug_name: string | null; status: string
  open_holds: number; hold_count: number; held_since: string | null
  holds: ScreeningHoldEvent[]
  /** ASP #9: the patient's language name (English included); null when Pioneer has none (server-decided). */
  preferred_language?: string | null
  /** ASP #22: the pharmacist's follow-up date (YYYY-MM-DD) on this therapy's open holds. */
  follow_up_on?: string | null
}
// D-CRX-71 — non-lab screening (Clinical Review). One evaluation, server-side: the panel renders
// exactly what the determination gate enforces.
// D-CRX-72 — the drug record's administration configuration, shown at enrollment.
export interface EnrollmentAdminConfig {
  formulary_id: string; drug_name: string | null
  protocols: { premed: string | null; kit: string | null; observation_minutes: number | null } | null
  flush: { protocol: string | null; constraints: string | null } | null
  rate_rule: {
    model: string | null
    first: { start: number | null; increment: number | null; interval_min: number | null; max: number | null } | null
    subsequent: { start: number | null; increment: number | null; interval_min: number | null; max: number | null } | null
    diluent: string | null; standard_final_volume_ml: number | null; infusion_duration_min: number | null
  } | null
  /** Present only when a therapy_id was asked for: what was already adjusted for this patient. */
  overrides: AdminOverrideBody | null
  /** D-CRX-89: the drug's protocol presets (ids). */
  protocol_ids?: { premed: string | null; kit: string | null; flush: string | null }
  /** D-CRX-89: this patient's protocols (pinned versions or staff choices: ids + printed text); null = drug presets. */
  protocol_overrides?: {
    premed_protocol_id: string | null; kit_protocol_id: string | null; flush_protocol_id: string | null
    premed: string | null; kit: string | null; flush: string | null
  } | null
  /** D-CRX-89 round 2: each slot's pinned version vs the latest (only with a therapy_id). */
  protocol_slots?: import('./carerxProtocols').EnrollmentProtocolSlot[] | null
}
export interface AdminOverrideBody {
  premed_override?: string | null; kit_override?: string | null
  flush_override?: string | null; rate_rule_override?: string | null
  /** D-CRX-89: absent = untouched; present = the full choice for the three slots (null = the drug preset). */
  protocol_override?: { premed_protocol_id: string | null; kit_protocol_id: string | null; flush_protocol_id: string | null; reason: string
    /** 'choice' (a different protocol) or 'update_latest' (same protocol, newest version) — the audit tells them apart. */
    kind?: 'choice' | 'update_latest' }
}
export const getEnrollmentAdminConfig = (formularyId: string, therapyId?: string) =>
  request<EnrollmentAdminConfig>('GET',
    `/carerx/enrollment/admin-config/${formularyId}${therapyId ? `?therapy_id=${therapyId}` : ''}`)
export const saveEnrollmentAdminConfig = (therapyId: string, body: AdminOverrideBody) =>
  request<{ ok: boolean }>('PUT', `/carerx/enrollment/admin-config/therapy/${therapyId}`, body)

export interface ScreeningFinding {
  rule_id: string; rule_ref: string; domain: string; severity: 'HARD_HOLD' | 'SOFT_HOLD' | 'ADVISORY'
  prompt_text: string; remediation_text: string | null; citation: string
  state: 'MET' | 'UNMET' | 'UNANSWERED' | 'NOTEVALUABLE' | 'OVERRIDDEN' | 'NOTAPPLICABLE'
  detail: string | null; blocks: boolean; answer: string | null
}
export interface ScreeningEvaluation {
  therapy_id: string
  /** false = shadow mode: findings are information, nothing blocks an approval yet. */
  gate_enforced: boolean
  is_initiation: boolean; rule_count: number
  /** false = the therapy's drug has no Drug Reference link, so no rule can resolve at all. */
  drug_linked: boolean; drug_category: string | null
  hard_blocked: boolean; soft_blocked: boolean; advisories: number
  findings: ScreeningFinding[]
}
export const getScreeningEvaluation = (therapyId: string) =>
  request<ScreeningEvaluation>('GET', `/carerx/screening/${therapyId}`)
export const answerScreeningRule = (therapyId: string, body: {
  rule_id: string; answer: 'YES' | 'NO' | 'UNKNOWN' | 'NA'; note?: string
  override?: boolean; override_reason?: string
}) => request<{ ok: boolean }>('POST', `/carerx/screening/${therapyId}/answer`, body)

export interface ScreeningRule { rule_ref: string; condition: string; analyte: string | null; trigger: string | null; hold_decision: string | null; scope: string | null; active: boolean }
export const listScreeningHolds = () =>
  request<{ items: ScreeningHoldRow[]; count: number; cap?: number; truncated?: boolean }>('GET', '/carerx/screening-holds')
export const getScreeningRules = () =>
  request<{ rules: ScreeningRule[]; count: number }>('GET', '/carerx/screening-holds/rules')
export const resolveScreeningHold = (holdId: string, body: { resolution?: string; status?: string; prescriber_notified?: boolean }) =>
  request<{ ok: boolean; status: string; returned_to_review: boolean }>('PUT', `/carerx/screening-holds/${holdId}/resolve`, body)

// ── D-CRX-45 Phase 5 AI panels (CR-E9 Consultation / CR-E10 Care Plan) ──
export interface Consultation {
  id: string; person_consulted: string | null; caregiver_rel: string | null; status: string | null;
  follow_up_on: string | null; decline_reason: string | null; note: string | null;
  note_provenance: string | null; options_json: string | null; completed_at: string | null;
}
export const getConsultation = (therapyId: string) =>
  request<{ consultation: Consultation | null }>('GET', `/carerx/consultation/${therapyId}`)
export const saveConsultation = (therapyId: string, body: { person_consulted?: string; caregiver_rel?: string; status?: string; follow_up_on?: string; decline_reason?: string; note?: string; note_provenance?: string; options_json?: string; attested?: boolean }) =>
  request<{ ok: boolean }>('POST', `/carerx/consultation/${therapyId}`, body)

export interface CarePlanGoalRec {
  id?: string; horizon: string | null; domain: string | null; goal_statement: string | null;
  measure: string | null; unit: string | null; direction: string | null; baseline_value: string | null;
  baseline_date: string | null; target_value: string | null; target_date: string | null;
  status: string | null; status_evidence: string | null; provenance: string | null;
  /** ASP #14: the saved baseline waiver (carerx_care_plan_goals.data_source_json), stamped server-side. */
  baseline_waiver?: CarePlanBaselineWaiver | null;
}
export interface CarePlanBaselineWaiver { reason: string; by?: string | null; by_name?: string | null; at?: string | null }
export interface CarePlanRec { id: string; version: number; plan_type: string | null; assessment_anchor: string | null; next_reassessment: string | null; status: string | null; accepted_at: string | null }
// D-CRX-60: grounded clinical suggestions — care-plan goals + consultation counseling points,
// derived from the therapy's drug record and assigned protocols (each carries its basis).
export interface ClinicalSuggestion {
  horizon?: string; domain?: string; goal_statement?: string; measure?: string; unit?: string
  direction?: string; target_value?: string | null
  topic?: string; text?: string
  basis: string; why?: string; source: string
}
export const suggestCarePlanGoals = (therapyId: string) =>
  request<{ therapy_id: string; drug: string | null; count: number; suggestions: ClinicalSuggestion[]; generation: string; note: string }>(
    'GET', `/carerx/care-plan/${therapyId}/suggest-goals`)
export const suggestConsultationPoints = (therapyId: string) =>
  request<{ therapy_id: string; drug: string | null; count: number; suggestions: ClinicalSuggestion[]; generation: string; note: string }>(
    'GET', `/carerx/consultation/${therapyId}/suggest-points`)
export const getCarePlanE10 = (therapyId: string) =>
  request<{ plan: CarePlanRec | null; goals: CarePlanGoalRec[] }>('GET', `/carerx/care-plan/${therapyId}`)
// ASP #14: the server derives the baseline exemption from direction + domain + baseline_value; a
// waiver counts only with a reason, keyed by the goal's id in the same request (its index on save).
export interface CarePlanEvalGoal { id?: string; direction?: string; domain?: string; baseline_value?: string; target_value?: number | null; target_date?: string; values?: { value: number | null; date: string }[] }
export interface CarePlanGapWaiver { goal_id: string; reason: string }
export const evaluateCarePlan = (therapyId: string, body: { goals: CarePlanEvalGoal[]; waivers?: CarePlanGapWaiver[] }) =>
  request<{ goals: { id: string; eligible: string[]; derived_status: string }[]; gaps: { goalId: string; reason: string }[]; save_blocked: boolean }>('POST', `/carerx/care-plan/${therapyId}/evaluate`, body)
export const saveCarePlanE10 = (therapyId: string, body: { plan_type?: string; assessment_anchor?: string; next_reassessment?: string; goals: Record<string, unknown>[]; waivers?: CarePlanGapWaiver[]; attested?: boolean }) =>
  request<{ ok: boolean; plan_id: string; version: number; auto_assigned?: boolean; kept_assignee?: boolean }>('POST', `/carerx/care-plan/${therapyId}`, body)




export const listCareRxTherapies = (patientId: string) =>
  request<{ patient_id: string; therapies: CareRxTherapyLane[] }>('GET', `/carerx/patients/${patientId}/therapies`)

export type CareRxTwin = { id: string; display_name: string | null; mrn: string | null; date_of_birth: string }
export const getCareRxTwins = (patientId: string) =>
  request<{ patient_id: string; collision: boolean; twins: CareRxTwin[] }>('GET', `/carerx/patients/${patientId}/twins`)

export const listCareRxTherapyNotes = (therapyId: string) => request<{ items: { id: string; lane: string; body: string; created_at: string }[]; count: number }>('GET', `/carerx/therapies/${therapyId}/notes`)
export const addCareRxTherapyNote = (therapyId: string, lane: string, body: string) => request<{ id: string }>('POST', `/carerx/therapies/${therapyId}/notes`, { lane, body })
export const getCareRxDispenseLane = () => request<{ items: any[]; count: number }>('GET', '/carerx/dispense-lane')
export const getCareRxNeedsClassification = () => request<{ items: any[]; unconfirmed: number; confirmed: number; total: number }>('GET', '/carerx/drug-list/needs-classification')
export const classifyCareRxDrugs = (formulary_ids: string[], disposition: string) => request<{ applied: number }>('POST', '/carerx/drug-list/classify', { formulary_ids, disposition })
export const getCareRxStaffingBoard = () => request<{ items: any[]; count: number }>('GET', '/carerx/staffing-board')
export const getCareRxStaffingSuggestions = (visitId: string) => request<{ items: any[] }>('GET', `/carerx/visits/${visitId}/staffing-suggestions`)
// D-CRX-57 — nurse coverage areas (POT staffing soft-priority)
export interface CoverageAreaRow { id: string; name: string; zone: string | null; counties: string | null; coverage_status: string; zip_count: number; nurses: string | null; active_patients: number }
export const listCareRxCoverageAreas = () => request<{ items: CoverageAreaRow[] }>('GET', '/carerx/coverage-areas')
// staff: `override`+`override_reason` staff past open blockers (audited carerx.staffing.blocker_override);
// `territory_override` records an out-of-area assign. A 422 {error:'blockers_open', blockers:[...]} asks for them.
export const updateCareRxStaffing = (visitId: string, body: {
  action: string; nurse_id?: string; category?: string; notes?: string; reason?: string; visit_date?: string
  override?: boolean; override_reason?: string; territory_override?: boolean
}) => request<{ action: string }>('PUT', `/carerx/visits/${visitId}/staffing`, body)
export const getCareRxHistory360 = (therapyId: string) => request<{ fills: { items: any[]; dispensed: number; authorized: number | null; remaining: number | null }; holds: { items: any[]; count: number } }>('GET', `/carerx/therapies/${therapyId}/history360`)
export const getCareRxDocChecklist = (therapyId: string) => request<{ items: { key: string; label: string; status: string; at: string | null }[] }>('GET', `/carerx/therapies/${therapyId}/doc-checklist`)
export const markCareRxWelcomePacket = (therapyId: string, delivered_on: string) => request<{ delivered_on: string }>('PUT', `/carerx/therapies/${therapyId}/doc-checklist/welcome-packet`, { delivered_on })
export const listCareRxDoses = (therapyId: string) => request<{ items: { dose_no: number; dosed_at: string; source: string }[]; count: number }>('GET', `/carerx/therapies/${therapyId}/doses`)
export const addCareRxDose = (therapyId: string, dosed_at: string) => request<{ dose_no: number; reanchored: number }>('POST', `/carerx/therapies/${therapyId}/doses`, { dosed_at, source: 'manual' })
export const getCareRxCarePlan = (therapyId: string) => request<{ latest: any; previous?: any; versions: any[]; goals: any[]; cadence: any[] }>('GET', `/carerx/therapies/${therapyId}/careplan`)
export const createCareRxCarePlan = (therapyId: string) => request<{ id: string; version: number; status: string; existing: boolean }>('POST', `/carerx/therapies/${therapyId}/careplan`, {})
export const updateCareRxCarePlan = (id: string, body: { problem_list?: any; interventions?: any; fills_authorized?: number; days_supply?: number; education_needs?: string; adr_history?: string }) => request<{ id: string }>('PUT', `/carerx/careplans/${id}`, body)
export const signCareRxCarePlan = (id: string) => request<{ id: string; status: string; cadence_spawned: number; auto_assigned?: boolean; kept_assignee?: boolean }>('POST', `/carerx/careplans/${id}/sign`, {})
export const addCareRxCarePlanGoal = (id: string, body: { horizon: string; goal_text: string; measurable_target?: string; target_date?: string }) => request<{ id: string }>('POST', `/carerx/careplans/${id}/goals`, body)
export const updateCareRxCarePlanGoal = (id: string, body: { status?: string; review_date?: string }) => request<{ id: string }>('PUT', `/carerx/careplan-goals/${id}`, body)
export const updateCareRxCadenceTask = (id: string, body: { status: string; notes?: string }) => request<{ id: string }>('PUT', `/carerx/cadence-tasks/${id}`, body)
export const switchCareRxPathway = (therapyId: string, body: { mode: string; reason: string; scope?: string }) =>
  request<{ id: string; old_mode: string | null; new_mode: string; re_consent_required: boolean; note: string | null }>(
    'PUT', `/carerx/therapies/${therapyId}/pathway`, body)
export const transitionCareRxTherapy = (therapyId: string, body: { state: string; reason?: string }) =>
  request<{ id: string; from: string; state: string; is_terminal: boolean; discharge_doc_id?: string | null }>(
    'PUT', `/carerx/therapies/${therapyId}/transition`, body)

export const getCareRxSignatureStatus = (assessmentId: string) =>
  request<{ configured: boolean; envelope: any }>('GET', `/carerx/assessments/${assessmentId}/signature-status`)
export const sendCareRxForSignature = (assessmentId: string, body: { recipient_name: string; recipient_email: string }) =>
  request<any>('POST', `/carerx/assessments/${assessmentId}/send-for-signature`, body)
// D-CRX-47 POT-1: send the POT (Plan of Treatment) document to the physician for signature.
export const sendCareRxPotForSignature = (documentId: string, body: { recipient_email: string; recipient_name?: string; replace_envelope_id?: string }) =>
  request<{ envelope_record: string; envelope_id: string; status: string; subject_type: string; visit_id: string | null; recipient_name: string }>(
    'POST', `/carerx/documents/${documentId}/send-signature`, body)

// D-CRX-59: the per-Plan-of-Treatment document tracker — enrollment / consent / POT lanes,
// each Draft → Ready to send → Sent → Signed. Drafts visible from therapy birth.
export interface CareRxDocTrackerRow {
  consent_status: string; obtained_at: string | null; therapy_id: string; patient_id: string
  patient_name: string | null; patient_email: string | null; drug_name: string | null; therapy_state: string | null
  envelope_id: string | null; envelope_status: string | null; recipient_name: string | null; recipient_email: string | null
  sent_at: string | null; signed_at: string | null
  enrollment_status: string | null; enrollment_lane: string; enrollment_envelope_id: string | null
  enrollment_sent_at: string | null; enrollment_signed_at: string | null
  prescriber_name: string | null; prescriber_email: string | null; prescriber_id: string | null
  // consent_lane is DERIVED SERVER-SIDE (D-CRX-73 review). It used to be recomputed in the browser
  // from consent_status + envelope_status; once the dashboard tiles had to count every matching row
  // rather than the loaded page, a second copy of that rule was how a tile would start disagreeing
  // with the chip beneath it. One derivation, in SQL.
  consent_lane: string
  pot_lane: string; open_visit_status: string | null; pot_form_status: string | null; pot_document_id: string | null
  pot_envelope_id: string | null; pot_sent_at: string | null; pot_signed_at: string | null
  enrollment_signed_doc_id: string | null; consent_signed_doc_id: string | null; pot_signed_doc_id: string | null
  // D-ESIGN-1 O3: set only on an in-app enrollment envelope in process — its signed-so-far copy + who it waits on.
  enrollment_progress?: import('./inappEsign').InAppLaneProgress | null
}
// Open an authenticated PDF (documents need the Bearer token, so a plain href can't work):
// fetch → blob → object URL. Owner tester pass 2026-08-14: pass a window claimed SYNCHRONOUSLY
// in the click gesture — window.open after an await is eaten by popup blockers.
export const openCareRxPdfDocument = async (docId: string, target?: Window | null) => {
  const res = await apiFetch(`/carerx/documents/${docId}`)
  if (!res.ok) { target?.close(); throw new Error(`document fetch failed (${res.status})`) }
  const url = URL.createObjectURL(await res.blob())
  if (target) target.location.href = url
  else window.open(url, '_blank')
}
// REMOVED (Mario 2026-09-02): openCareRxTemplatePdf streamed the ACTIVE UPLOADED BLOB from
// esign_doc_templates via /carerx/doc-templates/{kind}/pdf. Its only caller was the Consent tab's
// document tracker, which therefore showed a document that was neither this patient's nor this
// tenant's — the exact mechanism D-CRX-63 (generated consent) and D-CRX-67 (spec-driven enrollment
// form) replaced, and a `template_missing` 404 on any tenant with nothing uploaded. Every CareRx
// surface now opens the generated therapy document through pages/carerx/therapyDocuments. The
// endpoint still exists for the e-sign template admin; there is deliberately no client helper that
// puts an uploaded blob in front of staff as if it were the patient's document.
// D-CRX-63: the generated, tenant-branded Patient Consent for a therapy. There is no template
// file to pick — the document is composed from the tenant's own branding, its licensing state's
// clause pack, and the therapy's data, so any pharmacy gets its own consent with nothing uploaded.
// The tab is opened SYNCHRONOUSLY inside the click gesture: a window.open after the fetch resolves
// is popup-blocked silently, which reads to the operator as "the button does nothing".
export const openCareRxConsentPdf = async (therapyId: string, opts?: { final?: boolean; target?: Window | null }) => {
  // `target`: a tab a patient-grain caller already claimed in its own click gesture, because it had
  // to resolve which therapy the consent is for before it could ask for one.
  const w = opts?.target ?? window.open('', '_blank')
  if (!w) throw new Error('Pop-up blocked - allow pop-ups for this site to view the consent PDF.')
  w.document.write('<title>Consent PDF...</title><p style="font-family:sans-serif;padding:24px">Generating the consent form...</p>')
  try {
    const res = await apiFetch(`/carerx/therapies/${therapyId}/consent-document${opts?.final ? '?final=1' : ''}`)
    if (!res.ok) throw new Error(`consent render failed (${res.status})`)
    w.location.replace(URL.createObjectURL(await res.blob()))
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Consent render failed'
    w.document.body.innerHTML = `<p style="font-family:sans-serif;color:#b91c1c;padding:24px">${msg.replace(/</g, '&lt;')}</p>`
    throw err
  }
}
/** File the generated consent on the patient's chart (blob + carerx_documents row). */
/**
 * D-CRX-80 — the generated enrollment form. Deliberately identical in shape to
 * openCareRxConsentPdf, because CareRx now has ONE way to produce a PDF: the server composes and
 * renders it, the browser just opens the bytes.
 *
 * Replaces the pdf-lib AcroForm fill, which shipped a ~200 KB template to the browser, could only
 * print three baseline-lab rows whatever the drug required, and produced nothing the e-sign lane
 * could attach — which is why DocuSign fell back to sending a blank uploaded template.
 */
export const openCareRxEnrollmentPdf = async (therapyId: string, opts?: { final?: boolean; target?: Window | null }) => {
  const w = opts?.target ?? window.open('', '_blank')
  if (!w) throw new Error('Pop-up blocked - allow pop-ups for this site to view the enrollment form.')
  w.document.write('<title>Enrollment form...</title><p style="font-family:sans-serif;padding:24px">Generating the enrollment form...</p>')
  try {
    const res = await apiFetch(`/carerx/therapies/${therapyId}/enrollment-document${opts?.final ? '?final=1' : ''}`)
    if (!res.ok) throw new Error(`enrollment render failed (${res.status})`)
    w.location.replace(URL.createObjectURL(await res.blob()))
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Enrollment render failed'
    w.document.body.innerHTML = `<p style="font-family:sans-serif;color:#b91c1c;padding:24px">${msg.replace(/</g, '&lt;')}</p>`
    throw err
  }
}
/** File the generated enrollment on the patient's chart (blob + carerx_documents row). */
/**
 * D-CRX-80 (mig 386) — SOFT-delete an enrollment. Manager and above
 * (`carerx.enrollment.delete`).
 *
 * Nothing is destroyed: the therapy, its labs, holds, documents and any signed envelope are
 * retained for the clinical and HIPAA record — a therapy anchors all of them and mig 313's holds
 * FK cascades. The row is marked with an actor, a timestamp and a REQUIRED reason, and leaves the
 * queues. The same patient can be enrolled on the same drug again afterwards.
 */
/**
 * A one-page sample of THIS pharmacy's document letterhead, for Branding settings.
 *
 * Replaces the client-side previewEnrollmentFormPdf, which needed pdf-lib and the embedded AcroForm
 * template. Same server renderer as the real consent, so what an owner checks here is what a
 * patient receives - the old preview could drift from the document it claimed to preview.
 */
export const openCareRxLetterheadPreview = async (target?: Window | null) => {
  const w = target ?? window.open('', '_blank')
  if (!w) throw new Error('Pop-up blocked - allow pop-ups for this site to preview the letterhead.')
  w.document.write('<title>Letterhead preview...</title><p style="font-family:sans-serif;padding:24px">Building the preview...</p>')
  try {
    const res = await apiFetch('/carerx/letterhead-preview')
    if (!res.ok) throw new Error(`letterhead preview failed (${res.status})`)
    w.location.replace(URL.createObjectURL(await res.blob()))
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Letterhead preview failed'
    try { w.document.body.innerHTML = `<p style="font-family:sans-serif;padding:24px;color:#b91c1c">${msg}</p>` } catch { /* tab closed */ }
    throw err
  }
}

export const deleteCareRxEnrollment = (therapyId: string, reason: string) =>
  request<{ deleted: boolean; therapy_id: string; patient_id: string; reason: string;
            had_signed_envelope: boolean; note: string
            // Go-live #2/#3: the open work the delete closed, and any envelope DocuSign would not void.
            visits_cancelled: number; envelopes_voided: number
            envelopes_not_voided: { envelope_id: string; subject_type: string; error: string }[] }>(
    'POST', `/carerx/therapies/${therapyId}/delete`, { reason })
export const storeCareRxEnrollmentDocument = (therapyId: string) =>
  request<{ document_id: string; kind: string; byte_size: number; content_hash: string; render_ms: number; lab_rows: number; unmapped: number; store_error: string | null }>(
    'POST', `/carerx/therapies/${therapyId}/documents/enrollment`, {})
export const storeCareRxConsentDocument = (therapyId: string) =>
  request<{ document_id: string; kind: string; byte_size: number; content_hash: string; render_ms: number; store_error: string | null }>(
    'POST', `/carerx/therapies/${therapyId}/documents/consent`, {})
export const fetchSignedCareRxEsign = (envelopeId: string) =>
  request<{ document_id: string; envelope_id: string; kind: string }>('POST', `/carerx/esign/envelopes/${envelopeId}/fetch-signed`, {})
/** The dashboard tiles, counted over every row matching the search — not just the loaded page. Each
 *  counts PLANS (rows) — the set the tile's click lists (CareRx wave-1 #2). */
export interface CareRxDocTrackerSummary {
  drafts: number; ready_to_send: number; awaiting_signature: number; fully_signed: number
  /** Plans the funnels allow, before the tile filter (wave-1 #2). */
  plans?: number
}
export const listCareRxDocTracker = (opts?: { q?: string; page?: number; page_size?: number; filter?: string[]
  /** CareRx wave-1 #2: a dashboard tile's set (server whitelist); narrows the list, never the summary. */
  tile?: 'draft' | 'ready_to_send' | 'sent' | 'fully_signed' }) => {
  const p = new URLSearchParams()
  if (opts?.q) p.set('q', opts.q)
  if (opts?.page) p.set('page', String(opts.page))
  if (opts?.page_size) p.set('page_size', String(opts.page_size))
  for (const f of opts?.filter ?? []) if (f) p.append('filter', f)
  if (opts?.tile) p.set('tile', opts.tile)
  const qs = p.toString()
  return request<{
    items: CareRxDocTrackerRow[]; count: number; total: number | null
    page: number; page_size: number; total_pages: number | null; truncated: boolean
    summary: CareRxDocTrackerSummary
  }>('GET', `/carerx/consents/esign${qs ? `?${qs}` : ''}`)
}
export const sendCareRxConsentEsign = (therapyId: string, body?: { patient_email?: string; interpreter_email?: string }) =>
  request<{ envelope_id: string; status: string }>('POST', `/carerx/therapies/${therapyId}/consent-send`, body ?? {})
export const resendCareRxConsentEsign = (envelopeId: string, body?: { patient_email?: string; interpreter_email?: string }) =>
  request<{ envelope_id: string; status: string; voided: string }>('POST', `/carerx/consents/esign/${envelopeId}/resend`, body ?? {})
// replace_envelope_id = Resend (go-live #11): the open envelope is voided only once the new send has
// passed every check, immediately before it goes out.
export const sendCareRxEnrollmentEsign = (therapyId: string, body?: { provider_email?: string; provider_name?: string; pharmacist_email?: string; pharmacist_name?: string; provider_first?: boolean; replace_envelope_id?: string }) =>
  request<{ envelope_id: string; status: string; recipients: string[]; voided?: string | null }>('POST', `/carerx/therapies/${therapyId}/enrollment-send`, body ?? {})

// CareRx Module 4 — screening + Hold & Protocol
export const listCareRxScreenings = (patientId: string) =>
  request<{ patient_id: string; items: any[] }>('GET', `/carerx/patients/${patientId}/screenings`)
export const upsertCareRxScreenings = (patientId: string, screenings: Array<{ analyte: string; result?: string; result_date?: string; notes?: string }>) =>
  request<{ patient_id: string; upserted: number }>('POST', `/carerx/patients/${patientId}/screenings`, { screenings })
export const getCareRxHold = (patientId: string) =>
  request<{ patient_id: string; hold: any | null; on_hold: boolean }>('GET', `/carerx/patients/${patientId}/hold`)
export const openCareRxHold = (patientId: string, body: { reason?: string; trigger_analyte?: string; protocol?: string; monitoring?: string; reinitiation_criteria?: string }) =>
  request<{ patient_id: string; hold: any }>('POST', `/carerx/patients/${patientId}/hold`, body)
export const updateCareRxHold = (holdId: string, body: { action?: 'clear'; protocol?: string; monitoring?: string; reinitiation_criteria?: string; notes?: string }) =>
  request<{ patient_id: string; hold_id: string; hold: any }>('PUT', `/carerx/holds/${holdId}`, body)
// CareRx Module 6 — consent-to-treat + fill/shipment
export const listCareRxConsents = (patientId: string) =>
  request<{ patient_id: string; items: any[] }>('GET', `/carerx/patients/${patientId}/consents`)
export const upsertCareRxConsents = (patientId: string, consents: Array<{ consent_type: string; status?: string; method?: string; expires_at?: string; notes?: string }>) =>
  request<{ patient_id: string; upserted: number }>('POST', `/carerx/patients/${patientId}/consents`, { consents })
export const updateCareRxConsent = (consentId: string, body: { action?: 'revoke'; status?: string; method?: string; notes?: string }) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/consents/${consentId}`, body)
export interface CareRxConsentWorklistRow { id: string; patient_id: string; patient_name: string | null; mrn: string | null; consent_type: string; status: string; method: string | null; expires_at: string | null }
export interface CareRxFillWorklistRow { id: string; patient_id: string; patient_name: string | null; mrn: string | null; drug: string | null; fill_status: string; carrier: string | null; tracking_number: string | null }
export interface CareRxAwaitingRow { id: string; envelope_id: string; subject_type: string; subject_id: string | null; status: string; recipient_name: string | null; recipient_email: string | null; sent_at: string | null; created_at: string | null; days_out: number; aging: boolean }
export const listCareRxEsignAwaiting = () => request<{ items: CareRxAwaitingRow[]; count: number }>('GET', '/carerx/esign/awaiting')
// Go-live #10: a nudge is DocuSign's reminder on the SAME envelope (any document type) — it used to
// re-post the assessment send without a recipient, which the server refused every time.
export const remindCareRxEsign = (envelopeId: string) =>
  request<{ envelope_id: string; status: string; reminded: boolean }>('POST', `/carerx/esign/envelopes/${envelopeId}/remind`, {})
export const listCareRxFulfillmentWorklist = () =>
  request<{ consents: CareRxConsentWorklistRow[]; fills: CareRxFillWorklistRow[]; count: number }>('GET', '/carerx/fulfillment/worklist')

export const listCareRxFills = (patientId: string) =>
  request<{ patient_id: string; items: any[] }>('GET', `/carerx/patients/${patientId}/fills`)
export const createCareRxFill = (patientId: string, body: { drug?: string; quantity?: string; lot?: string; ndc?: string; notes?: string }) =>
  request<{ id: string; patient_id: string }>('POST', `/carerx/patients/${patientId}/fills`, body)
export const updateCareRxFill = (fillId: string, body: { action?: 'confirm' | 'ship' | 'deliver' | 'cancel'; fill_status?: string; drug?: string; quantity?: string; lot?: string; ndc?: string; carrier?: string; tracking_number?: string; notes?: string }) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/fills/${fillId}`, body)

export const getCareRxScreeningGate = (patientId: string, drug?: string) =>
  request<{ patient_id: string; drug?: string; required_screenings: any[]; positives: string[]; missing: string[]; hold_recommended: boolean; on_hold: boolean; gate_clear: boolean }>('GET', `/carerx/patients/${patientId}/screening-gate${drug ? `?drug=${encodeURIComponent(drug)}` : ''}`)

export const reviewCareRxEnrollment = (patientId: string, body: { action: 'approve' | 'reject' | 'request_changes'; notes?: string }) =>
  request<{ patient_id: string; enrollment: Record<string, any> }>('PUT', `/carerx/patients/${patientId}/enrollment/review`, body)
export const searchCareRxCommonMeds = (q: string) => request<{ items: { name: string; rxnorm_cui: string | null }[] }>('GET', `/carerx/common-meds?q=${encodeURIComponent(q)}`)
export const addCareRxHomeMed = (patientId: string, body: { kind: 'med' | 'nkda' | 'none_reported'; drug_name?: string; rxnorm_cui?: string }) =>
  request<{ id?: string }>('POST', `/carerx/patients/${patientId}/home-meds`, body)
export const getCareRxEnrollment = (patientId: string) =>
  request<{ patient_id: string; enrollment: Record<string, any> | null }>('GET', `/carerx/patients/${patientId}/enrollment`)
export const putCareRxEnrollment = (patientId: string, body: { enrollment_status?: string; assigned_nurse_id?: string; episode?: string; review_status?: string; intake_notes?: string; ins_payer?: string; ins_plan?: string; ins_member_id?: string; ins_group?: string; ins_bin?: string; ins_pcn?: string; ins_coverage_type?: string; home_meds?: string; dose_sig?: string; pa_followup_at?: string; outreach_status?: string; next_infusion_at?: string; deliver_on?: string; city?: string; zip?: string; lab_provider_id?: string }) =>
  request<{ patient_id: string; enrollment: Record<string, any> }>('PUT', `/carerx/patients/${patientId}/enrollment`, body)
export interface CareRxComplaint { id: string; kind: string; occurred_on: string; description: string; investigation: string | null; resolution: string | null; trend_category: string | null; status: string; opened_at: string; resolved_at: string | null; patient_name: string | null }
export const getCareRxGovernance = () => request<any>('GET', '/carerx/governance')
export const getCareRxQapiReport = (quarter?: string) => request<any>('GET', `/carerx/qapi-report${quarter ? '?quarter=' + quarter : ''}`)
export const listCareRxComplaints = () => request<{ items: CareRxComplaint[]; count: number }>('GET', '/carerx/complaints')
export const createCareRxComplaint = (body: { kind: string; occurred_on: string; description: string; trend_category?: string; patient_id?: string }) => request<{ id: string }>('POST', '/carerx/complaints', body)
export const updateCareRxComplaint = (id: string, body: { investigation?: string; resolution?: string; trend_category?: string; status?: string }) => request<{ id: string }>('PUT', `/carerx/complaints/${id}`, body)
/** D-CRX-84 R2: where one prescription is now (null = no open therapy, or a tenant without migs 310/313). */
export type CareRxEnrollmentStage = 'on_hold' | 'renewal' | 'pending_labs' | 'pending_review' | 'pending_review_callback' | 'approved'
/** D-CRX-84 R2: the queue chip a row belongs to, assigned by the server ("other" rows appear only under All). */
export type CareRxEnrollmentBucket = 'review' | 'labs' | 'clinical' | 'approved' | 'assigned' | 'active' | 'other'
/** One Enrollment-queue row: one per open therapy, or one for a patient with none. */
export interface CareRxEnrollmentQueueRow {
  patient_id: string
  patient_name: string | null
  mrn: string | null
  enrollment_status: string
  assigned_nurse_id: string | null
  episode: string | null
  enrolled_at: string | null
  assigned_at: string | null
  review_status: string | null
  updated_at: string
  drug_name: string | null
  therapy_archetype: string | null
  open_therapies: number
  date_of_birth: string | null
  city: string | null
  rx_numbers: string | null
  therapy_id: string | null
  nurse_name: string | null
  preferred_language: string | null
  therapy_refs: string[]
  pregnancy_yes: boolean
  intake_notes: string | null
  last_review_note: string | null
  last_review_by: string | null
  last_review_at: string | null
  assigned_rph_id: string | null
  pharmacist_name: string | null
  stage: CareRxEnrollmentStage | null
  bucket: CareRxEnrollmentBucket
}
export interface CareRxEnrollmentQueue {
  items: CareRxEnrollmentQueueRow[]
  count: number
  cap?: number
  truncated?: boolean
  /** Whole-queue rows per bucket (counted server-side when the queue is capped). */
  bucket_counts?: Partial<Record<CareRxEnrollmentBucket, number>>
}
export const listCareRxEnrollmentQueue = (status?: string) =>
  request<CareRxEnrollmentQueue>('GET', `/carerx/enrollment/queue${status ? '?status=' + status : ''}`)
/** D-CRX-84 R2 row action: the existing Clinical Review "send back for labs" step, audited with its source. */
export const sendCareRxTherapyToLabs = (therapyId: string, source: 'clinical_review' | 'enrollment_queue') =>
  request<{ ok: boolean; routed: string; unchanged?: boolean }>('POST', `/carerx/clinical-review/${therapyId}/send-to-labs`, { source })

export const aiAssist = (messages: { role: string; content: string }[], maxTokens?: number, model?: string) =>
  request<{ text: string; phi_flag: string; phi_count: number; model?: string }>('POST', '/carerx/ai/assist', { messages, max_tokens: maxTokens, model })
export interface CareRxAiModel { id: string; label: string; in_per_1m: number; out_per_1m: number }
export const getCareRxAiModels = () => request<{ default_model: string; models: CareRxAiModel[] }>('GET', '/carerx/ai/models')

/** D-POT-2: the LATEST consent row wins — a decline or an admin reset after an acceptance = not consented. */
export const getCareRxAiConsent = () =>
  request<{ consented: boolean; version?: string; at?: string; action?: 'accepted' | 'declined' | 'reset' }>('GET', '/carerx/ai/consent')
/** asp-pot /api/consent/accept: the device + (best-effort) location are recorded with the acceptance. */
export interface AiConsentEventBody { latitude?: number; longitude?: number; user_agent?: string }
export const acceptCareRxAiConsent = (body?: AiConsentEventBody) =>
  request<{ consented: boolean; version: string; at: string }>('POST', '/carerx/ai/consent', body ?? {})
// D-CRX-47 POT-5: admin audit of who accepted the AI training & consent (D-POT-2: every event — accepted / declined / reset).
export interface AiConsentLogRow {
  account_id: string; accepted_by: string | null; version: string
  /** The row timestamp whatever the action (name kept for the existing consent-log screen). */
  accepted_at: string
  action?: 'accepted' | 'declined' | 'reset'
  client_ip?: string | null; user_agent?: string | null; app_version?: string | null
  latitude?: number | null; longitude?: number | null
  reset_by?: string | null; reset_by_name?: string | null
}
export const getCareRxAiConsentLog = () => request<{ items: AiConsentLogRow[]; count: number }>('GET', '/carerx/ai/consent-log')

// ── CareRx billing ──────────────────────────────────────────────────────────
export const listCareRxAgencies = () => request<{ items: any[]; count: number }>('GET', '/carerx/agencies')
export const createCareRxAgency = (b: { name: string; city?: string; state_code?: string; contact_name?: string; contact_email?: string; contact_phone?: string
  address_line?: string; zip_code?: string; agency_type?: 'hha' | 'nurse_independent'; ein?: string; w9_on_file?: boolean }) =>
  request<{ id: string; name: string }>('POST', '/carerx/agencies', b)
    .then(r => { swrInvalidate('/carerx/agencies'); return r })   // the list is SWR-cached: the reload after Add must not serve the old list
export const listCareRxRateCards = (agencyId: string) => request<{ items: any[] }>('GET', `/carerx/agencies/${agencyId}/rate-cards`)
export const createCareRxRateCard = (agencyId: string, b: any) => request<{ id: string }>('POST', `/carerx/agencies/${agencyId}/rate-cards`, b)
/** asp-pot PUT /api/billing/agencies/:id — any field, or status active | inactive. */
export const updateCareRxAgency = (agencyId: string, b: Record<string, unknown>) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/agencies/${agencyId}`, b)
    .then(r => { swrInvalidate('/carerx/agencies'); return r })
/** asp-pot PUT /api/billing/rate-cards/:id — refused (409 rate_card_on_signed_loa) once a signed LOA points at it. */
export const updateCareRxRateCard = (rateCardId: string, b: Record<string, unknown>) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/rate-cards/${rateCardId}`, b)
/** asp-pot DELETE /api/billing/rate-cards/:id — soft-archive: never picked for a new invoice. */
export const archiveCareRxRateCard = (rateCardId: string) =>
  request<{ id: string; archived: boolean }>('DELETE', `/carerx/rate-cards/${rateCardId}`)
export const listCareRxVisits = (agencyId?: string, unbilled?: boolean) => {
  const q = new URLSearchParams(); if (agencyId) q.set('agency_id', agencyId); if (unbilled) q.set('unbilled', '1')
  const qs = q.toString(); return request<{ items: any[]; count: number }>('GET', `/carerx/visits${qs ? '?' + qs : ''}`)
}
export const createCareRxVisit = (b: any) => request<{ id: string }>('POST', '/carerx/visits', b)
export const generateCareRxInvoice = (agencyId: string, b: { period_start: string; period_end: string }) =>
  request<any>('POST', `/carerx/agencies/${agencyId}/invoices`, b)
export const listCareRxInvoices = (agencyId: string) => request<{ items: any[]; count: number }>('GET', `/carerx/agencies/${agencyId}/invoices`)
export const listCareRxLoas = (agencyId: string) => request<{ items: any[]; count: number }>('GET', `/carerx/agencies/${agencyId}/loas`)
export const listCareRxCredentials = (agencyId: string) => request<{ credentials: any[] }>('GET', `/carerx/agencies/${agencyId}/credentials`)
export const createCareRxCredential = (agencyId: string, b: any) => request<{ id: string; created: boolean }>('POST', `/carerx/agencies/${agencyId}/credentials`, b)
export const createCareRxLoa = (agencyId: string, b: any) => request<{ id: string; loa_number: string }>('POST', `/carerx/agencies/${agencyId}/loas`, b)

export const reviewCareRxInvoice = (invoiceId: string, body: { action: 'submit' | 'start_review' | 'approve' | 'reject' | 'mark_paid' | 'void'; notes?: string }) =>
  request<{ id: string; status: string; visits_released?: number }>('PUT', `/carerx/invoices/${invoiceId}/review`, body)
/** asp-pot "Edit Visit": a visit's billing details, editable until it is on an invoice (409 visit_invoiced). */
export interface CareRxVisitBillingUpdate {
  agency_id?: string; visit_type?: string; city?: string; state_code?: string; zip?: string
  mileage?: number; after_hours?: boolean; weekend?: boolean; holiday?: boolean
}
export const updateCareRxVisitBilling = (visitId: string, body: CareRxVisitBillingUpdate) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/visits/${visitId}/billing`, body)

export const renderCareRxInvoice = (invoiceId: string) =>
  request<{ document_id: string; content_hash: string; stored: boolean }>('POST', `/carerx/invoices/${invoiceId}/document`)
export const renderCareRxLoa = (loaId: string) =>
  request<{ document_id: string; content_hash: string; stored: boolean }>('POST', `/carerx/loas/${loaId}/document`)

// CareRx drug list (distinct from PioneerRx rx_items) — feeds the Plan of Care drug picker.
export const listCareRxDrugs = (q?: string) =>
  request<{ items: string[]; count: number }>('GET', `/carerx/drugs${q ? '?q=' + encodeURIComponent(q) : ''}`)

export const listCareRxDiagnoses = (q?: string) =>
  request<{ items: string[]; count: number }>('GET', `/carerx/diagnoses${q ? '?q=' + encodeURIComponent(q) : ''}`)

export const assignCareRxWork = (b: { subject_type: string; subject_id: string; assigned_to?: string; action?: 'assign' | 'release' }) => request<{ assigned_to: string | null; released: boolean }>('POST', '/carerx/work/assign', b)
export const getCareRxPipeline = () => request<{ pipeline: Record<string, number>; my_work: { kind: string; id: string; label: string; at: string | null; tab: string; /** ASP #9 badge; null on non-patient work (invoices, cadence tasks). */ patient_id?: string | null }[] }>('GET', '/carerx/dashboard/pipeline')
export const getCareRxInsights = () => request<{
  assessments: { by_status: { key: string; count: number }[]; by_signature: { key: string; count: number }[] }
  enrollment: { by_status: { key: string; count: number }[] }
  ai: { calls: number; total_cost_usd: number; by_phi_flag: { key: string; count: number }[] }
  billing: { agencies: number; invoices: number; invoiced_total_usd: number; unbilled_visits: number }
  labs: { expiring: number; expired: number; required_missing: number }
  clinical: { by_status: { key: string; count: number }[]; by_determination: { key: string; count: number }[] }
  screening: { active_holds: number; flagged_screenings: number }
  fulfillment: { open_consents: number; fills_in_flight: number }
}>('GET', '/carerx/insights')

// ── Per-pharmacy SSO (Entra ID) ─────────────────────────────────────────────
export interface PharmacySsoConfig {
  configured: boolean; enabled: boolean; provider?: string
  entra_tenant_id?: string | null; entra_client_id?: string | null; authority?: string | null
  secret_configured?: boolean; client_secret_kv_ref?: string | null; allowed_email_domains?: string | null
  jit_provisioning?: boolean; require_sso?: boolean
}
export const getPharmacySso = (pharmacyId: string) =>
  request<PharmacySsoConfig>('GET', `/pharmacies/${pharmacyId}/sso`)
export const putPharmacySso = (pharmacyId: string, body: {
  enabled?: boolean; entra_tenant_id?: string; entra_client_id?: string; client_secret_kv_ref?: string
  allowed_email_domains?: string[]; jit_provisioning?: boolean; require_sso?: boolean
}) => request<{ ok: boolean; authority?: string }>('PUT', `/pharmacies/${pharmacyId}/sso`, body)
// SSO sign-in is a full-page navigation (OIDC redirect), not a fetch:
export const ssoStartUrl = (pharmacyId: string, redirectTo?: string) =>
  `/api/auth/sso/${pharmacyId}/start${redirectTo ? '?redirect_to=' + encodeURIComponent(redirectTo) : ''}`

// ── Platform (Owl Consultations management portal) SSO ───────────────────────
export const getPlatformSso = () => request<PharmacySsoConfig>('GET', '/platform/sso')
export const putPlatformSso = (body: {
  enabled?: boolean; entra_tenant_id?: string; entra_client_id?: string; client_secret_kv_ref?: string
  allowed_email_domains?: string[]; jit_provisioning?: boolean; require_sso?: boolean
}) => request<{ ok: boolean; authority?: string }>('PUT', '/platform/sso', body)
export const platformSsoStartUrl = (redirectTo?: string) =>
  `/api/auth/sso/platform/start${redirectTo ? '?redirect_to=' + encodeURIComponent(redirectTo) : ''}`

export const getPublicPlatformSso = () => request<{ enabled: boolean; require_sso?: boolean }>('GET', '/public/platform-sso')

// Change a pharmacy's URL code (slug). SaaS-admin only; changing it changes the login URL.
export const setPharmacyUrlCode = (pharmacyId: string, urlCode: string) =>
  request<{ ok: boolean; slug: string; login_url: string }>('PUT', `/pharmacies/${pharmacyId}/url-code`, { url_code: urlCode })

// Update the recorded BAA reference + effective window (SaaS-admin only; audited).
// Document versions still go through the WI-822 documents flow.
export const updatePharmacyBaa = (pharmacyId: string, input: {
  baaReference: string
  baaEffectiveDate?: string | null
  baaExpiryDate?: string | null
}) =>
  request<{ ok: boolean; baa_reference: string; baa_effective_date: string | null; baa_expiry_date: string | null }>(
    'PUT', `/pharmacies/${pharmacyId}/baa`, input)

// ── D2 Drug Formulary ──────────────────────────────────────────────────────
export interface CareRxFormularyRow {
  id: string
  pharmacy_id: string | null
  is_global: boolean
  ndc: string | null
  name: string
  brand_name: string | null
  strength: string | null
  manufacturer: string | null
  route: string | null
  category: string | null
  drug_class: string | null
  required_baselabs: string | null
  recareplan_interval_days: number | null
  active: boolean
}
export const listCareRxFormulary = (params?: { category?: string; route?: string; search?: string; active?: boolean }) => {
  const q = new URLSearchParams()
  if (params?.category) q.set('category', params.category)
  if (params?.route) q.set('route', params.route)
  if (params?.search) q.set('search', params.search)
  if (params?.active) q.set('active', 'true')
  const qs = q.toString()
  return request<{ count: number; items: CareRxFormularyRow[] }>('GET', `/carerx/formulary${qs ? '?' + qs : ''}`)
}
export const getCareRxFormularyFacets = () =>
  request<{ categories: string[]; routes: string[]; drug_classes: string[] }>('GET', '/carerx/formulary/facets')
export const createCareRxFormulary = (body: { name: string; ndc?: string; brandName?: string; strength?: string; manufacturer?: string; route?: string; category?: string; drugClass?: string; requiredBaselabs?: string[]; recareplanIntervalDays?: number; active?: boolean }) =>
  request<{ id: string; created: boolean }>('POST', '/carerx/formulary', body)
export const deleteCareRxFormulary = (id: string) =>
  request<{ id: string; deleted: boolean }>('DELETE', `/carerx/formulary/${id}`)

// ── D3/D4 Re-careplan renewal queue ────────────────────────────────────────
export interface CareRxRenewalRow {
  open_exception?: boolean
  labs_expiring_30d?: boolean
  id: string
  patient_id: string
  patient_name: string | null
  mrn: string | null
  trigger_type: string
  trigger_reason: string
  severity: string
  source: string
  status: string
  drug_name: string | null
  therapy_id: string | null
  cycle_no: number | null
  interval_days: number | null
  last_pot_date: string | null
  due_date: string | null
  detected_at: string
  note: string | null
}
export const listCareRxRecareplanQueue = (params?: { status?: string; severity?: string }) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.severity) q.set('severity', params.severity)
  const qs = q.toString()
  return request<{ count: number; items: CareRxRenewalRow[] }>('GET', `/carerx/recareplan/queue${qs ? '?' + qs : ''}`)
}
export const runCareRxRecareplan = () =>
  request<{ scanned_patients: number; triggers: number; by_type: Record<string, number>; note?: string }>('POST', '/carerx/recareplan/run', {})
export const updateCareRxRecareplanItem = (id: string, action: 'dismiss' | 'resolve' | 'reopen', note?: string) =>
  request<{ id: string; status: string }>('PUT', `/carerx/recareplan/queue/${id}`, { action, note })
export const createCareRxRecareplanManual = (body: { patient_id: string; trigger_reason: string; severity?: string; note?: string }) =>
  request<{ id: string; created: boolean }>('POST', '/carerx/recareplan/queue', body)

// ── D5/D6 PCW two-stage + D7 queue claims ──────────────────────────────────
export interface CareRxPcwQueueItem {
  id: string; patient_id: string; patient_name: string | null; mrn: string | null
  status: string; phase: string; therapy_determination: string | null
  sections_complete: number; sections_total: number; assessment_id: string | null; updated_at: string
}
export const listCareRxPcwIntakeQueue = () =>
  request<{ phase: string; count: number; items: CareRxPcwQueueItem[] }>('GET', '/carerx/pcw/intake-queue')
export const listCareRxPcwPharmacistQueue = () =>
  request<{ phase: string; count: number; items: CareRxPcwQueueItem[] }>('GET', '/carerx/pcw/pharmacist-queue')
export const getCareRxPcwSection = (id: string, num: number) =>
  request<{ pcw_id: string; section: number; group: string; status: string | null; data: any; last_saved_at: string | null }>('GET', `/carerx/pcw/${id}/section/${num}`)
export const updateCareRxPcwSection = (id: string, num: number, body: { status?: string; data?: any }) =>
  request<{ pcw_id: string; section: number; status: string | null; sections_complete: number; sections_total: number }>('PUT', `/carerx/pcw/${id}/section/${num}`, body)
export const completeCareRxPcwIntake = (id: string) =>
  request<{ pcw_id: string; phase: string }>('POST', `/carerx/pcw/${id}/complete-intake`, {})
export interface CareRxClaim { entity_type: string; entity_id: string; claimed_by: string; claimed_by_name: string | null; claimed_at: string; expires_at: string }
export const claimCareRxQueueItem = (entity_type: string, entity_id: string) =>
  request<{ claimed: boolean; expires_at: string }>('POST', '/carerx/queue/claim', { entity_type, entity_id })
export const releaseCareRxQueueItem = (entity_type: string, entity_id: string) =>
  request<{ released: boolean }>('POST', '/carerx/queue/release', { entity_type, entity_id })
export const listCareRxQueueClaims = (params?: { entity_type?: string; entity_id?: string }) => {
  const q = new URLSearchParams()
  if (params?.entity_type) q.set('entity_type', params.entity_type)
  if (params?.entity_id) q.set('entity_id', params.entity_id)
  const qs = q.toString()
  return request<{ count: number; items: CareRxClaim[] }>('GET', `/carerx/queue/claims${qs ? '?' + qs : ''}`)
}

// ── CareRx UAT runner ───────────────────────────────────────────────────────
export interface CareRxUatTest { id: string; name: string; status: 'pass' | 'fail' | 'skip'; detail: string; ms: number }
export interface CareRxUatReport {
  ran_at: string; base_url: string
  summary: { pass: number; fail: number; skip: number; total: number }
  suites: { name: string; tests: CareRxUatTest[] }[]
}
export const runCareRxUat = () => request<CareRxUatReport>('POST', '/carerx/uat/run', {})

// ---------- Data Subject Requests (DSR) — staff/admin tooling ----------
// Backend: PR-B1. Intake + admin queue live behind auth (pharmacy_staff/admin);
// resolution is admin-only and the server enforces the retention guard
// (clinical-record deletion cannot be 'fulfilled'). The public /legal/data-rights
// page intentionally does NOT post here — it directs subjects to the Privacy
// Officer; intake is a staff action. Admin queue UI is a tracked follow-up.
export interface DsrCreateInput {
  subject_type: 'patient' | 'user'
  subject_id?: string
  subject_label?: string
  request_type: 'access' | 'amendment' | 'restriction' | 'portability' | 'deletion'
  scope?: 'account' | 'clinical' | 'all'
  detail?: string
}
export interface DsrRecord {
  id: string
  status: string
  subject_type?: string
  subject_label?: string | null
  request_type?: string
  scope?: string
  due_at?: string | null
  created_at?: string
  resolved_at?: string | null
  note?: string | null
  denial_basis?: string | null
}
export const createDsrRequest = (input: DsrCreateInput) =>
  request<DsrRecord>('POST', '/ops/dsr', input)
export const listDsrRequests = (status?: string) =>
  request<{ items: DsrRecord[]; count: number }>('GET', `/ops/dsr${status ? `?status=${encodeURIComponent(status)}` : ''}`)
export const resolveDsrRequest = (id: string, body: { status: string; disposition?: string; denial_basis?: string }) =>
  request<DsrRecord>('PUT', `/ops/dsr/${id}`, body)

// ---------- Policy acceptance (PR-B2, record-only versioned) ----------
// Records that the current user accepted a legal document VERSION. Does not gate
// login (Option A) — the status call powers a future blocking accept-gate once
// counsel signs the text. Version key comes from POLICY_VERSION in LegalPages.
export type PolicyDocument = 'terms' | 'privacy' | 'data_rights'
export const recordPolicyAcceptance = (document: PolicyDocument, version: string) =>
  request<{ document: string; version: string; accepted: boolean }>('POST', '/policy-acceptances', { document, version })
export const getPolicyAcceptances = () =>
  request<{ account_id: string; acceptances: { document: string; version: string; accepted_at: string }[]; count: number }>('GET', '/policy-acceptances')

// --- Notifications (pharmacy in-app) ---
export interface NotificationItem { id: string; event_type: string; title: string; body: string | null; link: string | null; is_read: boolean; created_at: string }
export interface NotificationPref { event_type: string; inapp_enabled: boolean; email_enabled: boolean }
export const getNotifications = (unread = false, limit = 50) =>
  request<{ unread_count: number; items: NotificationItem[] }>('GET', `/notifications?unread=${unread}&limit=${limit}`)
export const markNotificationsRead = (body: { ids?: string[]; all?: boolean }) =>
  request<{ ok: boolean }>('POST', '/notifications/mark-read', body)
export const getNotificationPrefs = () =>
  request<{ email_available: boolean; preferences: NotificationPref[] }>('GET', '/notifications/preferences')
export const putNotificationPrefs = (preferences: NotificationPref[]) =>
  request<{ ok: boolean }>('PUT', '/notifications/preferences', { preferences })

// --- CareRx e-sign field configuration ---
export interface EsignTemplate { id: string; scope: string; name: string; file_name: string; page_count: number; status: string; updated_at: string; subject_type?: string | null; doc_kind?: string | null; attach_mode?: string }
export interface EsignPlacement {
  id?: string; field_type: string; recipient_role?: string; page_number: number;
  pos_x: number; pos_y: number; width?: number | null; height?: number | null;
  is_required: boolean; label?: string | null; data_binding?: string | null
}
// D-CRX-73 review (Mario 2026-09-02): E-signature settings is about the documents OwlRx GENERATES.
// The server reports each one using the send paths' own template rules, so this screen can never
// claim "generated" about a lane that is quietly mailing an uploaded file.
export interface EsignGeneratedDoc {
  kind: 'consent' | 'enrollment'
  label: string
  /** Where the generated document is built: server-side, or in the browser (no server copy exists). */
  generator: 'server' | 'browser'
  /** What the SEND path actually transmits. 'nothing' = this lane cannot send at all. */
  sends: 'generated' | 'template' | 'nothing'
  /** How signature tabs are placed on whatever `sends` names; null when nothing is sent. */
  tabs: 'anchor' | 'coordinate' | null
  /** The uploaded template in play, if any. */
  template: { id: string; name: string } | null
  /**
   * 'override' — a generated document exists and this file is in front of it; removing the file is
   *              a safe way back.
   * 'required' — this file IS the document that gets signed. Removing it ends signing for the lane.
   * The distinction is the whole point: the same warning text for both told operators to delete the
   * only template that makes enrollment signing work.
   */
  template_role: 'override' | 'required' | null
  esign_ready: boolean
  /** Machine codes, not sentences: 'docusign_not_configured' | 'no_template'. */
  blockers: string[]
}
export const listEsignGeneratedDocs = () =>
  request<{ documents: EsignGeneratedDoc[]; uploads_enabled: boolean; docusign_connected: boolean }>(
    'GET', '/carerx/esign/generated-documents')
export const listEsignTemplates = () =>
  request<{ templates: EsignTemplate[] }>('GET', '/carerx/esign/templates')
export const getEsignTemplate = (id: string) =>
  request<{ template: any; placements: EsignPlacement[] }>('GET', `/carerx/esign/templates/${id}`)
export const saveEsignPlacements = (id: string, placements: EsignPlacement[]) =>
  request<{ ok: boolean; count: number }>('PUT', `/carerx/esign/templates/${id}/placements`, { placements })
export const esignPdfUrl = (id: string) => `${API_BASE}/carerx/esign/templates/${id}/pdf`
// Upload is multipart — done via fetch directly in the component.
export const deleteEsignTemplate = (id: string) =>
  request<{ ok: boolean; deleted: string }>('DELETE', `/carerx/esign/templates/${id}`)

export const setEsignBinding = (
  id: string,
  binding: { subject_type?: string | null; doc_kind?: string | null; attach_mode: string; activate?: boolean },
) => request<{ ok: boolean; id: string; subject_type?: string; doc_kind?: string; attach_mode: string }>(
  'PUT', `/carerx/esign/templates/${id}/binding`, binding)
export interface EsignPreviewField { field_type: string; signer: string; page: number; auto_filled: boolean; fills_with: string; binding?: string | null }
export const previewEsignFill = (id: string, assessmentId?: string) =>
  request<{ template_id: string; data_source: string; fields: EsignPreviewField[] }>(
    'GET', `/carerx/esign/templates/${id}/preview-fill${assessmentId ? `?assessment=${assessmentId}` : ''}`)

// ---------- Support ticketing ----------
export type SupportSeverity = 'low' | 'normal' | 'high' | 'critical'
// D-SUPPORT-2 Phase A canonical model. 'triage' | 'in_progress' | 'resolved' are LEGACY
// values that still exist on rows created under the phase-1 model — readable, never
// written again (the backend folds them onto open/solved on any write).
export type SupportStatus = 'new' | 'open' | 'pending' | 'on_hold' | 'solved' | 'closed' | 'triage' | 'in_progress' | 'resolved'
export type SupportCategory = 'billing' | 'technical' | 'clinical' | 'account' | 'other'
export type SupportSlaClockStatus = 'none' | 'ok' | 'due_soon' | 'breached' | 'met' | 'missed' | 'paused'
export interface SupportSlaClock {
  status: SupportSlaClockStatus
  target_minutes: number | null
  elapsed_minutes: number
  remaining_minutes: number | null
}
export interface SupportSlaDetail {
  priority: 'P1' | 'P2' | 'P3' | 'P4'
  worst: SupportSlaClockStatus
  first_response: SupportSlaClock
  resolution: SupportSlaClock
  // Null when nobody owes the customer a reply. "No clock" and "clock at zero" are different
  // states, so the UI hides the row rather than rendering a dash.
  next_response: SupportSlaClock | null
  awaiting_reply_since: string | null
}

export interface SupportTicketSummary {
  id: string
  ticket_no: number
  subject: string
  category: SupportCategory
  severity: SupportSeverity
  status: SupportStatus
  created_at: string
  last_activity_at: string
}
export interface SupportQueueItem extends SupportTicketSummary {
  requester_name?: string | null
  pharmacy_id: string
  pharmacy_name: string | null
  assigned_to_account_id: string | null
  first_response_at?: string | null
  resolved_at?: string | null
  paused?: boolean
  tags?: string[]
  age_seconds: number
  priority: 'P1' | 'P2' | 'P3' | 'P4'
  sla_status: SupportSlaClockStatus
  sla_target_minutes: number | null
  fr_status?: SupportSlaClockStatus
  fr_target_minutes?: number | null
  fr_remaining_minutes?: number | null
  res_status?: SupportSlaClockStatus
  res_target_minutes?: number | null
  res_remaining_minutes?: number | null
  // Next-response clock (migration 369): time owed on the oldest unanswered customer comment.
  nr_status?: SupportSlaClockStatus
  nr_target_minutes?: number | null
  nr_remaining_minutes?: number | null
  awaiting_reply_since?: string | null
}
export interface SupportQueueSummary {
  open: number
  unassigned: number
  breached: number
  due_soon: number
  paused?: number
  oldest_age_seconds: number
  by_priority: Record<'P1' | 'P2' | 'P3' | 'P4', number>
  by_pharmacy?: { pharmacy_id: string; pharmacy_name: string | null; open: number }[]
}
export interface SupportCommentMention {
  account_id: string
  /** Name as rendered when the mention was written — survives a later rename. */
  display_name: string | null
  is_customer: boolean
}
export interface SupportTicketComment {
  id: string
  author_account_id: string
  author_name?: string | null
  body: string
  is_internal: boolean
  created_at: string
  mentions?: SupportCommentMention[]
}
export interface SupportTicketEvent {
  actor_account_id: string | null
  actor_name?: string | null
  event_type: string
  from_value: string | null
  to_value: string | null
  created_at: string
}
export interface SupportTicketDetail {
  id: string
  ticket_no: number
  pharmacy_id: string
  subject: string
  body: string
  category: SupportCategory
  severity: SupportSeverity
  status: SupportStatus
  assigned_to_account_id: string | null
  created_at: string
  updated_at: string
  last_activity_at: string
  resolved_at: string | null
  closed_at: string | null
  first_response_at?: string | null
  paused?: boolean
}

export const createSupportTicket = (input: { subject: string; body: string; category?: SupportCategory; severity?: SupportSeverity }) =>
  request<{ id: string; status: SupportStatus; severity: SupportSeverity; category: SupportCategory }>('POST', '/support/tickets', input)
export const listMySupportTickets = () =>
  request<{ items: SupportTicketSummary[] }>('GET', '/support/tickets')
export interface SupportTicketAttachment {
  id: string
  file_name: string
  content_type: string
  size_bytes: number
  created_at: string
}
// ─── Close request (D-SUPPORT-5) ─────────────────────────────────────────────
// The customer ASKS to close; an agent decides. Deliberately not a status change:
// status transitions are platform-only, because closing settles an SLA measurement.
export interface SupportCloseRequest {
  pending: boolean
  requested_at: string | null
  requested_by: string | null
  note: string | null
  declined_at: string | null
  decline_reason: string | null
}
export const requestSupportTicketClose = (id: string, note?: string) =>
  request<{ ok: boolean; close_request: { pending: boolean; note: string | null }; message: string }>(
    'POST', `/support/tickets/${id}/close-request`, { note })
export const withdrawSupportTicketClose = (id: string) =>
  request<{ ok: boolean }>('DELETE', `/support/tickets/${id}/close-request`)
export const declineSupportTicketClose = (id: string, note?: string) =>
  request<{ ok: boolean }>('POST', `/support/tickets/${id}/close-request/decline`, { note })

export const getSupportTicket = (id: string) =>
  request<{ ticket: SupportTicketDetail; comments: SupportTicketComment[]; events: SupportTicketEvent[]; attachments: SupportTicketAttachment[]; tags: string[]; sla: SupportSlaDetail | null; close_request: SupportCloseRequest }>('GET', `/support/tickets/${id}`)
export const uploadSupportTicketAttachment = async (ticketId: string, file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${API_BASE}/support/tickets/${ticketId}/attachments`, {
    method: 'POST', credentials: 'omit',
    headers: { ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}) },
    body: fd,
  })
  if (!res.ok) {
    const text = await res.text(); let p: any = {}
    try { p = JSON.parse(text) } catch { /* non-JSON */ }
    throw new ApiError(res.status, p.error || 'upload_failed', p.message || text)
  }
  return res.json() as Promise<SupportTicketAttachment>
}
export const getSupportTicketAttachmentUrl = (ticketId: string, attId: string) =>
  request<{ url: string }>('GET', `/support/tickets/${ticketId}/attachments/${attId}?format=json`)
export const addSupportTicketComment = (id: string, body: string, isInternal = false, mentionAccountIds: string[] = []) =>
  request<{ ok: boolean; reopened?: boolean; mentioned?: string[] }>(
    'POST', `/support/tickets/${id}/comments`, { body, is_internal: isInternal, mention_account_ids: mentionAccountIds })

// --- D-SUPPORT-12: @mentions on case comments ---
// Mentionable = Owl support staff + the users of THIS ticket's pharmacy. Scoped server-side from
// the ticket, never from a parameter, so a support agent cannot enumerate other tenants' users.
export interface SupportMentionablePerson {
  account_id: string
  name: string
  email: string | null
  is_customer: boolean
  /** The person who opened the case. Sorted first — usually who you want. */
  is_reporter: boolean
}
export const listSupportTicketMentionable = (id: string) =>
  request<{ people: SupportMentionablePerson[] }>('GET', `/support/tickets/${id}/mentionable`)
export type SupportQueueLane = 'queue' | 'mine' | 'archive'
export interface SupportQueueResponse {
  items: SupportQueueItem[]
  summary: SupportQueueSummary
  lane: SupportQueueLane
  lane_counts: { queue: number; mine: number; archive: number }
  total: number
  page: number
  page_size: number
  sort: string
  dir: 'asc' | 'desc'
}
/**
 * The queue is paged, filtered, laned and sorted SERVER-side (D-SUPPORT-3 review #7). The caller
 * renders exactly what it is handed; nothing is sliced in the browser, so the lane tab counts and
 * the summary tiles are computed over the same full data set and cannot disagree.
 */
export const getSupportQueue = (filters?: {
  lane?: SupportQueueLane
  status?: string
  severity?: SupportSeverity
  tag?: string
  q?: string
  page?: number
  pageSize?: number
  sort?: string
  dir?: 'asc' | 'desc'
  filter?: string[]
}) => {
  const qs = new URLSearchParams()
  if (filters?.lane) qs.set('lane', filters.lane)
  if (filters?.status) qs.set('status', filters.status)
  if (filters?.severity) qs.set('severity', filters.severity)
  if (filters?.tag) qs.set('tag', filters.tag)
  if (filters?.q) qs.set('q', filters.q)
  if (filters?.page) qs.set('page', String(filters.page))
  if (filters?.pageSize) qs.set('page_size', String(filters.pageSize))
  if (filters?.sort) qs.set('sort', filters.sort)
  if (filters?.dir) qs.set('dir', filters.dir)
  for (const f of filters?.filter ?? []) if (f) qs.append('filter', f)
  const s = qs.toString()
  return request<SupportQueueResponse>('GET', `/support/queue${s ? `?${s}` : ''}`)
}
export const setSupportTicketStatus = (id: string, status: SupportStatus) =>
  request<{ ok: boolean; status: SupportStatus }>('POST', `/support/tickets/${id}/status`, { status })
export const assignSupportTicket = (id: string, accountId: string | null) =>
  // null = unassign (explicit); a string = assign to that account. Omitting both would self-assign server-side.
  request<{ ok: boolean }>('POST', `/support/tickets/${id}/assign`, accountId === null ? { unassign: true } : { account_id: accountId })
export const setSupportTicketSeverity = (id: string, severity: SupportSeverity) =>
  request<{ ok: boolean; severity: SupportSeverity }>('POST', `/support/tickets/${id}/severity`, { severity })

// --- Tags + saved views (D-SUPPORT-2 Phase A.5) ---
export const addSupportTicketTag = (id: string, tag: string) =>
  request<{ ok: boolean; tag: string }>('POST', `/support/tickets/${id}/tags`, { tag })
export const removeSupportTicketTag = (id: string, tag: string) =>
  request<{ ok: boolean; removed: boolean }>('DELETE', `/support/tickets/${id}/tags/${encodeURIComponent(tag)}`)
// ---- D-SUPPORT-3: Support Home dashboard + badges -------------------------
export interface SupportCustomer {
  id: string; name: string; slug: string | null; onboarding_state: string | null
  created_at: string; user_count: number; open_tickets: number; open_critical: number
  primary_account_id: string | null; primary_name: string | null
  secondary_account_id: string | null; secondary_name: string | null
  // D-SUPPORT-9 — computed server-side by the SAME scorer the detail view uses, so the list and
  // the expanded panel can never disagree about whether a customer is red.
  // 'unknown' = the scorer could not run (busy database). It is NOT a healthy verdict and must
  // never be rendered as one — see SupportCustomerFunctions.ListCustomers.
  health_band: 'healthy' | 'watch' | 'at_risk' | 'unknown'
  health_score: number | null
  health_reasons: string[]
  sla_attainment_pct: number | null
}
export interface SupportTeamMember { id: string; display_name: string | null; email: string; role: string }
export const listSupportCustomers = () =>
  request<{ customers: SupportCustomer[] }>('GET', '/support/customers')
export const listSupportTeam = () =>
  request<{ team: SupportTeamMember[] }>('GET', '/support/team')

// ── Pioneer Rx lookup (D-SUPPORT-16) ──────────────────────────────────────────────────────────
// Raw PioneerRx envelopes for one Rx at one pharmacy, with what OwlRx shows for the same Rx beside
// them. `parsed` is null when an envelope arrived but never parsed — the finding, not a gap.
export interface PioneerEnvelope {
  staging_id: string
  received_at: string
  source_filename: string | null
  message_id: string | null
  sent_on_utc: string | null
  initiating_event_id: string | null
  workstation: string | null
  parse_status: string | null
  parse_error: string | null
  sha_prefix: string | null
  parsed: {
    rx_number: string | null
    refill_number: number | null
    rx_status: string | null
    tx_status: string | null
  } | null
  envelope_json: string
}
export interface PioneerPortalRx {
  id: string
  /** display_rx_number on a fill-grain tenant: bare for fill 0, rx-N otherwise. */
  rx_number: string | null
  /** Authoritative fill number from rx_fill_items (or rx_items.refill_number on a tenant without
   *  migration 285). Preferred over parsing the suffix out of rx_number. */
  fill_number?: number | null
  /** "fill" when read from rx_fill_items; "prescription" on a tenant lacking mig 285 (BioAccess). */
  grain?: 'fill' | 'prescription'
  refill_number: number | null
  state: string | null
  payer_state: string | null
  pioneer_rx_status: string | null
  pioneer_tx_status: string | null
  source: string | null
  created_at: string
  last_state_change_at: string | null
}
export interface PioneerLookupResponse {
  pharmacy_id: string
  rx: string
  envelope_count: number
  ingest_configured: boolean
  truncated?: boolean
  note?: string
  envelopes: PioneerEnvelope[]
  portal_rx: PioneerPortalRx[]
}
export const lookupPioneerEnvelopes = (pharmacyId: string, rx: string) =>
  request<PioneerLookupResponse>(
    'GET', `/support/pioneer/envelopes?pharmacy_id=${encodeURIComponent(pharmacyId)}&rx=${encodeURIComponent(rx)}`)
// D-SUPPORT-8: one customer, everything support needs — cases, users, and a reduced activity
// trail (action/entity/actor/time only; payloads omitted because they can embed patient data).
export interface SupportCustomerDetail {
  pharmacy: { id: string; name: string; slug: string | null; onboarding_state: string | null }
  health: {
    band: 'healthy' | 'watch' | 'at_risk'
    score: number
    flags: { severity: string; code: string; detail: string }[]
    sla: { measured: number; met: number; breached: number; attainment_pct: number | null }
  }
  modules: { module: string; enabled: boolean; trial_ends_at: string | null; used: boolean }[]
  ingest: {
    available: boolean; note?: string
    packages?: number; new_rx?: number; mirrored?: number; deadletter?: number
    avg_latency_sec?: number | null; last_bucket?: string
  }
  cases: { id: string; ticket_no: number; subject: string | null; category: string; severity: string
           status: string; created_at: string; last_activity_at: string | null; assignee: string | null }[]
  users: { display_name: string | null; email: string | null; status: string | null
           last_login_at: string | null; created_at: string; role: string | null }[]
  activity: { occurred_at: string; action: string | null; entity_type: string | null
              actor_role: string | null; actor: string | null }[]
  activity_note: string
  // CSM lens: is this customer actually using the portal, and who?
  usage: {
    window_days: number
    total_users: number; active_users: number; adoption_pct: number
    logins: number; last_login_at: string | null
    by_role: { role: string; users: number; active_users: number; logins: number; last_login_at: string | null }[]
    daily: { day: string; logins: number; users: number }[]
    feature_activity: { action: string; count: number; last_at: string }[]
    feature_note: string
  }
}
export const getSupportCustomerDetail = (pharmacyId: string) =>
  request<SupportCustomerDetail>('GET', `/support/customers/${pharmacyId}/detail`)

export const setSupportAssignments = (pharmacyId: string, primary: string | null, secondary: string | null) =>
  request<{ saved: boolean }>('PUT', `/support/customers/${pharmacyId}/assignments`,
    { primary_account_id: primary, secondary_account_id: secondary })
export interface SupportMonitor {
  checked_at: string
  api: { status: string }
  db: { cpu_percent: number | null; cpu_source: string; active_sessions: number; blocked_requests: number; size_mb: number }
  support: { open_tickets: number; breach_flagged: number }
  fleet_metrics: string
}
export const getSupportMonitor = () =>
  request<SupportMonitor>('GET', '/support/monitor')
export interface SupportAiSettings {
  configured: boolean; key_last4: string | null; model: string
  max_tokens: number; daily_token_cap: number; used_today: number; requests_today: number
  notify_emails: string; subject_mode: string; tools_enabled?: boolean
}
export interface SupportTeamReport {
  summary: { window_days: number; created: number; resolved: number; open_now: number; frt_median_min: number | null; ttr_median_min: number | null; breached: number; unassigned_open: number }
  agents: any[]; customers: any[]
}
export const getSupportTeamReport = (days = 30) =>
  request<SupportTeamReport>('GET', `/support/reports/team?days=${days}`)
export interface SupportMacro { id: string; title: string; body: string }
export const listSupportMacros = () =>
  request<{ items: SupportMacro[] }>('GET', '/support/macros')
export const createSupportMacro = (title: string, body: string) =>
  request<{ id: string; title: string }>('POST', '/support/macros', { title, body })
export const deleteSupportMacro = (id: string) =>
  request<{ deleted: boolean }>('DELETE', `/support/macros/${id}`)
export const supportAiDraft = (ticketId: string) =>
  request<{ draft: string; mode: 'ai' | 'demo' }>('POST', '/support/ai/draft', { ticket_id: ticketId })
export const getSupportAiSettings = () =>
  request<SupportAiSettings>('GET', '/support/ai/settings')
export const putSupportAiSettings = (body: { api_key?: string; model?: string; max_tokens?: number; daily_token_cap?: number; notify_emails?: string; subject_mode?: string; tools_enabled?: boolean }) =>
  request<{ saved: boolean }>('PUT', '/support/ai/settings', body)
// tools_used: which read-only lookups the assistant ran for this answer (D-SUPPORT-5). Empty
// when it answered from product knowledge alone, or when the demo KB answered.
export const supportAiChat = (messages: { role: string; content: string }[], ticketId?: string, conversationId?: string) =>
  request<{ reply: string; mode: 'ai' | 'demo'; tools_used?: string[]; conversation_id?: string | null }>('POST', '/support/ai/chat',
    { messages, ...(ticketId ? { ticket_id: ticketId } : {}), ...(conversationId ? { conversation_id: conversationId } : {}) })

// Which assistant tools can return data in THIS environment. 'limited' means something the tool
// needs (e.g. an Azure subscription) is absent here — a deployment fact, not a broken feature.
export interface SupportAiCapabilities {
  // 'tools_disabled' is a deliberate operator setting (support.ai.tools_enabled), NOT a broken or
  // half-wired environment — the UI must present it differently or it reads as a fault.
  environment: 'full' | 'limited' | 'tools_disabled'
  tools: { name: string; label: string; available: boolean; reason?: string | null }[]
}
// D-SUPPORT-7 — saved conversations. Every endpoint is scoped to the calling account server-side;
// there is no cross-user read, so there is nothing to pass here but the id.
export interface SupportAiConversation {
  id: string; title: string; ticket_ref?: string | null
  created_at: string; updated_at: string; message_count: number
}
export interface SupportAiConversationDetail {
  id: string; title: string; ticket_ref?: string | null
  messages: { role: 'user' | 'assistant'; content: string; tools?: string[]; mode?: string; created_at: string }[]
}
export const listSupportAiConversations = () =>
  request<{ conversations: SupportAiConversation[] }>('GET', '/support/ai/conversations')
export const getSupportAiConversation = (id: string) =>
  request<SupportAiConversationDetail>('GET', `/support/ai/conversations/${id}`)
export const deleteSupportAiConversation = (id: string) =>
  request<{ deleted: boolean }>('DELETE', `/support/ai/conversations/${id}`)
export const renameSupportAiConversation = (id: string, title: string) =>
  request<{ renamed: boolean; title: string }>('PATCH', `/support/ai/conversations/${id}`, { title })

export const getSupportAiCapabilities = () =>
  request<SupportAiCapabilities>('GET', '/support/ai/capabilities')
export const listSupportMembers = () =>
  request<{ members: any[] }>('GET', '/support/team/members')
export const getSupportMember = (id: string) =>
  request<any>('GET', `/support/team/members/${id}`)
export const putSupportMemberProfile = (id: string, body: { display_name?: string; title?: string; phone?: string; work_tz?: string; work_start?: string; work_end?: string; work_days?: string; reports_to_account_id?: string | null }) =>
  request<{ saved: boolean }>('PUT', `/support/team/members/${id}/profile`, body)
export const putSupportMemberRole = (id: string, role: string) =>
  request<{ saved: boolean }>('PUT', `/support/team/members/${id}/role`, { role })
export const putSupportMemberCoverage = (id: string, pharmacyIds: string[]) =>
  request<{ saved: boolean }>('PUT', `/support/team/members/${id}/coverage`, { pharmacy_ids: pharmacyIds })
export const signOutSupportMember = (id: string) =>
  request<{ revoked: number }>('POST', `/support/team/members/${id}/sign-out`, {})
export const setSupportMemberActive = (id: string, active: boolean) =>
  request<{ saved: boolean }>('POST', `/support/team/members/${id}/active`, { active })
export interface SupportInviteDetails {
  email: string; role: 'support' | 'support_supervisor' | 'support_manager'
  display_name?: string; title?: string; phone?: string
  work_start?: string; work_end?: string; work_days?: string
  reports_to_account_id?: string | null; pharmacy_ids?: string[]
}
// auth_mode tells the inviter how this hire signs in. 'sso' => bootstrap_link is the portal LOGIN
// (they use their work account, no password); 'password' => it is the accept-invitation link.
export const inviteSupportTeammate = (details: SupportInviteDetails) =>
  request<{ id: string; email: string; role: string; expires_at: string; bootstrap_link: string; auth_mode?: 'sso' | 'password' }>('POST', '/support/team/invite', details)

// --- D-SUPPORT-10: the invite form can finally see the people it is inviting ---
// Active Owl staff = platform accounts (pharmacy_id IS NULL). Not an email-domain match:
// an @owlconsultations.com address bound to a customer tenant is a tenant user.
export interface SupportPlatformUser {
  id: string
  email: string
  display_name: string | null
  /** Their CURRENT support tier, or null if they are not on the support team yet. */
  support_role: string | null
}
export const listSupportPlatformUsers = () =>
  request<{ users: SupportPlatformUser[] }>('GET', '/support/team/platform-users')

export interface SupportInvitation {
  id: string
  email: string
  display_name: string | null
  created_at: string
  expires_at: string
  invited_by: string | null
  role: string | null
  status: 'pending' | 'expired'
  /** Mail state, read from the outbound queue. null = no mail was ever queued for this
   *  invitation, which is a worse condition than 'queued' and must not be shown as the same. */
  email_status: string | null
  email_sent_at: string | null
  email_error: string | null
}
// D-SUPPORT-14: did the notification actually reach anyone? Deliberately carries NO subject or
// body — support mail subjects embed customer-authored ticket subjects, which may contain PHI.
export interface SupportNotificationDelivery {
  id: string
  kind: string
  recipient: string
  status: string
  attempts: number
  /** Provider text, not customer content. A 'failed' row without this is the dead end we are fixing. */
  last_error: string | null
  sent_at: string | null
  last_attempt_at: string | null
  created_at: string
  ticket_no: number | null
  pharmacy_name: string | null
}
export const listSupportNotificationDelivery = (opts?: { all?: boolean; days?: number }) =>
  request<{ items: SupportNotificationDelivery[]; count: number; failed_count: number; window_days: number; failed_only: boolean }>(
    'GET', `/support/notifications/delivery?status=${opts?.all ? 'all' : 'failed'}&days=${opts?.days ?? 14}`)

export const listSupportInvitations = () =>
  request<{ invitations: SupportInvitation[] }>('GET', '/support/team/invitations')

/** Add an EXISTING Owl employee to the support team. No invitation, no token, no bootstrap
 *  link — the account already exists; it just gains the role and gets a heads-up email. */
export const addExistingSupportMember = (details: {
  account_id: string
  role: string
  title?: string
  phone?: string
  work_start?: string
  work_end?: string
  work_days?: string
  reports_to_account_id?: string
  pharmacy_ids?: string[]
}) =>
  request<{ added: boolean; account_id: string; email: string; display_name: string
            role: string; notified: boolean; note: string }>('POST', '/support/team/members', details)

export const getSupportBadges = () =>
  request<{ open: number; mine: number }>('GET', '/support/badges')

export const listSupportTags = () =>
  request<{ items: { tag: string; uses: number }[] }>('GET', '/support/tags')
export interface SupportView {
  id: string
  shared: boolean
  name: string
  filter: string  // JSON: { status?, severity?, assigned?, tag? }
  created_at: string
}
export const listSupportViews = () =>
  request<{ items: SupportView[] }>('GET', '/support/views')
export const createSupportView = (name: string, filter: object, shared = false) =>
  request<{ id: string; name: string; shared: boolean }>('POST', '/support/views', { name, filter, shared })
export const deleteSupportView = (viewId: string) =>
  request<{ ok: boolean }>('DELETE', `/support/views/${viewId}`)

// --- Session replay (D-SUPPORT-1) ---
export const replayStart = () =>
  request<{ id: string; max_minutes: number }>('POST', '/replay/sessions')
export const replayComplete = (id: string, body: { ticket_id?: string; user_note?: string; duration_ms?: number }) =>
  request<{ ok: boolean }>('POST', `/replay/sessions/${id}/complete`, body)
export async function replayIngest(id: string, gz: ArrayBuffer): Promise<void> {
  const res = await apiFetch(`/replay/sessions/${id}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: gz,
  })
  if (!res.ok) throw new Error(`replay ingest ${res.status}`)
}
export interface ReplaySessionMeta {
  id: string; pharmacy_id: string; pharmacy?: string | null; account_id: string; user?: string | null
  ticket_id?: string | null; status: string; started_at: string; duration_ms?: number | null
  event_bytes: number; user_note?: string | null
}
export const replayList = (ticketId?: string) =>
  request<{ sessions: ReplaySessionMeta[] }>('GET', `/replay/sessions${ticketId ? `?ticket_id=${ticketId}` : ''}`)
export const replayPlayback = (id: string) =>
  request<{ id: string; batches: string[] }>('GET', `/replay/sessions/${id}/events`)

// ---------- System logs (PHI-free structured log viewer) ----------
export interface SystemLogEntry {
  ts: string
  level: string
  category: string
  code: string
  pharmacy_id?: string | null
  account_id?: string | null
  entity_type?: string | null
  entity_id?: string | null
  correlation_id?: string | null
  detail?: Record<string, string> | null
}
export const getSystemLogs = (params?: { date?: string; level?: string; category?: string; q?: string }) => {
  const qs = new URLSearchParams()
  if (params?.date) qs.set('date', params.date)
  if (params?.level) qs.set('level', params.level)
  if (params?.category) qs.set('category', params.category)
  if (params?.q) qs.set('q', params.q)
  const s = qs.toString()
  return request<{ date: string; events: SystemLogEntry[]; truncated: boolean; available_dates: string[] }>('GET', `/system/logs${s ? `?${s}` : ''}`)
}

// D-WB-3: pharmacy-admin custom field definitions (dropdowns w/ per-option colors).
export interface DropdownOption { label: string; color?: string | null; text?: string | null }
export interface CustomFieldDef {
  id: string
  field_name: string
  field_type: string
  dropdown_options: string | null   // JSON: string[] (legacy) or DropdownOption[]
  sort_order: number
  is_required: boolean
  is_active: boolean
  created_at: string
}
// ASP-80: self-service WorkBench field administration (admin only, audited).
// The id may be a prescription OR a fill: the Rx list and drawer pass the fill on a fill-grain row,
// and the server scopes the history to whichever was asked for (`scope` says which it answered).
export const getWorkbenchFieldHistory = (rxId: string, field: string) =>
  request<{ rx_item_id: string; fill_item_id: string | null; scope: 'fill' | 'prescription'; field: string; count: number; items: { changed_by: string | null; actor_role: string | null; from: string | null; to: string | null; at: string }[] }>(
    'GET', `/rx/${rxId}/workbench/history?field=${encodeURIComponent(field)}`)
export const undoWorkbenchBackfill = (batchId: string) =>
  request<{ batch_id: string; rows_cleared: number; message: string }>(
    'POST', `/workbench/backfill/${batchId}/undo`)
export const putWorkbenchSelectionMode = (fieldKey: string, mode: 'single' | 'multi') =>
  request<{ field_key: string; selection_mode: string; saved: boolean }>(
    'PUT', `/workbench/field-defs/${encodeURIComponent(fieldKey)}/selection-mode`, { mode })
export const collapseWorkbenchMulti = (fieldKey: string, priority: string[]) =>
  request<{ field_key: string; collapsed: number; message: string }>(
    'POST', `/workbench/field-defs/${encodeURIComponent(fieldKey)}/collapse-multi`, { priority })
export const previewWorkbenchBackfill = (fieldKey: string) =>
  // `unit` is what rows_matched COUNTS. On a per-fill tenant the Rx list draws one row per fill and
  // reads that fill's own value, so the number the operator is approving is fills, not
  // prescriptions — and a message that names the wrong one cannot be reconciled with the grid.
  request<{ batch_id: string; field_key: string; has_default: boolean; default_value?: string; rows_matched: number; total_rows?: number; unit?: 'fills' | 'prescriptions'; message: string }>(
    'POST', `/workbench/field-defs/${encodeURIComponent(fieldKey)}/backfill-preview`)
export const applyWorkbenchBackfill = (fieldKey: string, batchId: string) =>
  request<{ batch_id: string; rows_written: number; status: string; message: string }>(
    'POST', `/workbench/field-defs/${encodeURIComponent(fieldKey)}/backfill-apply`, { batch_id: batchId })
export const putWorkbenchFieldDef = (fieldKey: string, body: { display_name: string; field_kind?: string; sort_order?: number }) =>
  request<{ field_key: string; saved: boolean }>('PUT', `/workbench/field-defs/${encodeURIComponent(fieldKey)}`, body)
export const putWorkbenchFieldValues = (fieldKey: string, values: { value: string; sort_order?: number; color_bg?: string | null; color_text?: string | null; is_default?: boolean }[]) =>
  request<{ field_key: string; values: number; saved: boolean }>('PUT', `/workbench/field-defs/${encodeURIComponent(fieldKey)}/values`, { values })

// D-CFD-1 slice 1: dry run only — this endpoint never writes a prescription.
export const previewCustomFieldBackfill = (fieldId: string) =>
  request<{ batch_id: string; field_id: string; field_name: string; has_default: boolean
            default_value?: string; rows_matched: number; total_prescriptions?: number
            dry_run: boolean; message: string }>('POST', `/custom-fields/${fieldId}/backfill-preview`)

// D-CFD-1 slices 2+3: apply (writes fill-if-empty), undo (reverses a batch), history.
export const applyCustomFieldBackfill = (fieldId: string, batchId: string) =>
  request<{ batch_id: string; rows_written: number; status: string; message: string; applied_value: string }>(
    'POST', `/custom-fields/${fieldId}/backfill-apply`, { batch_id: batchId })
export const undoCustomFieldBackfill = (batchId: string) =>
  request<{ batch_id: string; rows_cleared: number; status: string; message: string }>(
    'POST', `/custom-fields/backfill/${batchId}/undo`)
export const listCustomFieldBackfills = () =>
  request<{ items: Array<{ batch_id: string; field_id: string; value: string; status: string; rows_matched: number; rows_written: number; created_at: string; applied_at: string | null; undone_at: string | null }>; count: number }>(
    'GET', '/custom-fields/backfill/history')

// CV-2c: inline editing of a single custom-field value on an Rx (writes rx_custom_field_values).
export type RxCustomFieldValue = { field_id: string; value_text?: string | null; value_date?: string | null; value_number?: number | null; value_bool?: boolean | null }
export const setRxCustomFieldValue = (rxId: string, v: RxCustomFieldValue) =>
  request<{ rx_id: string; fields_set: number }>('PUT', `/rx/${rxId}/custom-fields`, { values: [v] })
// BULK-3: the endpoint has always taken an ARRAY — writing N fields for one Rx is ONE round
// trip, one MERGE pass and one audit row, not N. Multi-field bulk change uses this so the
// per-Rx request count stays flat no matter how many fields the user picks (tenant DB is
// 1-vCore serverless; fan-out is the thing that hurts it).
export const setRxCustomFieldValues = (rxId: string, values: RxCustomFieldValue[]) =>
  request<{ rx_id: string; fields_set: number }>('PUT', `/rx/${rxId}/custom-fields`, { values })
export const listCustomFieldDefs = () =>
  request<{ items: CustomFieldDef[]; count: number }>('GET', '/custom-fields')
export const createCustomFieldDef = (body: { field_name: string; field_type: string; dropdown_options?: string | null; sort_order?: number; is_required?: boolean }) =>
  request<{ id: string; field_name: string }>('POST', '/custom-fields', body)
export const updateCustomFieldDef = (id: string, body: { field_name: string; field_type: string; dropdown_options?: string | null; sort_order?: number; is_required?: boolean }) =>
  request<{ id: string; updated: boolean }>('PUT', `/custom-fields/${id}`, body)
export const deleteCustomFieldDef = (id: string) =>
  request<{ id: string; deactivated: boolean }>('DELETE', `/custom-fields/${id}`)

// Shared workbench spec §5 color tokens for the option-color picker.
export const WB_COLOR_TOKENS: Array<{ name: string; bg: string; text: string }> = [
  { name: 'Gray', bg: '#E5E5E5', text: '#666666' },
  { name: 'Gold', bg: '#FFEBC0', text: '#8F6200' },
  { name: 'Peach', bg: '#FED5D1', text: '#A0410D' },
  { name: 'Mint', bg: '#CAF0CC', text: '#437406' },
  { name: 'Blue', bg: '#D4E7F6', text: '#0068B8' },
  { name: 'Mauve', bg: '#E6E0F0', text: '#7252AA' },
  { name: 'Orange', bg: '#CA5010', text: '#FFFFFF' },
  { name: 'Red', bg: '#D13438', text: '#FFFFFF' },
  { name: 'Green', bg: '#498205', text: '#FFFFFF' },
  { name: 'Navy', bg: '#004E8C', text: '#FFFFFF' },
]

// DrugReference module (D-DRUGREF-1) — My-pharmacy reference table, gated by the drugreference module.
export interface DrugRefRow {
  id: string; pharmacy_id: string | null
  medication_name: string | null; generic_name: string | null; brand_name: string | null
  category: string | null; gcn: string | null; ndc: string | null
  rems_program: string | null; substitution_targets: string | null
  days_before_procedure: number | null; copay_program: string | null
  is_active: boolean; source: string; needs_classification: boolean; updated_at: string
  daw_flags: string | null  // Pioneer-derived DAW codes (R-4: 0=substitution permitted, 1=DAW)
  care_plan_frequency: string | null  // G-35
}
// D-CRX-42 G-11: clinical column set from the linked carerx_formulary row (D-CRX-43 §4 seam).
export interface DrugRefClinical {
  nurse_needed: string | null; infusion_site_only: boolean | null
  requires_enrollment_signature: boolean; pump_required: boolean
  filter_type: string | null; pregnancy_test: string | null
  competency_requirements: string[]; observation_minutes: number | null
  baseline_analytes: string[]; rescreen_analytes: string[]
  rescreen_frequency: string | null; therapy_archetype: string; link_count: number
  // D-CRX-46 Set A: §5/§6/§7 are protocol references into the admin protocol library + per-drug overrides
  // D-CRX-67: what prints on the enrollment form's §05/§06/§07, as that form's own option keys
  // (enrollmentFormSpec.json). Stored as JSON arrays; null means nothing selected.
  form_premed_selections?: string[] | string | null
  form_flush_selections?: string[] | string | null
  form_kit_selections?: string[] | string | null
  premed_protocol_id: string | null; premed_overrides: string | null
  kit_protocol_id: string | null; kit_overrides: string | null
  flush_protocol_id: string | null; flush_overrides: string | null
  pump_type: string | null; named_nurse_only: boolean; named_nurse_ids: string | null
  // D-CRX-46 Set B — administration parameters (moved off the draft protocols onto the drug record)
  rate_model: string | null
  rate_first_start: number | null; rate_first_increment: number | null; rate_first_interval_min: number | null; rate_first_max: number | null
  rate_subsequent_start: number | null; rate_subsequent_increment: number | null; rate_subsequent_interval_min: number | null; rate_subsequent_max: number | null
  diluent: string | null; standard_final_volume_ml: number | null
  infusion_duration_min: number | null; setup_minutes: number | null; visit_length_override_min: number | null
  gravity_permitted: string | null; gravity_rationale: string | null; nurse_watchouts: string | null
  kit_required: boolean; filter_label_text: string | null
  no_iv_push: boolean; dedicated_line: boolean; protect_from_light: boolean
  sequence_dependent: boolean; resume_50pct_on_reaction: boolean; no_bolus_flush: boolean
  // D-CRX-55 v4: copay assistance quad + flush constraint list (JSON string of [{text, source}])
  copay_phone: string | null; copay_url: string | null
  copay_eligibility: string | null; copay_status: string | null
  flush_constraints: string | null
}
// D-CRX-55: drug specific assessment questions (prompt doc §11) + field provenance (§15) + AI suggest.
export interface DrugAssessQ {
  id: string; question_text: string; question_type: string; options: string | null
  expected_answer: string | null; out_of_expected: string; answered_by: string; asked_when: string
  required: boolean; tag: string; origin: string; sort_order: number
}
export const listDrugRefAssessment = (drugId: string) =>
  request<{ items: DrugAssessQ[]; count: number; formulary_id: string | null }>('GET', `/drug-reference/${drugId}/assessment`)
export const addDrugRefAssessment = (drugId: string, b: {
  question_text: string; question_type: string; options?: string[]; expected_answer?: string
  out_of_expected?: string; answered_by?: string; asked_when?: string; required?: boolean
  tag?: string; origin?: string; action?: string; confidence?: string; source_text?: string
}) => request<{ id: string; action: string }>('POST', `/drug-reference/${drugId}/assessment`, b)
export const updateDrugRefAssessment = (qid: string, b: { question_text?: string; expected_answer?: string; out_of_expected?: string; required?: boolean }) =>
  request<{ id: string; saved: boolean }>('PUT', `/drug-reference/assessment/${qid}`, b)
export const removeDrugRefAssessment = (qid: string) =>
  request<{ id: string; removed: boolean }>('DELETE', `/drug-reference/assessment/${qid}`)
export interface DrugProvRow { field: string; action: string; value_before: string | null; value_after: string | null; confidence: string | null; source_text: string | null; actor_id: string | null; acted_at: string }
export const listDrugRefProvenance = (drugId: string) =>
  request<{ items: DrugProvRow[]; count: number }>('GET', `/drug-reference/${drugId}/provenance`)
export const addDrugRefProvenance = (drugId: string, b: { field: string; action: string; value_before?: string; value_after?: string; confidence?: string; source_text?: string }) =>
  request<{ recorded: boolean }>('POST', `/drug-reference/${drugId}/provenance`, b)
export interface AiFieldSuggestion { field: string; value: unknown; confidence: string; source_text?: string | null; note?: string | null }
export const aiSuggestDrugRef = (drugId: string, section: string, splText?: string) =>
  request<{ section: string; suggestions: unknown[]; dropped: number; batch_at: string; model: string }>(
    'POST', `/drug-reference/${drugId}/ai-suggest`, { section, spl_text: splText })
// D-PERF-1: server-paged catalogue. Search and the category / source / active filters are the
// SERVER's now — filtering a fetched page would hide matches on every other page.
export const listDrugReference = (opts?: {
  page?: number; pageSize?: number; q?: string; category?: string; source?: string; includeInactive?: boolean
  /** D-UI-2: header funnels, `key:op:value`, sent as repeated `filter` parameters. */
  filter?: string[]
}) => {
  const p = new URLSearchParams()
  if (opts?.page) p.set('page', String(opts.page))
  if (opts?.pageSize) p.set('page_size', String(opts.pageSize))
  if (opts?.q?.trim()) p.set('q', opts.q.trim())
  if (opts?.category) p.set('category', opts.category)
  if (opts?.source) p.set('source', opts.source)
  if (opts?.includeInactive) p.set('include_inactive', '1')
  for (const f of opts?.filter ?? []) if (f) p.append('filter', f)
  const qs = p.toString()
  return request<{
    items: DrugRefRow[]; count: number; total?: number; page?: number; page_size?: number
    total_pages?: number; truncated?: boolean
    custom_values?: Record<string, Record<string, unknown>>; clinical?: Record<string, DrugRefClinical>
  }>('GET', `/drug-reference${qs ? `?${qs}` : ''}`)
}
// G-11 fan-out save (WS16): writes the clinical set to every linked formulary row.
export const setDrugRefClinical = (drugId: string, input: Partial<Omit<DrugRefClinical, 'link_count'>>) =>
  request<{ id: string; saved: boolean; fanout: number }>('PUT', `/drug-reference/${drugId}/clinical`, input)

// D-CRX-46 LB-2: administration protocol library (Flushing / Premedication / Anaphylaxis kit).
export interface AdminProtocolLine {
  seq: number; item: string | null; is_free_text: boolean
  volume_ml: string | null; timing: string | null; source: string | null; sig: string | null
}
export interface AdminProtocol {
  id: string; scope: string; code: string; category: string; name: string; version: number
  is_default: boolean; timing_minutes_prior: number | null; prints_to: string | null; note: string | null
  active: boolean; lines: AdminProtocolLine[]
}
export interface AdminProtocolComponent {
  id: string; scope: string; comp_type: string; name: string; default_sig: string | null; active: boolean
}
export const listCareRxAdminProtocols = () =>
  request<{ protocols: AdminProtocol[]; components: AdminProtocolComponent[]; count: number }>('GET', '/carerx/admin-protocols')

// D-CRX-47 POT-6: nurse roster + team management.
export interface NurseLogin { email: string | null; name: string | null; status: string | null }
export interface NurseRow { id: string; nurse_id: string; display_name: string | null; territory_zips: string | null; competencies: string | null; max_daily_visits: number | null; active: boolean; team_count: number; login: NurseLogin | null }
export const listCareRxNurses = (agencyId?: string) => request<{ items: NurseRow[]; count: number }>('GET', `/carerx/nurses${agencyId ? `?agency=${agencyId}` : ''}`)
// D-CRX-49: nurses with a login + CareRx visits but no roster record (orphan logins).
export interface UnrosteredNurse { account_id: string; email: string | null; name: string | null; status: string | null; visit_count: number }
export const listCareRxUnrosteredNurses = () => request<{ items: UnrosteredNurse[]; count: number }>('GET', '/carerx/nurses/unrostered')
export const createCareRxNurse = (b: { display_name: string; territory_zips?: string; competencies?: string; max_daily_visits?: number; account_id?: string; agency_id?: string }) =>
  request<{ id: string; nurse_id: string; agency: boolean }>('POST', '/carerx/nurses', b)
export const updateCareRxNurse = (id: string, b: { display_name?: string; territory_zips?: string; competencies?: string; max_daily_visits?: number; active?: boolean }) =>
  request<{ id: string; saved: boolean }>('PUT', `/carerx/nurses/${id}`, b)
export interface NurseTeamMember { roster_id: string; display_name: string | null; is_manager: boolean }
export interface NurseTeam { id: string; name: string; active: boolean; members: NurseTeamMember[] }
export const listCareRxNurseTeams = (agencyId?: string) => request<{ items: NurseTeam[]; count: number }>('GET', `/carerx/nurse-teams${agencyId ? `?agency=${agencyId}` : ''}`)
export const createCareRxNurseTeam = (b: { name: string; agency_id?: string }) => request<{ id: string; name: string }>('POST', '/carerx/nurse-teams', b)
export const updateCareRxNurseTeam = (id: string, b: { name?: string; active?: boolean }) => request<{ id: string; saved: boolean }>('PUT', `/carerx/nurse-teams/${id}`, b)
export const addCareRxTeamMember = (teamId: string, b: { roster_id: string; is_manager?: boolean }) => request<any>('POST', `/carerx/nurse-teams/${teamId}/members`, b)
export const removeCareRxTeamMember = (teamId: string, rosterId: string) => request<any>('DELETE', `/carerx/nurse-teams/${teamId}/members/${rosterId}`)

// D-CRX-46 LB-2 (Set C), rebuilt per CareRx_Supply_Generator_Handoff.md: supply catalog + rules
// pick lists + history + AI supply lookup.
export interface SupplyCategory { id: string; code: string; name: string; sort_order: number; active: boolean }
export interface SupplyItem {
  id: string; name: string; vendor_item_number: string | null; unit_price: number | null; category: string | null
  unit_of_issue: string; each_qty: number; is_kit: boolean; kit_contents: string | null
}
export interface SupplyPumpSet { id: string; name: string; pump: string; filter_micron: string | null; priming_volume_ml: number | null; vendor_item_number: string | null }
export interface SupplyDrug {
  formulary_id: string; name: string; brand_name: string | null; supply_route: string | null; route: string | null
  filter_micron: string | null; default_pack_level: string | null; approved: boolean
  gravity_permitted: string | null; diluent: string | null; standard_final_volume_ml: number | null
  rescue_override_reason: string | null; drug_reference_id: string | null
}
export const getCareRxSupplyCatalog = () =>
  request<{ categories: SupplyCategory[]; items: SupplyItem[]; pump_sets: SupplyPumpSet[]; drugs: SupplyDrug[]; rules_version: string }>('GET', '/carerx/supply-catalog')
export interface PickListItem {
  id?: string; name: string; vendor_item_number: string | null; generated_qty: string; picked_qty?: string | null
  unit_price: number | null; deviation?: boolean; qty_num?: number | null; each_qty?: number | null
  unit_of_issue?: string | null; ext_price?: number | null; rule_code?: string | null; rule_note?: string | null
  category?: string | null; unmet?: boolean
}
export interface PickListUnmet { item_name: string; rule_code: string; qty: number; reason: string | null; rule_note: string | null }
export interface PickListExcluded { item_name: string; rule_code: string; reason: string }
export interface PickListProfileChips {
  filter_min_micron: number | null; filter_max_micron: number | null; diluent: string | null
  standard_final_volume_ml: number | null; final_conc_min_mg_ml: number | null; final_conc_max_mg_ml: number | null
  co_infusion_allowed: boolean | null; premedication_required: boolean | null
  stability_window: string | null; stability_room_temp_hours: number | null; stability_refrigerated_hours: number | null
  source_citation: string | null; rescue_override_reason: string | null
}
export interface PickListResult {
  id: string; formulary_id: string; drug_name: string | null; label: string | null; pack_level: string
  site_of_care: string | null; access: string | null; pump: string | null; pump_set: string | null
  priming_volume_ml: number | null; state: 'complete' | 'incomplete'; rules_version: string
  items: PickListItem[]; count: number; total_cost: number
  unmet: PickListUnmet[]; excluded: PickListExcluded[]
  concentration_note: string | null; rule_errors: string[]; profile: PickListProfileChips | null
}
export const generateCareRxPickList = (body: { formulary_id: string; site_of_care: string; access?: string; pump?: string; pack_level?: string; fill_id?: string; label?: string }) =>
  request<PickListResult>('POST', '/carerx/pick-lists/generate', body)
export interface PickListHistoryRow {
  id: string; formulary_id: string | null; drug_name: string | null; label: string | null
  state: string | null; pack_level: string | null; access: string | null; site_of_care: string | null
  pump: string | null; total_cost: number | null; reuse_count: number
  last_used_at: string | null; generated_at: string; rules_version: string | null; archived: boolean
}
export const listCareRxPickLists = (formularyId?: string) =>
  request<{ items: PickListHistoryRow[]; count: number }>('GET', `/carerx/pick-lists${formularyId ? `?formulary_id=${formularyId}` : ''}`)
export interface SavedPickList { header: any; items: PickListItem[]; count: number }
export const reuseCareRxPickList = (id: string) => request<SavedPickList>('POST', `/carerx/pick-lists/${id}/reuse`)
export const patchCareRxPickList = (id: string, b: { label?: string; archived?: boolean }) =>
  request<{ id: string; updated: boolean }>('PUT', `/carerx/pick-lists/${id}`, b)
export const approveCareRxSupplyProfile = (formularyId: string, b: { approve: boolean; rescue_override_reason?: string }) =>
  request<{ formulary_id: string; approved: boolean }>('POST', `/carerx/supply-profile/${formularyId}/approve`, b)
// Read-only profile summary for decorating surfaces (enrollment Supply readiness strip).
// Fail-open by contract: callers render nothing on any error.
export interface SupplyProfileSummary {
  formulary_id: string; drug_name: string | null; approved: boolean; approved_at: string | null
  rules_version: string; supply_route: string | null; diluent: string | null
  standard_final_volume_ml: number | null; final_conc_min_mg_ml: number | null; final_conc_max_mg_ml: number | null
  filter_min_micron: number | null; filter_max_micron: number | null; infusion_duration_min: number | null
  flush_required: boolean; co_infusion_allowed: boolean; rescue_kit_required: boolean
  rescue_override_reason: string | null; saved_list_count: number
}
export const getCareRxSupplyProfileSummary = (formularyId: string) =>
  request<SupplyProfileSummary>('GET', `/carerx/supply-profile/${formularyId}`)
// AI supply lookup — allow-listed official sources only; every value carries its citation.
export interface SupplyCitation { field: string; url: string; title: string | null; quoted_text: string | null; source_tier: string }
export interface SupplyAiLookup {
  id: string; formulary_id?: string; drug_name?: string; status: string; model: string | null
  fields: Record<string, unknown> | null; citations: SupplyCitation[] | null; dropped?: string[]
  search_count: number | null; input_tokens: number | null; output_tokens: number | null
  error_detail?: string | null; accepted_at?: string | null; created_at?: string
}
export const runCareRxSupplyAiLookup = (b: { formulary_id?: string; drug_reference_id?: string }) =>
  request<SupplyAiLookup>('POST', '/carerx/supply/ai-lookup', b)
export const listCareRxSupplyAiLookups = (formularyId: string) =>
  request<{ items: SupplyAiLookup[]; count: number }>('GET', `/carerx/supply/ai-lookup?formulary_id=${formularyId}`)
export const acceptCareRxSupplyAiLookup = (id: string, fields?: string[]) =>
  request<{ accepted: string[]; citations_saved: number; formulary_id: string }>('POST', `/carerx/supply/ai-lookup/${id}/accept`, { fields })
// D-CRX-42 WS1 break #4: the working cell endpoint finally gets a caller.
export const setDrugRefCell = (drugId: string, input: { field_def_id: string; value_text?: string | null; value_date?: string | null; value_number?: number | null; value_bool?: boolean | null }) =>
  request<{ saved: boolean }>('PUT', `/drug-reference/${drugId}/cell`, input)

// ---- D-CRX-42 WS15: Site of Care (types + facility directory) ----
export interface SiteOfCareType { code: string; label: string; requires_facility: boolean; is_active: boolean; is_protected: boolean; sort_order: number; notes: string | null; facility_count: number }
// Every field here is one the server actually projects (SiteOfCareFunctions.FacilityList).
// D-SOC-4 F-2: five more used to be declared below this line under a "D-SOC-3: facility contact +
// operations" heading — contact_name, contact_phone, contact_email, operating_hours,
// delivery_cutoff_time — with no column on dbo.facilities, no SELECT projecting them and no member
// on FacilityBody to accept them. Being optional, they compiled clean and CI stayed green, so the
// interface promised data the API could never return; anything rendering them would have shown
// `undefined` in production with nothing failing first. Removed 2026-09-20 (Patrick's call) rather
// than left as a standing invitation to build against them. If D-SOC-3 is later specified, they
// come back WITH a migration, a projection and a request body — see
// docs/audits/REVIEW-2026-09-20-site-of-care.md §2.
export interface FacilityRow { id: string; name: string; facility_type: string | null; address_line: string | null; city: string | null; state_code: string | null; zip_code: string | null; phone: string | null; email: string | null; wing: string | null; room: string | null; bed: string | null; source: string; is_active: boolean; notes: string | null; used_by: number
  // D-SOC-3: facility contact + operations (mig 402)
  contact_name?: string | null; contact_phone?: string | null; contact_email?: string | null; operating_hours?: string | null; delivery_cutoff_time?: string | null }
export const listSiteOfCareTypes = (activeOnly?: boolean) =>
  request<{ items: SiteOfCareType[] }>('GET', `/site-of-care/types${activeOnly ? '?active=1' : ''}`)
export const updateSiteOfCareType = (code: string, input: { label?: string; requires_facility?: boolean; is_active?: boolean; notes?: string }) =>
  request<{ code: string; saved: boolean }>('PUT', `/site-of-care/types/${code}`, input)
export const addSiteOfCareType = (input: { code: string; label: string; requires_facility?: boolean }) =>
  request<{ code: string; saved: boolean }>('POST', '/site-of-care/types', input)
/**
 * D-SOC-5 S1: the directory is capped server-side (1000 rows) and reports `truncated` when the cap
 * bites. Not paged — whether it should be is still open on the ASP row count (D-SOC-5 §3.2). The
 * UI must SAY when it is showing a partial list; a silently truncated directory is worse than an
 * uncapped one, because the facility you cannot find looks deleted rather than hidden.
 */
/**
 * One PAGE of the facility directory. Server-side paging, search and sort (E-rules): this used to
 * return every facility with no TOP, and the settings tab filtered the whole array in the browser.
 * @param opts page (1-based), page_size (server caps at 200), q (name/address/city/zip),
 *             sort (name|city|zip|type|used_by — whitelisted server-side), dir.
 */
export const listFacilities = (
  type?: string,
  activeOnly?: boolean,
  opts?: { page?: number; pageSize?: number; q?: string; sort?: string; dir?: 'asc' | 'desc'; filter?: string[] },
) => {
  const p = new URLSearchParams()
  if (type) p.set('type', type)
  if (activeOnly) p.set('active', '1')
  if (opts?.page) p.set('page', String(opts.page))
  if (opts?.pageSize) p.set('page_size', String(opts.pageSize))
  if (opts?.q) p.set('q', opts.q)
  if (opts?.sort) p.set('sort', opts.sort)
  if (opts?.dir) p.set('dir', opts.dir)
  // D-UI-2: header funnels, `key:op:value`, sent as repeated `filter` parameters.
  for (const f of opts?.filter ?? []) if (f) p.append('filter', f)
  const qs = p.toString()
  return request<{ items: FacilityRow[]; total?: number; page?: number; page_size?: number }>(
    'GET', `/site-of-care/facilities${qs ? '?' + qs : ''}`)
}
/**
 * D-SOC-4 F-7: explicit input shape rather than `Partial<FacilityRow> & { name }`. The old
 * signature invited a PATCH-style body, and the server assigned every column straight from the
 * payload — so `{id, name, notes}` nulled the address, zip, phone, type and wing/room/bed, and
 * nulling zip additionally dropped the row out of its own duplicate-guard index. The server is
 * COALESCE-preserve now; this keeps a caller from depending on the old behaviour by accident.
 * Omitting `id` creates; supplying one edits and 404s if it does not exist.
 */
export interface FacilityUpsertInput {
  id?: string
  name: string
  facility_type?: string | null
  address_line?: string | null
  city?: string | null
  state_code?: string | null
  zip_code?: string | null
  phone?: string | null
  email?: string | null
  wing?: string | null
  room?: string | null
  bed?: string | null
  notes?: string | null
  // D-SOC-3 (mig 402). These MUST be here: the settings modal edits them, and the server MERGE
  // is a plain overwrite — an omitted member binds DBNull and NULLS THE COLUMN. While they were
  // missing from this type the call site cast them away, so renaming a facility silently wiped
  // its contact name, phone, email, operating hours and delivery cutoff.
  contact_name?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  operating_hours?: string | null
  delivery_cutoff_time?: string | null
}
export const upsertFacility = (input: FacilityUpsertInput) =>
  request<{ id: string; saved: boolean }>('POST', '/site-of-care/facilities', input)
export const setFacilityActive = (id: string, isActive: boolean) =>
  request<{ id: string; is_active: boolean }>('PUT', `/site-of-care/facilities/${id}/active`, { is_active: isActive })
export const upsertDrugReference = (input: Partial<DrugRefRow>) =>
  request<{ id: string; saved: boolean }>('POST', '/drug-reference', input)
export const listDrugCategories = () =>
  request<{ items: { name: string; sort_order: number; is_global: boolean; color: string | null }[] }>('GET', '/drug-reference/categories')
export const addDrugCategory = (name: string, color?: string) =>
  request<{ name: string; saved: boolean }>('POST', '/drug-reference/categories', { name, color })
export const setDrugCategoryColor = (name: string, color: string | null) =>
  request<{ name: string; color: string | null; saved: boolean }>('PUT', '/drug-reference/categories/color', { name, color })
export const listDrugRefColumns = () =>
  request<{ items: { id: string; field_name: string; field_type: string; dropdown_options: string | null; sort_order: number }[] }>('GET', '/drug-reference/columns')
export const addDrugRefColumn = (input: { name: string; type: string; dropdown_options?: string }) =>
  request<{ id: string; saved: boolean }>('POST', '/drug-reference/columns', input)
export const syncDrugReferenceFromRx = () =>
  request<{ synced: boolean; rows: number }>('POST', '/drug-reference/sync-from-rx', {})

// Per-Rx DrugReference field resolver for the Rx view builder (D-DRUGREF-3). Module-gated server-side.
export interface RxDrugRefFields { category: string | null; rems_program: string | null; days_before_procedure: number | null; copay_program: string | null; category_color?: string | null }
export const getDrugRefForRx = (rxId: string) =>
  request<RxDrugRefFields>('GET', `/rx/${rxId}/drug-reference`)

// ---- D-FILL-1 WS5 slice 1: the fills of one prescription, each as its own Rx ----
// Fetched SEPARATELY from the core Rx query (same posture as DrugRef above), so a
// failure or an empty result leaves the prescription page exactly as it is today.
export interface RxFillPage {
  id: string
  display_rx_number: string
  rx_number: string
  fill_number: number
  state: string | null
  pioneerrx_tx_status: string | null
  date_filled_utc: string | null
  quantity_dispensed: number | null
  days_supply: number | null
  patient_paid_amount: number | null
  insurance_carrier: string | null
}
export const getRxFillPages = (rxId: string) =>
  request<{ fill_pages: RxFillPage[] }>('GET', `/rx/${rxId}/fill-pages`)

// Batch per-Rx DrugReference resolver for list columns (D-DRUGREF-3b). Module-gated server-side.
//
// D-RXFILTER-1 WS0 — CHUNKED, for the same reason BatchCustomFieldValues is (D-PERF-7 Z2).
// This call used to post the whole loaded set in ONE request while the server applied
// `.Take(500)`: a `dr:` sort or header filter escalates the id set to every loaded row (up to
// the 5,000-row cap), so up to 4,500 rows came back with NO values and were then sorted and
// filtered AS EMPTY — a silent wrong-data bug, not a slow one. Identical failure to the one
// CustomFieldValueFunctions records as "The old Take(1000) silently DROPPED ids".
//
// Chunk + concurrency deliberately mirror CFV_CHUNK / CFV_CONCURRENCY so both batch resolvers
// put the same shape of load on a 0.5-1 vCore tenant DB (the 2026-08-06 outage class).
const DRUGREF_CHUNK = 200
const DRUGREF_CONCURRENCY = 2
export const batchDrugRefForRx = async (rxIds: string[]) => {
  const ids = Array.from(new Set(rxIds)).filter(Boolean)
  const post = (chunk: string[]) =>
    request<{ values: Record<string, RxDrugRefFields>; truncated?: boolean }>(
      'POST', '/drug-reference/for-rx-batch', { rx_ids: chunk })
  if (ids.length === 0) return { values: {} as Record<string, RxDrugRefFields> }
  if (ids.length <= DRUGREF_CHUNK) return post(ids)
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += DRUGREF_CHUNK) chunks.push(ids.slice(i, i + DRUGREF_CHUNK))
  const values: Record<string, RxDrugRefFields> = {}
  let next = 0
  const worker = async () => {
    while (next < chunks.length) {
      const mine = chunks[next++]
      const r = await post(mine)
      Object.assign(values, r.values ?? {})
    }
  }
  await Promise.all(Array.from({ length: Math.min(DRUGREF_CONCURRENCY, chunks.length) }, worker))
  return { values }
}

// ---- Security dashboard (D-SEC-2 Phase 1) — read-only admin posture ----
export interface SecurityGate { key: string; name: string; weight: number; score: number; percent_complete: number; drag: string }
export interface SecurityControl { id: string; name: string; cite: string; status: string; evidence: string; tasks: string }
export interface SecurityTask { id: string; title: string; severity: string; gate: string }
export interface SecurityProgramControl { id: string; name: string; cite: string; owner: string; status: string }
export interface SecurityStatusResponse {
  generated_at: string
  technical: { score: number; grade: string; gates: SecurityGate[] }
  program: { completeness_percent: number; attested: number; total: number; note: string; controls: SecurityProgramControl[] }
  controls: SecurityControl[]
  open_tasks: SecurityTask[]
  probes: Record<string, any>
  source: string
}
export const getSecurityStatus = () => request<SecurityStatusResponse>('GET', '/ops/security/status')
// ---- Compliance registry / BAA vault (D-SEC-2 Phase 2) ----
export interface ComplianceRecord {
  id: string; control_id: string; record_type: string; title: string
  counterparty: string | null; effective_date: string | null; expiration_date: string | null
  original_filename: string | null; byte_size: number | null; notes: string | null
  updated_at: string; has_document: boolean; status: string
}
export const listComplianceRecords = () =>
  request<{ records: ComplianceRecord[] }>('GET', '/ops/compliance/records')
export const createComplianceRecord = (body: {
  control_id: string; record_type: string; title: string
  counterparty?: string; effective_date?: string; expiration_date?: string; notes?: string
}) => request<{ id: string }>('POST', '/ops/compliance/records', body)
export const updateComplianceRecord = (id: string, body: {
  title?: string; counterparty?: string; effective_date?: string; expiration_date?: string; notes?: string
}) => request<{ updated: boolean }>('PUT', `/ops/compliance/records/${id}`, body)
export const archiveComplianceRecord = (id: string) =>
  request<{ archived: boolean }>('POST', `/ops/compliance/records/${id}/archive`)
export async function uploadComplianceDocument(id: string, file: File): Promise<{ uploaded: boolean; filename: string }> {
  const form = new FormData(); form.append('file', file)
  const res = await apiFetch(`/ops/compliance/records/${id}/document`, { method: 'POST', credentials: 'include', body: form })
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || `Upload failed (${res.status})`)
  return res.json()
}
export const complianceDocumentUrl = (id: string) => `/ops/compliance/records/${id}/document`

// ---- Compliance reporting (D-SEC-2 Phase 3) ----
export interface ComplianceFramework { code: string; name: string; jurisdiction: string; criteria: string }
export interface ComplianceReport {
  id: string; framework: string; jurisdiction: string | null; status: string
  gap_count: number | null; generated_at: string; has_document: boolean
}
export const listComplianceFrameworks = (state: string) =>
  request<{ state: string; frameworks: ComplianceFramework[] }>('GET', `/ops/compliance/frameworks?state=${encodeURIComponent(state)}`)
export const generateComplianceReport = (framework: string, jurisdiction: string) =>
  request<{ id: string; framework: string; jurisdiction: string; gap_count: number }>('POST', '/ops/compliance/reports', { framework, jurisdiction })
export const listComplianceReports = () =>
  request<{ reports: ComplianceReport[] }>('GET', '/ops/compliance/reports')
export const complianceReportUrl = (id: string) => `/ops/compliance/reports/${id}/document`
// ---- API / integration configuration (read-only posture, SaaS-only) ----
export interface ApiConfigStatus {
  generated_at: string
  integrations: { name: string; configured: boolean; detail: string }[]
  settings: { key: string; value: string }[]
  secrets: { name: string; present: boolean; source: string }[]
  note: string
}
export const getApiConfig = () => request<ApiConfigStatus>('GET', '/ops/api-config/status')

// ── Data API governance (D-API-1 Phase 3c, SaaS admin) ───────────────────────────────────────────
/** Platform-scoped limits, as the SaaS-admin surface sees them. Distinct from DataApiLimits above,
 *  which is the pharmacy-facing view (clients_used, no budgets) returned by the bootstrap call. */
export type DataApiPlatformLimits = {
  max_clients: number
  max_keys_per_client: number
  max_webhooks: number | null
  max_rows_per_day: number | null
  max_wakes_per_day: number | null
  /** false = no row yet, so the values shown are platform defaults rather than a choice. */
  configured: boolean
}

/** One (client, metric) total over the trailing 7 days, straight from the meter. */
export type DataApiUsageRow = {
  client_id: string
  client_name: string | null
  metric: string
  total: number
}

export type DataApiGovernance = {
  pharmacy_id: string
  limits: DataApiPlatformLimits
  usage_7d: DataApiUsageRow[]
}

/** PATCH semantics: omit a field to leave it unchanged. clearBudgets removes both budgets, which is
 *  otherwise unsayable because omitted already means "unchanged".
 *
 *  camelCase on the way IN, snake_case on the way OUT — the request binds to a C# DTO with
 *  PropertyNameCaseInsensitive (which ignores case but NOT underscores), while responses are shaped
 *  by hand in the handler. Mismatching this silently binds null and looks like a no-op save. */
export type DataApiLimitsPatch = {
  maxClients?: number
  maxKeysPerClient?: number
  maxWebhooks?: number
  maxRowsPerDay?: number
  maxWakesPerDay?: number
  clearBudgets?: boolean
}

export const getDataApiGovernance = (pharmacyId: string) =>
  request<DataApiGovernance>('GET', `/platform/data-api/pharmacies/${pharmacyId}/limits`)

export const setDataApiLimits = (pharmacyId: string, patch: DataApiLimitsPatch) =>
  request<DataApiPlatformLimits>('PUT', `/platform/data-api/pharmacies/${pharmacyId}/limits`, patch)

// ── Platform oversight of a pharmacy's Data API (2026-09-18) ─────────────────────────────────────
export type AdminDataApiClient = {
  id: string; name: string; purpose: string | null; status: string
  cert_pinned: boolean; baa_ref: string | null; baa_expiry: string | null
  created_at: string; live_keys: number; grants: number; hooks: number
  rate_limit_per_min: number | null; daily_quota: number | null
  max_page_size: number | null; query_timeout_sec: number | null
}
export type AdminDataApiKey = {
  id: string; client_id: string; key_prefix: string; created_at: string
  expires_at: string | null; last_used_at: string | null; revoked: boolean; active: boolean
}
export type AdminDataApiHook = {
  id: string; client_id: string; name: string; url: string; status: string
  consecutive_failures: number; last_error: string | null
  last_success_at: string | null; last_failure_at: string | null
}
export type AdminDataApiDetail = {
  pharmacy_id: string
  clients: AdminDataApiClient[]
  keys: AdminDataApiKey[]
  hooks: AdminDataApiHook[]
}

/** Per-client commercial limits — PLATFORM ONLY. Rate, quota, page size and query timeout decide
 *  what we are willing to serve and what it costs us, so they sit with the party carrying the cost.
 *  PATCH semantics: omit to leave unchanged, clearAll resets every override to the service default. */
export type AdminClientLimits = {
  rate_limit_per_min: number | null
  daily_quota: number | null
  max_page_size: number | null
  query_timeout_sec: number | null
}
export const setAdminClientLimits = (
  pharmacyId: string, clientId: string,
  patch: { rateLimitPerMin?: number; dailyQuota?: number; maxPageSize?: number; queryTimeoutSec?: number; clearAll?: boolean },
) => request<AdminClientLimits>(
  'PUT', `/platform/data-api/pharmacies/${pharmacyId}/clients/${clientId}/limits`, patch)

export const getAdminDataApiDetail = (pharmacyId: string) =>
  request<AdminDataApiDetail>('GET', `/platform/data-api/pharmacies/${pharmacyId}/clients`)

export type AdminDisclosureRow = {
  requested_at: string; client_name: string | null; resource: string
  row_count: number; status: number | null; patient_count: number
}
export const getAdminDataApiDisclosures = (pharmacyId: string, opts?: { page?: number; page_size?: number; filter?: string[] }) => {
  const p = new URLSearchParams({ page: String(opts?.page ?? 0), page_size: String(opts?.page_size ?? 50) })
  for (const f of opts?.filter ?? []) if (f) p.append('filter', f)
  return request<{ disclosures: AdminDisclosureRow[]; total: number; page: number; page_size: number }>(
    'GET', `/platform/data-api/pharmacies/${pharmacyId}/disclosures?${p}`)
}

/** Platform control, both directions. `revoke` is the one that never comes back; everything else is
 *  reversible. Minting keys, creating partners and setting grants stay with the pharmacy — those
 *  decide WHAT a partner sees, which is its minimum-necessary judgement, not ours. */
export type AdminDataApiAction =
  | 'suspend' | 'activate'      // clients
  | 'revoke' | 'disable' | 'enable'  // keys
  | 'pause' | 'resume'          // hooks
export const adminDataApiAction = (
  pharmacyId: string, target: 'clients' | 'keys' | 'hooks', id: string,
  action: AdminDataApiAction, reason: string,
) => request<{ ok: boolean; action: string }>(
  'POST', `/platform/data-api/pharmacies/${pharmacyId}/${target}/${id}/${action}`, { reason })

// ── The pharmacy's own API usage (previously platform-only) ──────────────────────────────────────
export type PharmacyUsage = {
  days: number
  usage: { client_id: string; client_name: string | null; metric: string; total: number }[]
  budgets: { max_rows_per_day: number | null; max_wakes_per_day: number | null }
}
export const getPharmacyDataApiUsage = (days = 7) =>
  request<PharmacyUsage>('GET', `/pharmacy/data-api/usage?days=${days}`)

// ── Wave-1 tenant analytics dashboards (Insights › Analytics) ──────────────
// All read-only; resolve the bound pharmacy's dedicated DB via the enforcing
// server factory. See functions/Workflow/TenantDashboardsFunctions.cs.
export interface SpeedToTherapy {
  completed_count: number
  median_days: number
  p90_days: number
  same_week_pct: number
  distribution: { label: string; count: number }[]
  scoped: boolean
}
export interface StaffProductivity {
  team_members: number
  rx_touches: number
  per_user: { actor: string; role: string | null; actions: number }[]
  queue_depth: { state: string; count: number }[]
  scoped: boolean
}
export interface PriorAuthDash {
  approved: number
  denied: number
  pending: number
  approval_pct: number
  denial_pct: number
  by_state: { state: string; count: number }[]
  scoped: boolean
}
export interface ExceptionsDash {
  open_total: number
  aging_over_3d: number
  by_reason: { reason: string; count: number }[]
  scoped: boolean
}
export interface AdherenceDash {
  total: number
  covered: number
  overdue: number
  due_soon: number
  coverage_pct: number
  scoped: boolean
}
export type DashRange = { from?: string; to?: string }
const dqs = (r?: DashRange) => {
  if (!r || (!r.from && !r.to)) return ''
  const p = new URLSearchParams()
  if (r.from) p.set('from', r.from)
  if (r.to) p.set('to', r.to)
  return `?${p.toString()}`
}
export const getSpeedToTherapy    = (r?: DashRange) => request<SpeedToTherapy>('GET', `/dashboards/speed-to-therapy${dqs(r)}`)
export const getStaffProductivity = (r?: DashRange) => request<StaffProductivity>('GET', `/dashboards/staff-productivity${dqs(r)}`)
export const getPriorAuthDash     = (r?: DashRange) => request<PriorAuthDash>('GET', `/dashboards/prior-auth${dqs(r)}`)
export const getExceptionsDash    = (r?: DashRange) => request<ExceptionsDash>('GET', `/dashboards/exceptions${dqs(r)}`)
export const getAdherenceDash     = (r?: DashRange) => request<AdherenceDash>('GET', `/dashboards/adherence${dqs(r)}`)

// ── D-OBS-1 per-pharmacy latency board (SaaS admin › Monitoring › Latency) ──
// Reads the control-plane rollup; PHI-free. See functions/Admin/LatencyDashboardFunctions.cs.
export type LatencyHealth = 'ok' | 'warn' | 'breach' | 'na'
export interface LatencySlo { operation: string; target_p95_ms: number; warn_p95_ms: number; breach_p95_ms: number; enabled: boolean }
export interface LatencyOpCell { operation: string; n: number; p95_ms: number; cold_resumes: number; errors: number; health: LatencyHealth }
export interface LatencyPharmacyRow { pharmacy_id: string; name: string | null; worst_health: LatencyHealth; operations: LatencyOpCell[] }
export interface LatencyMatrix { window_hours: number; pharmacies: LatencyPharmacyRow[]; slos: LatencySlo[] }
export interface LatencyPoint { bucket_start: string; p50_ms: number; p95_ms: number; p99_ms: number; count: number; cold_resumes: number; errors: number }
export interface LatencySeries { operation: string; points: LatencyPoint[] }
export interface LatencyRecentEvent { operation: string; duration_ms: number; cold_resume: boolean; status: string; route_template: string | null; occurred_at: string }
export interface LatencyDetail { pharmacy_id: string; window_hours: number; series: LatencySeries[]; recent_events: LatencyRecentEvent[]; slos: LatencySlo[] }
export const getLatencyMatrix = (hours = 24) => request<LatencyMatrix>('GET', `/ops/latency?hours=${hours}`)
export const getLatencyDetail = (pharmacyId: string, hours = 24) => request<LatencyDetail>('GET', `/ops/latency/${pharmacyId}?hours=${hours}`)
export interface FinancialSummary {
  rx_count: number
  revenue: number
  cost: number
  profit: number
  margin_pct: number
  copay_captured: number
  top_drugs: { drug: string; revenue: number }[]
  scoped: boolean
}
export const getFinancialSummary = (r?: DashRange) => request<FinancialSummary>('GET', `/dashboards/financial${dqs(r)}`)

// ── D-SHIP-19: per-pharmacy POD document storage (Phase 1 provision + Phase 2 backfill) ──
export interface PodStorageStatus {
  storage_configured: boolean
  migration_applied: boolean
  ready: boolean
  total_docs: number
  migrated: number
  remaining: number
  percent: number
}
export interface PodBackfillBatchResult {
  migrated: number
  failed: number
  remaining: number
  scanned: number
  time_boxed?: boolean
  next_after_created: string | null
  next_after_id: string | null
  ms: number
  failures: { doc_id: string; reason: string }[]
}
export const getPodStorageStatus = (pharmacyId: string) =>
  request<PodStorageStatus>('GET', `/pharmacies/${pharmacyId}/pod-doc-backfill`)
export const ensurePharmacyStorage = (pharmacyId: string) =>
  request<{ pharmacy_id: string; storage_account: string; status: string }>('POST', `/pharmacies/${pharmacyId}/ensure-storage`)
export const runPodBackfillBatch = (pharmacyId: string, batch = 50, after?: { created: string; id: string }) =>
  request<PodBackfillBatchResult>('POST',
    `/pharmacies/${pharmacyId}/pod-doc-backfill?batch=${batch}` +
    (after ? `&after_created=${encodeURIComponent(after.created)}&after_id=${after.id}` : ''))

// ── D-CRX-61: CareRx formulary backfill from dispensing history ────────────
// pharmacy_id is sent for the SaaS-admin case (a platform account has no pharmacy of its
// own, so the server cannot infer the target); a pharmacy-bound caller's own pharmacy wins
// server-side regardless of what is sent.
export type FormularyBackfillCandidate = {
  name: string; ndc: string | null; strength: string | null; brand_name: string | null
  dispense_count: number; first_fill: string | null; last_fill: string | null; linked: boolean
}
export type FormularyBackfillPreview = {
  mode: 'preview'; pharmacy_id: string; candidates: number; auto_linkable: number
  needs_clinical_review: number; limit: number; truncated: boolean
  items: FormularyBackfillCandidate[]
}
export type FormularyBackfillApplied = {
  mode: 'apply'; pharmacy_id: string; created: number; truncated: boolean; review_at: string
}
export const previewFormularyBackfill = (pharmacyId: string, limit = 500, minDispenseCount = 1) =>
  request<FormularyBackfillPreview>('POST', '/carerx/formulary/backfill',
    { mode: 'preview', pharmacy_id: pharmacyId, limit, min_dispense_count: minDispenseCount })
export const applyFormularyBackfill = (pharmacyId: string, limit = 500, minDispenseCount = 1) =>
  request<FormularyBackfillApplied>('POST', '/carerx/formulary/backfill',
    { mode: 'apply', pharmacy_id: pharmacyId, limit, min_dispense_count: minDispenseCount })

// ── D-CRX-81: CareRx e-sign refresh/void + additive contact emails ──────────────────
export const refreshCareRxEsign = (envelopeId: string) =>
  request<{ envelope_id: string; raw_status: string; status: string }>('POST', `/carerx/esign/envelopes/${envelopeId}/refresh`, {})
// Void an IN-PROCESS envelope with a required reason (409 if already signed/completed).
export const voidCareRxEsign = (envelopeId: string, reason: string) =>
  request<{ envelope_id: string; status: string; prior_status: string; reason: string }>('POST', `/carerx/esign/envelopes/${envelopeId}/void`, { reason })
// D-CRX-81 per-envelope audit trail: our audit_log actions ∪ live DocuSign recipient events.
export interface EsignAuditAction { action: string; actor: string | null; actor_role: string | null; at: string }
export interface EsignRecipientEvent {
  name: string; email: string; role: string; status: string
  sent_at: string | null; delivered_at: string | null; completed_at: string | null
  declined_at: string | null; declined_reason: string | null
}
export interface EsignEnvelopeAudit {
  envelope_id: string; status: string | null; subject_type: string | null
  our_actions: EsignAuditAction[]
  docusign_events: EsignRecipientEvent[]
  docusign_source: 'live' | 'unavailable' | 'owlrx'
  /** D-ESIGN-1: an in-app envelope's evidence chain (created / viewed / signed / completed / voided). */
  inapp_events?: { id: number; event_type: string; actor: string | null; at: string; detail: string | null }[]
  certificate_document_id: string | null
}
export const getCareRxEsignAudit = (envelopeId: string) =>
  request<EsignEnvelopeAudit>('GET', `/carerx/esign/envelopes/${envelopeId}/audit`)
// Fetch + file the Certificate of Completion (idempotent); returns the stored document id to open.
export const fetchCareRxEsignCertificate = (envelopeId: string) =>
  request<{ document_id: string; envelope_id: string; already_filed?: boolean }>('POST', `/carerx/esign/envelopes/${envelopeId}/fetch-certificate`, {})
// Additive contact emails: PioneerRx stays source of truth; these are ADDITIONAL known emails.
export interface ContactEmail { id: string; email: string; label: string | null; added_at: string }
export interface ContactEmailsResponse {
  subject_type: string; subject_id: string
  primary_email: string | null; primary_source: string
  alternates: ContactEmail[]
}
export const listContactEmails = (subjectType: 'patient' | 'prescriber', subjectId: string) =>
  request<ContactEmailsResponse>('GET', `/carerx/contacts/${subjectType}/${subjectId}/emails`)
export const addContactEmail = (subjectType: 'patient' | 'prescriber', subjectId: string, email: string, label?: string) =>
  request<{ id: string; email: string; label: string | null }>('POST', `/carerx/contacts/${subjectType}/${subjectId}/emails`, { email, label })
export const deleteContactEmail = (subjectType: 'patient' | 'prescriber', subjectId: string, emailId: string) =>
  request<{ ok: boolean; deleted: string }>('DELETE', `/carerx/contacts/${subjectType}/${subjectId}/emails/${emailId}`)

// ── D-POT-2 POT classic: asp-pot consent / AI audit / security-training parity (mig 450) ────────────
// NOTE (E-05): client.ts is frozen for new exports; these live here only because the POT pages and the
// admin tabs built in the same PR import `api/client`. Move to web/src/api/carerxAi.ts when those imports flip.
/** asp-pot /api/consent/decline — the decline is recorded like an acceptance (latest row wins). */
export const declineCareRxAiConsent = (body?: AiConsentEventBody) =>
  request<{ consented: boolean; version: string; at: string }>('POST', '/carerx/ai/consent/decline', body ?? {})
/** Admin: asp-pot /api/consent/reset — a new 'reset' row; the user is asked again on next AI use. */
export const resetCareRxAiConsent = (accountId: string) =>
  request<{ ok: boolean; account_id: string; action: 'reset'; at: string }>('POST', `/carerx/ai/consent/reset/${accountId}`, {})

/** One AI audit row (asp-pot ai-audit.json). Previews are TOKENIZED text; costs derive from the model pricing table. */
export interface CareRxAiAuditRow {
  id: string; at: string
  account_id: string | null; account_name: string | null
  status: string; phi_flag: string; phi_count: number; client_tokens: number
  model: string | null; input_tokens: number | null; output_tokens: number | null
  cost_usd: number | null; input_cost_usd: number | null; output_cost_usd: number | null
  key_source: string | null; client_ip: string | null
  prompt_preview: string | null; response_preview: string | null
  detected_types: string | null; error_message: string | null
}
export interface CareRxAiAuditPage { items: CareRxAiAuditRow[]; total: number; page: number; page_size: number }
/** Admin: asp-pot /api/ai-audit/log — newest first, pharmacy-scoped, optional per-account filter. */
export const getCareRxAiAudit = (q: { page: number; pageSize: number; accountId?: string }) => {
  const p = new URLSearchParams({ page: String(q.page), page_size: String(q.pageSize) })
  if (q.accountId) p.set('account_id', q.accountId)
  return request<CareRxAiAuditPage>('GET', `/carerx/ai/audit?${p.toString()}`)
}
/**
 * Admin: asp-pot /api/ai-audit/export → { blob, filename } (ai-audit-<yyyy-mm-dd>.csv). A bare URL cannot
 * be window.open'ed — it would travel without the Bearer token — so the CSV is fetched on the authenticated
 * path and handed back for a download link (same pattern as the report CSV export).
 */
export const downloadCareRxAiAuditCsv = async (): Promise<{ blob: Blob; filename: string }> => {
  const res = await apiFetch('/carerx/ai/audit/export')
  if (!res.ok) throw await parseApiError(res)
  const cd = res.headers.get('Content-Disposition') || ''
  const m = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(cd)
  return { blob: await res.blob(), filename: m ? decodeURIComponent(m[1]) : 'ai-audit.csv' }
}

/** asp-pot /api/training/acknowledge — the actor acknowledged the security training. */
export const acknowledgeCareRxTraining = (body?: { user_agent?: string }) =>
  request<{ acknowledged: boolean; at: string }>('POST', '/carerx/training/acknowledge', body ?? {})
/** asp-pot /api/training/status — latest row wins (an admin reset after an acknowledgment = not acknowledged). */
export const getCareRxTrainingStatus = () =>
  request<{ acknowledged: boolean; entry: { at: string; client_ip: string | null; app_version: string | null } | null }>('GET', '/carerx/training/status')
export interface CareRxTrainingLogRow {
  account_id: string; name: string | null; role: string | null
  action: 'acknowledged' | 'reset'
  client_ip: string | null; user_agent: string | null; app_version: string | null
  reset_by: string | null; reset_by_name: string | null
  at: string
}
/** Admin: asp-pot /api/training/log — every event in the pharmacy, newest first. */
export const getCareRxTrainingLog = () =>
  request<{ items: CareRxTrainingLogRow[]; count: number }>('GET', '/carerx/training/log')
/** Admin: asp-pot /api/training/reset — a new 'reset' row; the user re-acknowledges on next POT use. */
export const resetCareRxTraining = (accountId: string) =>
  request<{ ok: boolean; account_id: string; action: 'reset'; at: string }>('POST', `/carerx/training/reset/${accountId}`, {})
